<template>
  <div
    :class="{ 'd-none': hidden, 'd-flex': !hidden }"
    class="flex-column flex-grow-1 overflow-hidden"
    style="height: 100%; position: relative;"
    @dragover.prevent="onChatDragOver"
    @dragleave="onChatDragLeave"
    @drop.prevent="onChatDrop"
  >
    <div
      id="ai-chat-messages"
      ref="messages"
      class="flex-grow-1 card mb-2 overflow-auto"
    >
      <div class="card-body">
        <div v-if="messages.length">
          <AIChatMessage
            v-for="(message, index) in messages"
            :key="index"
            :index="index"
            :message="message"
            :is-last="index === messages.length - 1"
            @select-product="selectProduct"
            @copy-proof-link="copyProofLink"
            @duplicate-template-and-prepare-resized-data="duplicateTemplateAndPrepareResizeData"
            @select-product-option="selectProductOption($event, message)"
            @open-product-selector="openProductSelector($event)"
            @widget-action="handleWidgetAction"
            @widget-sequence-confirm="handleSequenceConfirm"
            @save-to-clipboard="onSaveMessageToClipboard"
          />
        </div>
        <div v-else>
          <div class="welcome-message">
            <div class="message__author">
              <svg>
                <use
                  width="14"
                  height="14"
                  href="/img/icons/bot.svg#icon-bot"
                />
              </svg>
            </div>
            <div class="welcome-message__content">
              <p>Hi! I'm Optix AI - your layout assistant.<br>Ask me for copy improvements, hierarchy tweaks, or layer cleanup ideas.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="separator" />

    <div class="ai-chat-suggestions d-flex justify-content-center pb-0">
      <button
        v-for="suggestion in suggestions"
        :key="suggestion"
        class="ai-chat-suggestions__bubble"
        @click="setSuggestion(suggestion)"
      >
        {{ suggestion }}
      </button>
    </div>

    <div
      v-if="loading"
      class="mt-2 d-flex justify-content-center align-items-center gap-2"
    >
      <div class="thinking-dots">
        <span />
        <span />
        <span />
      </div>
      <button
        class="btn-stop-ai"
        title="Stop"
        @click="stopCurrentProcess"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="currentColor"
        >
          <rect
            x="2"
            y="2"
            width="10"
            height="10"
            rx="2"
          />
        </svg>
      </button>
    </div>

    <div
      v-if="pendingPdpRegeneration"
      class="ai-chat-regen-banner"
    >
      <span>
        Describe the changes for <b>{{ pendingPdpRegeneration.label }}</b>, then press Enter.
      </span>
      <button
        type="button"
        class="ai-chat-regen-banner__cancel"
        @click="cancelPdpRegeneration"
      >
        Cancel
      </button>
    </div>

    <AIChatTextArea
      ref="chatTextArea"
      :files.sync="userFiles"
      :disabled="loading"
      @update:prompt="userInput = $event"
      @handle-enter-key="handleEnterKey"
      @send-message="sendMessage($event.text, $event.files)"
    />

    <SelectFolderModal      
      :mode="selectFolderModalState.mode"
      :view-tabs="selectFolderModalState.viewTabs"
      :modal-title="selectFolderModalState.modalTitle"
      @onSelectedItems="onAiChatFolderItemsSelected"
    />

    <div
      v-if="isChatDragover"
      class="ai-chat-tab-drop-overlay"
    >
      <div class="ai-chat-tab-drop-overlay__inner">
        Drop images to attach
      </div>
    </div>

    <ClipboardSavedToast ref="clipboardSavedToast" />
  </div>
</template>

<script>
/* global fontFamilyManager */
import { mapGetters } from "vuex";
import {
    editorControlEventBroker,
    EVENTS,
} from "@frontend/group/modules/editor-control-popup/event-broker";
import { copyAndResizeEventBroker, EVENTS as COPY_AND_RESIZE_EVENTS } from '@frontend/group/modules/copy-and-resize/event-broker';
import { getObjectsForOrder } from '@frontend/group/modules/copy-and-resize';
import {
    HORIZONTAL_GUIDE_LINE_PREFIX_ID,
    VERTICAL_GUIDE_LINE_PREFIX_ID,
} from "@frontend/constants/templates-field-prefixes";
import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from "@frontend/constants/global-update";
import selectedTemplateForApplyToAll from "@frontend/group/modules/editor-control-popup/selected-template-for-apply-to-all";
import { setSelectedInstance } from '@frontend/group/modules/copy-paste-object'
import aiChatFunctions from "@frontend/components/modules/layouts-page/aiChatFunctions";
import toastr from 'toastr';
import FileUploadService from '@frontend/services/api/file-upload';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { createGradient } from '@frontend/utils/gradient-helper';
import AIChatMessage from "@frontend/components/modules/layouts-page/EditorControl/components/AIChat/AIChatMessage.vue";
import AIChatTextArea
  from "@frontend/components/modules/layouts-page/EditorControl/components/AIChat/AIChatTextArea.vue";
import SelectFolderModal from "@frontend/components/modules/file-browser/modals/SelectFolderModal.vue";
import ClipboardSavedToast from '@frontend/components/common/clipboard/ClipboardSavedToast.vue';
import { CLIPBOARD_SAVED_PROMPT_TITLE } from '@frontend/components/common/clipboard/clipboard-constants';
import { createClipboardItem } from '@frontend/services/api/clipboard';
import API_MEDIA from "@frontend/services/api/media";
import {addAddField} from "@frontend/group/modules/additional-fields";
import {FIELD_TYPE_BACKGROND_THEME_IMAGE} from "@frontend/constants/type-fields-of-template";
import { handleCreatePdpImageSet } from "@frontend/store/creative-studio/chat-function-handlers/create-pdp-image-set";
import {
    handlePdpAiGenerate,
    handlePdpLayeredImage,
    handlePdpFromTemplate,
    showAiGenerateSizePicker,
    showLayeredImageSizePicker,
    runPdpAiGenerate,
    startPdpLayeredQueue,
    keepAndAdvancePdpQueue,
    skipPdpQueue,
    finishPdpQueue,
    retryPdpQueueStep,
    skipFailedPdpQueueStep,
    finishPdpQueueFromFailure,
    regeneratePdpImage,
    buildPdpImageDecisionWidget,
    normalizeTypeKey,
} from "@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths";
import { createSidebarPdpAdapter } from "@frontend/components/modules/layouts-page/EditorControl/components/AIChat/pdp/sidebar-pdp-adapter";
import {
    createPdpAiLayoutInCurrentLayout,
    createPdpLayeredLayoutInCurrentLayout,
} from "@frontend/components/modules/layouts-page/EditorControl/components/AIChat/pdp/sidebar-pdp-layout-creation";
import { postDesignAndAwait } from '@frontend/store/creative-studio/await-design-job';

const STORED_MESSAGES_KEY = 'layoutShowSidebarAiChatDesignCopilotMessages';

export default {
    name: "AIEditorTab",
    components: {
        AIChatTextArea,
        AIChatMessage,
        SelectFolderModal,
        ClipboardSavedToast,
    },
    props: {
        targetElement: {
            type: fabric.Object,
            require: true,
            default: () => {},
        },
        isTabActive: {
            type: Boolean,
            default: false,
        },
        hidden: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        messages: [],
        userInput: "",
        userFiles: [],
        loading: false,
        isChatDragover: false,
        suggestions: ['Analyze image', 'Analyze layout', 'Analyze mobile readiness'],
        layersOrdered: [],
        activeAbortController: null,
        processCancelled: false,
        pendingPdpRegeneration: null,
        pdpParsedPrompt: {},
        selectFolderModalState: {
          mode: 'default',
          viewTabs: true,
          modalTitle: 'Select File',
        },
        savingClipboard: false,
    }),
    computed: {
        ...mapGetters("layout", ["layers", "target", "selectedInstance"]),
    },
    watch: {
        isTabActive(active) {
            this.rerenderList();
        },
    },
    created() {
        this._pdpAdapter = createSidebarPdpAdapter(this);
    },
    beforeDestroy() {
        if (this._visibilityObserver) {
            this._visibilityObserver.disconnect();
        }
    },
    mounted() {
        this.prepareStoredMessages();
      
        this.messages = [];
        if (localStorage.getItem(STORED_MESSAGES_KEY)) {
            this.messages = this.getStoredMessages();
            this.$nextTick(() => this.scrollToBottomOfMessages());
        }

        // Detect when chat becomes visible (tab switched to Design AI)
        this._visibilityObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.scrollToBottomOfMessages();
                this.focusChatInput();
            }
        });
        this._visibilityObserver.observe(this.$el);

        // Resume AI chat tab after page reload
        if (localStorage.getItem("aiChatResumeAfterReload")) {
            localStorage.removeItem("aiChatResumeAfterReload");
            setTimeout(() => {
                window.dispatchEvent(new Event('ai-chat:open-tab'));
                this.$nextTick(() => {
                    this.scrollToBottomOfMessages();
                    this.focusChatInput();
                });
            }, 1500);
        }

        if (localStorage.getItem("newSettingsFromAiBannerResizer")) {
            const aiBannerResizerSettings = JSON.parse(localStorage.getItem("newSettingsFromAiBannerResizer"));
            const grid = $("#template-group-preview");
            const customer_id = grid.attr("data-customer-id");
            const layout_id = grid.attr("data-layout-id");
            if (aiBannerResizerSettings.layoutId !== layout_id || aiBannerResizerSettings.customerId !== customer_id) {
                return;
            }
            const preview = window.previews.some(preview => preview.id === aiBannerResizerSettings.instanceId);
            if (!preview) {
                return;
            }
            setSelectedInstance(aiBannerResizerSettings.instanceId);
            setTimeout(() => {
                this.applyAiResizeSettings(
                    aiBannerResizerSettings.instanceId,
                    aiBannerResizerSettings.templateId,
                    aiBannerResizerSettings.layoutId,
                    aiBannerResizerSettings.customerId,
                    aiBannerResizerSettings.newWidth,
                    aiBannerResizerSettings.newHeight,
                    aiBannerResizerSettings.bannerResizeSettings
                );
                localStorage.removeItem("newSettingsFromAiBannerResizer");
            }, 1000);
        }
    },
    methods: {
        hasDragFiles(e) {
            const dt = e?.dataTransfer;
            if (!dt) return false;
            if (dt.files && dt.files.length > 0) return true;
            const types = Array.from(dt.types || []);
            return types.includes('Files');
        },
        onChatDragOver(e) {
            if (this.loading) return;
            if (!this.hasDragFiles(e)) return;
            this.isChatDragover = true;
        },
        onChatDragLeave(e) {
            const container = this.$el;
            if (!container) {
                this.isChatDragover = false;
                return;
            }
            if (!container.contains(e.relatedTarget)) {
                this.isChatDragover = false;
            }
        },
        async onSaveMessageToClipboard({ content, prompts }) {
            // A/B hero results pass an array of per-variant prompts (each saved as
            // its own clipboard entry); every other message passes a single
            // `content` string. (POR-13583)
            const source = Array.isArray(prompts) && prompts.length
                ? prompts
                : [{ title: CLIPBOARD_SAVED_PROMPT_TITLE, content }];
            const items = source
                .map((item) => ({
                    title: (item && item.title) || CLIPBOARD_SAVED_PROMPT_TITLE,
                    content: String((item && item.content) || '').trim(),
                }))
                .filter((item) => item.content);

            if (!items.length || this.savingClipboard) {
                return;
            }

            this.savingClipboard = true;

            try {
                for (const item of items) {
                    const created = await createClipboardItem({
                        type: 'prompt',
                        title: item.title,
                        content: item.content,
                    });
                    this.$root.$emit('clipboard:item-created', created);
                }

                this.$nextTick(() => {
                    this.$refs.clipboardSavedToast?.show?.(undefined, items.length);
                });
            } catch (err) {
                console.error('[Clipboard] save chat message failed', err);
                toastr.error('Could not save to clipboard.');
            } finally {
                this.savingClipboard = false;
            }
        },

        onChatDrop(e) {
            this.isChatDragover = false;
            if (this.loading) return;
            const files = Array.from(e?.dataTransfer?.files || []);
            if (!files.length) return;
            const chat = this.$refs.chatTextArea;
            chat?.addFiles?.(files);
        },
        async onAiChatFolderItemsSelected(items) {
            const chat = this.$refs.chatTextArea;
            const selected = Array.isArray(items) ? items : [];
            if (!chat || !selected.length) return;
            const normalized = selected
                .map((item) => {
                    const path = item?.original_path || item?.path || '';
                    if (!path) return null;
                    return {
                        name: item?.name || path.split('/').pop(),
                        path,
                        url: `/share?file=${path}`,
                    };
                })
                .filter(Boolean);

            const files = await chat.fetchFilesFromBrowserSelection(normalized);
            if (files.length) {
                chat.addFiles(files);
            }
        },
        getFonts() {
            const fonts = [];

            const fontsList = fontFamilyManager.getFontList() || {};

            for (const key in fontsList) {
                const font = fontsList[key];
                const selectedFontFamily = this.value;

                if (Array.isArray(font)) {
                    fonts.push({
                        name: key,
                        value: font.includes(selectedFontFamily)
                            ? selectedFontFamily
                            : font[0],
                    });
                } else {
                    fonts.push({
                        name: font,
                        value: key,
                    });
                }
            }

            return fonts;
        },
        async callOpenAI(settings) {
            // Stringify messages and functions if they are arrays,
            // because the backend expects JSON strings for json_decode()
            const formData = new FormData();
            const payload = { ...settings };
            if (Array.isArray(payload.messages)) {
                formData.append('messages', JSON.stringify(payload.messages));
            }
            if (Array.isArray(payload.functions)) {
                formData.append('functions', JSON.stringify(payload.functions));
            }

            if (payload.response_format) {
                formData.append('response_format', JSON.stringify(payload.response_format));
            }

            if (payload.attachments) {
                payload.attachments.forEach((att, index) => {
                    if (att.type === 'url') {
                    formData.append(`attachments[${index}][url]`, att.payload);
                    } else if (att.type === 'file' && att.payload instanceof File) {
                    formData.append(`attachments[${index}][file]`, att.payload);
                    }
                });
            }
            formData.append('model', payload.model);

            this.activeAbortController = new AbortController();
            const { data } = await axios.post(`/call-open-ai`, formData, {
                signal: this.activeAbortController.signal,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (data.savedAttachments) {
                this.messages[this.messages.length - 1].attachments = data.savedAttachments ?? [];
                this.saveMessages();
            }

            this.activeAbortController = null;
            return data;
        },
        stopCurrentProcess() {
            this.processCancelled = true;
            if (this.activeAbortController) {
                this.activeAbortController.abort();
                this.activeAbortController = null;
            }
            this.pushMessage({
                role: 'assistant',
                content: 'Process stopped.',
            });
            this.stopLoading();
            delete this._isChaining;
        },
        validateProductIdInput(input) {
            if (!input || input.trim() === '') {
                return { valid: false, message: 'Product ID cannot be empty' };
            }
            
            const trimmed = input.trim();
            
            // Check if it contains multiple IDs
            if (/[\s,\n]/.test(trimmed)) {
                const identifiers = trimmed
                    .split(/[\s,\n]+/)
                    .map(id => id.trim())
                    .filter(id => id.length > 0);
                
                if (identifiers.length === 0) {
                    return { valid: false, message: 'No valid product identifiers provided' };
                }
                
                // Validate each identifier
                for (const identifier of identifiers) {
                    const validation = this.validateSingleProductId(identifier);
                    if (!validation.valid) {
                        return { valid: false, message: `Invalid product ID format: ${identifier}. ${validation.message}` };
                    }
                }
                
                return { valid: true, type: 'multiple', identifiers };
            } else {
                // Single identifier
                const validation = this.validateSingleProductId(trimmed);
                if (!validation.valid) {
                    return { valid: false, message: `Invalid product ID format: ${validation.message}` };
                }
                return { valid: true, type: 'single', identifier: trimmed };
            }
        },
        validateSingleProductId(productId) {
            if (!productId || productId.trim() === '') {
                return { valid: false, message: 'Product ID cannot be empty' };
            }
            
            const trimmed = productId.trim();
            
            // ASIN validation (10 alphanumeric characters)
            if (/^[A-Z0-9]{10}$/i.test(trimmed)) {
                return { valid: true, type: 'ASIN' };
            }
            
            // GTIN validation (8, 12, 13, or 14 digits)
            if (/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(trimmed)) {
                return { valid: true, type: 'GTIN' };
            }
            
            // TCIN validation (A- followed by 8-9 digits)
            if (/^A-\d{8,9}$/i.test(trimmed)) {
                return { valid: true, type: 'TCIN' };
            }
            
            // Walmart ID validation (alphanumeric, 6-20 characters)
            if (/^[A-Z0-9]{6,20}$/i.test(trimmed)) {
                return { valid: true, type: 'Walmart ID' };
            }
            
            // Material code validation (alphanumeric, 3-50 characters)
            if (/^[A-Z0-9-_]{3,50}$/i.test(trimmed)) {
                return { valid: true, type: 'Material Code' };
            }
            
            // URL validation
            try {
                new URL(trimmed);
                return { valid: true, type: 'URL' };
            } catch (e) {
                // Not a valid URL, continue with other validations
            }
            
            // Generic validation for other formats (at least 3 characters, alphanumeric with some special chars)
            if (/^[A-Z0-9-_]{3,100}$/i.test(trimmed)) {
                return { valid: true, type: 'Other' };
            }
            
            return { 
                valid: false, 
                message: 'Invalid format. Expected ASIN (10 chars), GTIN (8-14 digits), TCIN (A-XXXXXXX), Walmart ID, Material Code, URL, or other alphanumeric identifier' 
            };
        },
        setActiveObject(object) {
            if (!object) {
                return;
            }
            this.selectedInstance.canvas.setActiveObject(object);
            this.selectedInstance.canvas.renderAll();
        },
        reorderLayers(instanceId) {
            const usersFieldsOrder = JSON.parse(
                this.selectedInstance.data.users_fields_order || null,
            );
            this.selectedInstance.canvas.fire("layers:update", instanceId);
            if (usersFieldsOrder) {
                this.layersOrdered = _.sortBy(this.layers, (layer) =>
                    usersFieldsOrder.findIndex((id) => id === layer.id),
                );
                this.moveRulerGuideLinesToFront();
                return;
            }
            this.layersOrdered = this.layers;
            this.moveRulerGuideLinesToFront();
        },
        rerenderList() {
            if (this.isTabActive) {
                this.reorderLayers(this.selectedInstance.id);
            }
        },
        moveRulerGuideLinesToFront() {
            this.selectedInstance.canvas
                .getObjects()
                .filter((obj) =>
                    [
                        HORIZONTAL_GUIDE_LINE_PREFIX_ID,
                        VERTICAL_GUIDE_LINE_PREFIX_ID,
                    ].some((id) => obj.id.startsWith(id)),
                )
                .forEach((obj) => obj.bringToFront());
        },
        async getImageDimensions(blob) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                const url = URL.createObjectURL(blob);

                img.onload = () => {
                    URL.revokeObjectURL(url);
                    resolve({ width: img.width, height: img.height });
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    reject(new Error("Failed to load the image blob."));
                };

                img.src = url;
            });
        },
        async clearChat() {
            const clearAttachmentsResult = await this.clearAttachments();
            
            if (clearAttachmentsResult === false) {
                return;
            }

            this.messages = [];
            this.saveMessages();
            
            this.userInput = "";
            
            this.$nextTick(() => {
            const messagesDiv = this.$refs.messages;
            if (messagesDiv) {
                messagesDiv.scrollTop = 0;
            }
            });
        },
        startLoading() {
            this.loading = true;
        },
        stopLoading() {
            this.loading = false;
            this.saveMessages();
            if (typeof window.refreshLayoutSidebar === 'function') {
                window.refreshLayoutSidebar();
            }
            this.focusChatInput();
            this.scrollToBottomOfMessages();
        },
        focusChatInput() {
            this.$nextTick(() => {
                const editorPrompt = this.$el?.querySelector('.ai-chat-input__editor-prompt');
                if (editorPrompt) {
                    editorPrompt.focus();
                }
            });
        },
        async triggerSave() {
            try {
                const scope = ShowPageScopeCollector.get(['saveLayout']);
                if (scope.saveLayout) {
                    await scope.saveLayout(false);
                } else {
                    const $saveBtn = $(".btn-save-changes");
                    $saveBtn.prop("disabled", false);
                    $saveBtn.click();
                }
            } catch (e) {
                console.error('Error saving layout:', e);
            }
        },
        getStoredMessages() {
          const grid = $("#template-group-preview");
          const layoutId = grid.attr("data-layout-id");

          try {
            const storedMessagesRaw = localStorage.getItem(STORED_MESSAGES_KEY) ?? '[]';
            const storedMessages = JSON.parse(storedMessagesRaw);
            return storedMessages[layoutId] ?? [];
          }
          catch (err) {
            console.error("Failed to retrieve stored messages:", {
              message: err.message,
              stack: err.stack,
              layoutId,
            });
          }
        },
        saveMessages() {
          const grid = $("#template-group-preview");
          const layoutId = grid.attr("data-layout-id");
          
          try {
            const prevMessagesRaw = localStorage.getItem(STORED_MESSAGES_KEY) ?? '[]';
            const prevMessages = JSON.parse(prevMessagesRaw);
            
            const newMessages = {
              ...prevMessages,
              [layoutId]: this.messages,
            }

            localStorage.setItem(STORED_MESSAGES_KEY, JSON.stringify(newMessages));
          }
          catch (err) {
            console.error("Failed to save messages:", {
              message: err.message,
              stack: err.stack,
              layoutId,
            });
          }
        },
      
        prepareStoredMessages() {
          // Migration: legacy format was an array of messages.
          // Convert it to the new schema: { [layoutId]: messages }.
          // Ensures backward compatibility without data loss.
          
          const grid = $("#template-group-preview");
          const layoutId = grid.attr("data-layout-id");
          
          try {
            const messagesRaw = localStorage.getItem(STORED_MESSAGES_KEY);
            
            if (!messagesRaw) {
              return;
            }
            
            const messages = JSON.parse(localStorage.getItem(STORED_MESSAGES_KEY));
            
            if (!Array.isArray(messages)) {
              return;
            }
  
            const fixedMessages = {
              [layoutId]: messages
            }
            
            localStorage.setItem(STORED_MESSAGES_KEY, JSON.stringify(fixedMessages));
          } catch (err) {
            console.error("Invalid stored messages JSON:", {
              message: err.message,
              stack: err.stack,
            });
          }
        },
        scrollToBottomOfMessages() {
            this.$nextTick(() => {
                const el = document.getElementById("ai-chat-messages");
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            });
        },
        pushMessage(message) {
            this.messages.push(message);
            this.scrollToBottomOfMessages();
            // If message has an image, scroll again after it loads
            if (message.imageUrl) {
                this.$nextTick(() => {
                    const container = document.getElementById("ai-chat-messages");
                    if (!container) return;
                    const images = container.querySelectorAll('img');
                    const lastImg = images[images.length - 1];
                    if (lastImg && !lastImg.complete) {
                        lastImg.addEventListener('load', () => this.scrollToBottomOfMessages(), { once: true });
                    }
                });
            }
        },
        findLayerByElementIdOrSelected(elementId) {
            let layer;
            if (elementId) {
                layer = this.layersOrdered.find(l =>
                    l.element_id === elementId ||
                    l.id === elementId ||
                    l.name === elementId ||
                    (l.relatedCanvasObject && (
                        l.relatedCanvasObject.element_id === elementId ||
                        l.relatedCanvasObject.id === elementId
                    ))
                );
            }
            if (!layer) {
                const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                    this.selectedInstance.canvas.getActiveObject() : null;
                const selectedElement = activeObject || this.targetElement || this.target;
                if (selectedElement) {
                    // Try exact reference match first, then ID match
                    layer = this.layersOrdered.find(l =>
                        l.relatedCanvasObject === selectedElement
                    ) || this.layersOrdered.find(l =>
                        l.relatedCanvasObject && (
                            (selectedElement.id && l.relatedCanvasObject.id === selectedElement.id) ||
                            (selectedElement.element_id && l.relatedCanvasObject.element_id === selectedElement.element_id)
                        )
                    );
                }
            }
            return layer;
        },
        async uploadToBackgroundStorage(imageUrl) {
            // Upload image via /banner/upload_bk_image → returns [{path: '...'}]
            // This is the same endpoint the Generate Image popup uses
            const preview = this.selectedInstance;
            const settings = preview?.data || {};
            const formData = new FormData();
            formData.append('customer_id', settings.customer_id || '');
            formData.append('theme_id', settings.theme || '');
            formData.append('template_id', settings.template_id || '');

            // Fetch the image and send as a file
            const imgResponse = await fetch(imageUrl);
            const imgBlob = await imgResponse.blob();
            formData.append('file_1', new File([imgBlob], 'ai_generated.png', { type: imgBlob.type }));
            formData.append('filename_1', 'ai_generated.png');

            const response = await axios.post('/banner/upload_bk_image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const uploaded = response.data && response.data[0];
            if (!uploaded || !uploaded.path) {
                throw new Error('Upload failed: no path returned');
            }
            return uploaded.path;
        },
        async uploadToProductImageStorage(imageUrl) {
            // Upload image via /file/uploadimg/store → returns {filenames: ['...']}
            // This is the same endpoint the product image upload uses
            const imgResponse = await fetch(imageUrl);
            const imgBlob = await imgResponse.blob();
            const formData = new FormData();
            formData.append('upload_images[]', new File([imgBlob], 'ai_generated.png', { type: imgBlob.type }));
            formData.append('image_type', 'product_image');
            formData.append('max_size', '10000');
            formData.append('resize', '99');
            formData.append('dpi', '75');
            formData.append('company_id', '-1');

            const response = await axios.post('/file/uploadimg/store', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const filename = response.data?.filenames?.[0];
            if (!filename) {
                throw new Error('Product image upload failed: no filename returned');
            }
            // file_ids uses filename without extension
            const fileId = filename.substring(0, filename.lastIndexOf('.')) || filename;
            return fileId;
        },
        async applyImageToCurrentElement(imageUrl) {
            try {
                const activeObject = this.selectedInstance && this.selectedInstance.canvas
                    ? this.selectedInstance.canvas.getActiveObject()
                    : null;
                const targetObj = activeObject || this.targetElement || this.target;
                const instanceId = this.selectedInstance ? this.selectedInstance.id : null;

                if (!targetObj || !instanceId) {
                    toastr.error('No element selected to replace.');
                    return;
                }

                const objectId = String(targetObj.id || targetObj.element_id || '');
                const isBackgroundThemeImage = objectId.startsWith('bk_theme_img_');
                const isAdditionalBackgroundImage = objectId.startsWith('add_bk_theme_img_');
                const isProductImage = objectId.startsWith('image_') || objectId.startsWith('product_image_');

                const scope = ShowPageScopeCollector.get([
                    'handleObjectMoving', 'saveLayout', 'updateLayoutChanges',
                    'addFileIdForProductImage', 'reDrawProductImage'
                ]);

                // === PRODUCT IMAGES (image_*, product_image_*) ===
                // Upload via /file/uploadimg/store → update file_ids → redraw
                if (isProductImage) {
                    const fileId = await this.uploadToProductImageStorage(imageUrl);
                    const index = parseInt(objectId.split('_').pop()) || 0;
                    scope.addFileIdForProductImage(fileId, instanceId, index);
                    await scope.reDrawProductImage(instanceId);
                    await this.triggerSave();
                    return;
                }

                // === BACKGROUND IMAGES (bk_theme_img_*, add_bk_theme_img_*) ===
                // Upload via /banner/upload_bk_image → update data arrays → setSrc on canvas
                if (isBackgroundThemeImage || isAdditionalBackgroundImage) {
                    const uploadedPath = await this.uploadToBackgroundStorage(imageUrl);
                    const newImageUrl = `/share?file=${uploadedPath}`;
                    const imageIndex = parseInt(objectId.split('_').pop()) || 0;
                    const currentData = this.selectedInstance.data;

                    // Update canvas visual
                    await new Promise((resolve) => {
                        targetObj.setSrc(newImageUrl, () => {
                            targetObj.setCoords();
                            this.selectedInstance.canvas.renderAll();
                            resolve();
                        }, { crossOrigin: 'anonymous' });
                    });

                    // Persist the URL in layoutChanges (same pattern as Generate Image popup)
                    if (isBackgroundThemeImage) {
                        const backgroundImages = currentData.background ? [...currentData.background] : [];
                        backgroundImages[imageIndex] = uploadedPath;
                        scope.updateLayoutChanges(instanceId, 'background', backgroundImages);
                    } else {
                        const additionalImages = currentData.additional_background_images ? [...currentData.additional_background_images] : [];
                        additionalImages[imageIndex] = uploadedPath;
                        scope.updateLayoutChanges(instanceId, 'additional_background_images', additionalImages);
                    }

                    // Also update position/scale via handleObjectMoving
                    if (scope.handleObjectMoving) {
                        scope.handleObjectMoving({ target: targetObj, resizing: false });
                    }
                    await this.triggerSave();
                    return;
                }

                // === OTHER IMAGE TYPES ===
                // Upload as background, setSrc + handleObjectMoving
                if (targetObj.type === 'image' && targetObj.setSrc) {
                    const uploadedPath = await this.uploadToBackgroundStorage(imageUrl);
                    const newImageUrl = `/share?file=${uploadedPath}`;
                    await new Promise((resolve) => {
                        targetObj.setSrc(newImageUrl, () => {
                            targetObj.setCoords();
                            this.selectedInstance.canvas.renderAll();
                            resolve();
                        }, { crossOrigin: 'anonymous' });
                    });
                    if (scope.handleObjectMoving) {
                        scope.handleObjectMoving({ target: targetObj, resizing: false });
                    }
                    await this.triggerSave();
                    return;
                }

                toastr.error('Could not determine how to replace this element.');
            } catch (error) {
                console.error('[AI-CHAT] Error applying image:', error);
                toastr.error('Failed to apply image. Please try again.');
            }
        },
        async applyImageAsBackground(imageUrl) {
            try {
                const instanceId = this.selectedInstance ? this.selectedInstance.id : null;
                if (!instanceId) {
                    toastr.error('No template selected.');
                    return;
                }

                // Find a background image in the canvas objects
                const canvas = this.selectedInstance.canvas;
                const bgObject = canvas ? canvas.getObjects().find(obj => {
                    const id = String(obj.id || '');
                    return obj.type === 'image' && (
                        id.startsWith('bk_theme_img_') ||
                        id.startsWith('add_bk_theme_img_')
                    );
                }) : null;

                if (bgObject) {
                    // Set as active and delegate to applyImageToCurrentElement
                    canvas.setActiveObject(bgObject);
                    canvas.renderAll();
                    await this.applyImageToCurrentElement(imageUrl);
                    return;
                }

                // No background found - try first image
                const firstImage = canvas ? canvas.getObjects().find(obj => obj.type === 'image') : null;
                if (firstImage) {
                    canvas.setActiveObject(firstImage);
                    canvas.renderAll();
                }
                await this.applyImageToCurrentElement(imageUrl);
            } catch (error) {
                console.error('Error applying as background:', error);
                toastr.error('Failed to apply image as background.');
            }
        },
        async addImageToNewTemplate(imageUrl, sizeValue) {
            try {
                let width, height;

                if (sizeValue === 'original') {
                    // Use canvas dimensions (actual size) rather than template.width which may be scaled
                    const canvas = this.selectedInstance && this.selectedInstance.canvas;
                    const dimension = this.selectedInstance && this.selectedInstance.dimension;
                    width = canvas ? Math.round(canvas.width) : (dimension ? parseInt(dimension[0]) : null);
                    height = canvas ? Math.round(canvas.height) : (dimension ? parseInt(dimension[1]) : null);
                } else {
                    const parts = sizeValue.split('x').map(Number);
                    width = parts[0];
                    height = parts[1];
                }

                if (!width || !height) {
                    toastr.error('Could not determine template dimensions.');
                    return;
                }

                this.pushMessage({
                    role: 'assistant',
                    content: `Creating a new ${width}x${height} template with the image...`,
                });
                this.startLoading();

                const grid = $("#template-group-preview");
                const customer_id = grid.attr("data-customer-id");
                const layout_id = grid.attr("data-layout-id");

                // Use a relative path for the image (strip origin if present)
                let imagePath = imageUrl;
                if (imagePath.startsWith(window.location.origin)) {
                    imagePath = imagePath.substring(window.location.origin.length);
                }

                const result = await axios.post(`/banner/${customer_id}/group/create-template-with-size`, {
                    customer_id,
                    layout_id,
                    image_path: imagePath,
                    width,
                    height,
                    product_image_urls: [],
                    scale_mode: 'fit',
                });

                if (result.data && result.data.success) {
                    this.pushMessage({
                        role: 'assistant',
                        content: `New ${width}x${height} template created! Reloading page...`,
                    });
                    this.saveMessages();
                    this.stopLoading();
                    localStorage.setItem('aiChatResumeAfterReload', 'true');
                    setTimeout(() => location.reload(), 500);
                } else {
                    throw new Error('Template creation failed');
                }
            } catch (error) {
                console.error('Error adding to new template:', error);
                toastr.error('Failed to create new template.');
                this.stopLoading();
            }
        },
        async handleWidgetAction({ widgetId, action, messageIndex, message }) {
            let actionName = typeof action === 'string' ? action : action.action;
            let payload = typeof action === 'object' ? action.payload : null;

            if (actionName === 'pdp_reference_images_confirm') {
                const { urls, baseProduct } = payload;
                this._pdpAdapter.setMessageDisabled(messageIndex, true);
                
                this.pushMessage({
                    role: 'assistant',
                    content: `Generating ${urls.length} images based on your selected references...`,
                });
                this.startLoading();

                try {
                    const generateSpecificPrompt = async (baseProductUrl, referenceUrl) => {
                        try {
                            const response = await this.callOpenAI({
                                model: 'gpt-4o',
                                messages: [
                                    {
                                        role: 'system',
                                        content: `Analyze the provided images to extract parameters for a design template.
- BASE PRODUCT: The product to be placed in the scene.
- REFERENCE SCENE: The background/environment.

You must categorize the situation into one of two cases:
Case A: The scene contains a product that should be replaced by the base product.
Case B: The scene does NOT contain a product to replace, or you are just editing scene elements.

Identify:
1. Which case applies (A or B).
2. Text visible in the scene that should be updated to match the NEW product (e.g., if the scene says "Orange juice" but the base product is "Milk", update it). Provide "from" (original) and "to" (new).
3. A cohesive color palette "Z".

Return ONLY a JSON object:
{
  "case": "A" | "B",
  "text_A": "original 1",
  "text_B": "new 1",
  "text_C": "original 2",
  "text_D": "new 2",
  "palette_Z": "color palette"
}`
                                    },
                                    {
                                        role: 'user',
                                        content: [
                                            { type: 'text', text: 'Analyze these images:' },
                                            { type: 'image_url', image_url: { url: baseProductUrl } }, // Image 1 for AI: Product
                                            { type: 'image_url', image_url: { url: referenceUrl } }   // Image 2 for AI: Scene
                                        ]
                                    }
                                ],
                                response_format: {
                                    type: "json_schema",
                                    json_schema: {
                                        name: "design_parameters",
                                        strict: true,
                                        schema: {
                                            type: "object",
                                            properties: {
                                                case: { type: "string", enum: ["A", "B"] },
                                                text_A: { type: "string" },
                                                text_B: { type: "string" },
                                                text_C: { type: "string" },
                                                text_D: { type: "string" },
                                                palette_Z: { type: "string" }
                                            },
                                            required: ["case", "text_A", "text_B", "text_C", "text_D", "palette_Z"],
                                            additionalProperties: false
                                        }
                                    }
                                }
                            });

                            let content = response.choices?.[0]?.message?.content || response.content || "";
                            content = content.replace(/```json/g, "").replace(/```/g, "").trim();
                            if (!content) return null;

                            const data = JSON.parse(content);
                            console.log('AI CHAT: Design analysis result', data);
                            
                            if (data.case === 'A') {
                                return {
                                    prompt: `Edit the first image image. COMPLETELY REMOVE the existing product from the first image and replace it with the product from the second image, ensuring its visual integrity is maintained 100% without any modifications to its design, branding, or text. If there is text in the scene referring to the old product, replace it with text relevant to the new product (e.g. change "${data.text_A}" to "${data.text_B}"). The new product must be perfectly sharp, detailed, and high-resolution, looking like a real photograph integrated naturally into the environment. Do not allow any blending, ghosting, or distortion of the product's identity. Use a "${data.palette_Z}" color palette.`,
                                    attachments: [referenceUrl, baseProductUrl] // Image 1: Scene, Image 2: Product
                                };
                            } else {
                                return {
                                    prompt: `Edit the second image image. Change text "${data.text_A}" to "${data.text_B}", Change text "${data.text_C}" to "${data.text_D}". Use a "${data.palette_Z}" color palette.`,
                                    attachments: [baseProductUrl, referenceUrl] // Image 1: Product, Image 2: Scene
                                };
                            }
                        } catch (e) {
                            console.error('Failed to generate dynamic prompt:', e);
                            return null;
                        }
                    };

                    const generateWithRetry = async (url, retries = 1) => {
                        const analysis = await generateSpecificPrompt(baseProduct, url);
                        
                        let specificPrompt = analysis?.prompt;
                        let referenceUrls = [];
                        let productImageUrls = [];

                        if (analysis?.attachments) {
                            referenceUrls = analysis.attachments;
                            if (analysis.attachments.includes(baseProduct)) {
                                productImageUrls = [baseProduct];
                            }
                        }

                        if (!specificPrompt) {
                            specificPrompt = `Edit the first image. Use the second image as the product reference. COMPLETELY REMOVE the original product from the first image and replace it with the product from the second image, ensuring its visual integrity is maintained 100% without any modifications to its design, branding, or text. If there is text in the scene referring to the old product, replace it with text relevant to the new product to maintain contextual consistency. The new product must be perfectly sharp, detailed, and high-resolution, looking like a real photograph integrated naturally into the environment. Do not allow any blending, ghosting, or distortion of the product's identity.`;
                            referenceUrls = baseProduct ? [url, baseProduct] : [url];
                            productImageUrls = baseProduct ? [baseProduct] : [];
                        }

                        for (let i = 0; i <= retries; i++) {
                            try {
                                return await postDesignAndAwait('/networks/design', {
                                    image_prompt: specificPrompt,
                                    product_images_names: productImageUrls,
                                    reference_images_urls: referenceUrls,
                                    user_sizes: '2048x2048',
                                    source: 'creative_studio_chat'
                                }, false, null);
                            } catch (e) {
                                if (i === retries) {
                                    console.error(`Failed to generate image for ${url} after ${retries} retries`, e);
                                    return null;
                                }
                                console.warn(`Retrying image generation for ${url} (attempt ${i + 1})...`);
                            }
                        }
                    };

                    const promises = urls.map(url => generateWithRetry(url, 1));
                    
                    const results = await Promise.all(promises);
                    const generatedImages = results.map(result => 
                        Array.isArray(result) ? (result[0]?.image_url ?? result[0]) : result
                    ).filter(Boolean);
                    
                    
                    this.pushMessage({
                        role: 'assistant',
                        content: `Here are your generated images based on the references:`,
                        attachments: generatedImages.map(url => ({ type: 'url', payload: url })),
                    });
                } catch (e) {
                    console.error('Failed generating PDP reference images:', e);
                    this.pushMessage({
                        role: 'assistant',
                        content: `Failed to generate some images.`,
                    });
                } finally {
                    this.stopLoading();
                }
                
                this.scrollToBottomOfMessages();
                this.focusChatInput();
                return;
            }

            if (!message) return;
            if (typeof action === 'string' && (action.startsWith('open_showcase:') || action === 'browse_gallery')) {
                this.$set(message, 'disabled', false);
            } else {
                this.$set(message, 'disabled', true);
            }

            const ctx = message._imageActionContext;
            const widgetValues = message._widgetValues || {};

            if (action === 'apply_image' || action === 'apply_generated_image' || action === 'apply_translation') {
                const radioValue = widgetValues['image-save-action'] || widgetValues['gen-image-action'] || 'replace';
                const imageUrl = ctx.newImageUrl || (ctx.imageUrls && ctx.imageUrls[0]);
                const fullUrl = imageUrl && (imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl);

                if (!fullUrl) return;

                if (radioValue === 'replace') {
                    this.applyImageToCurrentElement(fullUrl);
                    this.pushMessage({
                        role: 'assistant',
                        content: 'The image has been applied to the current template.',
                    });
                } else if (radioValue === 'add_background') {
                    await this.applyImageAsBackground(fullUrl);
                    this.pushMessage({
                        role: 'assistant',
                        content: 'The image has been set as the template background.',
                    });
                } else if (radioValue === 'new_template') {
                    const sizeValue = widgetValues['template-size'] || 'original';
                    this.addImageToNewTemplate(fullUrl, sizeValue);
                }
            } else if (action === 'discard_image' || actionName === 'discard_design_idea') {
                this.pushMessage({
                    role: 'assistant',
                    content: 'No changes were applied.',
                });
            } else if (action === 'add_translation_new_template') {
                const imageUrl = ctx.newImageUrl;
                const fullUrl = imageUrl && (imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl);
                if (fullUrl) {
                    this.addImageToNewTemplate(fullUrl, 'original');
                }
            } else if (action === 'apply_translate_choice') {
                const choice = widgetValues['translate-action'] || 'new_template';
                const imageUrl = ctx.newImageUrl;
                const fullUrl = imageUrl && (imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl);
                if (fullUrl) {
                    if (choice === 'replace') {
                        this.applyImageToCurrentElement(fullUrl);
                        this.pushMessage({ role: 'assistant', content: 'The translated image has been applied.' });
                    } else {
                        this.addImageToNewTemplate(fullUrl, 'original');
                    }
                }
            } else if (action === 'open_product_selector') {
                this.$set(message, 'disabled', false); // Keep enabled so user can reopen
                this.openProductSelector();
                this.pushMessage({ role: 'assistant', content: 'I\'ve opened the product selector for you.' });
            } else if (action === 'cancel_product_selector') {
                this.pushMessage({ role: 'assistant', content: 'OK, no changes made.' });
            } else if (action === 'confirm_language_variant') {
                // Fall back to first option if user didn't explicitly click the radio
                const selectedLanguage = widgetValues['language-variant']
                    || (message.widgets && message.widgets.find(w => w.id === 'language-variant')?.options[0]?.value);
                if (selectedLanguage) {
                    this.sendMessage(`Translate this image to ${selectedLanguage} (use exactly this language variant, do not ask again)`);
                }
            } else if (action === 'analyze_layout_selected') {
                this.sendMessage('Are the images in the selected template in the best order for immersive storytelling on a Product Detail Page?', null);
            } else if (action === 'analyze_layout_all') {
                this.sendMessage('Are the images in this layout in the best order for immersive storytelling across all templates?', null);
            } else if (action === 'extract_text_selected_image') {
                this.startLoading();

                try {
                    const activeObject = this.selectedInstance && this.selectedInstance.canvas
                        ? this.selectedInstance.canvas.getActiveObject()
                        : null;

                    const imgObj = activeObject && activeObject.type === 'image' && activeObject._element ? activeObject : null;

                    if (!imgObj || !imgObj._element.naturalWidth) {
                        this.pushMessage({ role: 'assistant', content: 'No image selected. Please select an image on the canvas first.' });
                        this.stopLoading();
                        return;
                    }

                    const base64 = this.generateThumbnail(imgObj._element, 400);

                    const visionResponse = await this.callOpenAI({
                        model: 'gpt-5.1',
                        messages: [{
                            role: 'user',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Extract all text visible in this image. List each piece of text exactly as it appears.'
                                },
                                {
                                    type: 'image_url',
                                    image_url: { url: base64 }
                                },
                            ],
                        }],
                    });

                    this.pushMessage({ role: 'assistant', content: visionResponse.content || 'No text found.' });
                } catch (e) {
                    console.error('Error in extract_text_selected_image:', e);
                    this.pushMessage({ role: 'assistant', content: 'Sorry, text extraction failed. Please try again.' });
                }

                this.stopLoading();
            } else if (action === 'extract_text_selected_template') {
                this.startLoading();

                try {
                    const preview = (window.previews || []).find(p => p.id === this.selectedInstance?.id);

                    if (!preview) {
                        this.pushMessage({ role: 'assistant', content: 'No template selected.' });
                        this.stopLoading();
                        return;
                    }

                    preview.canvas.discardActiveObject();

                    const canvasEl = preview.canvas.getElement();
                    const canvasWidth = canvasEl.width;
                    const canvasHeight = canvasEl.height;

                    let imageData;

                    if (canvasWidth <= 1500 && canvasHeight <= 1500) {
                        imageData = canvasEl.toDataURL('image/jpeg', 0.8);
                    } else {
                        const scale = Math.min(1500 / canvasWidth, 1500 / canvasHeight);

                        const thumbCanvas = document.createElement('canvas');
                        const thumbCtx = thumbCanvas.getContext('2d');

                        thumbCanvas.width = Math.round(canvasWidth * scale);
                        thumbCanvas.height = Math.round(canvasHeight * scale);

                        thumbCtx.drawImage(canvasEl, 0, 0, thumbCanvas.width, thumbCanvas.height);

                        imageData = thumbCanvas.toDataURL('image/jpeg', 0.8);
                    }

                    const visionResponse = await this.callOpenAI({
                        model: 'gpt-5.1',
                        messages: [{
                            role: 'user',
                            content: [
                                {
                                    type: 'text',
                                    text: 'This is a rendered banner/template image. Follow these steps:\n1. Identify every visible element in the image (headline, subheadline, price, product name, button label, disclaimer, watermark, etc.).\n2. For each element that contains text, extract its exact text in order from top to bottom, left to right.\nOutput only the extracted text. Preserve exact line breaks within each element. Use <b>text</b> to mark bold text. Do not use any other HTML tags. Do not ask questions.'
                                },
                                {
                                    type: 'image_url',
                                    image_url: { url: imageData }
                                },
                            ],
                        }],
                    });

                    this.pushMessage({ role: 'assistant', content: visionResponse.content || 'No text found.' });
                } catch (e) {
                    console.error('Error in extract_text_selected_template:', e);
                    this.pushMessage({ role: 'assistant', content: 'Sorry, text extraction failed. Please try again.' });
                }
                this.stopLoading();
            } else if (actionName && actionName.startsWith('pdp_path:')) {
                const pathWidget = (message.widgets || []).find((w) => w.id === 'pdp_creation_path');
                if (pathWidget) {
                    this.$set(pathWidget, 'selectedAction', actionName);
                }
                this.$set(message, 'disabled', true);
                const path = actionName.replace('pdp_path:', '');
                if (path === 'ai_generate') {
                    handlePdpAiGenerate(this._pdpAdapter);
                } else if (path === 'layered_image') {
                    handlePdpLayeredImage(this._pdpAdapter);
                } else if (path === 'template') {
                    handlePdpFromTemplate(this._pdpAdapter);
                }
            } else if (actionName && actionName.startsWith('pdp_ai_size:')) {
                const sizeWidget = (message.widgets || []).find((w) => w.id === 'pdp_ai_size_select');
                if (sizeWidget) {
                    this.$set(sizeWidget, 'selectedAction', actionName);
                }
                this.$set(message, 'disabled', true);
                const sizeStr = actionName.replace('pdp_ai_size:', '');
                const parsedPrompt = this._pdpAdapter.getParsedPrompt();
                this._pdpAdapter.setParsedPrompt({ ...parsedPrompt, pdp_ai_size: sizeStr });
                this._pdpAdapter.addMessage({
                    role: 'assistant',
                    content: `Size set to <b>${sizeStr.replace('x', ' x ')}</b>.`,
                });
                this._pdpAdapter.saveMessages();
                runPdpAiGenerate(this._pdpAdapter);
            } else if (actionName && actionName.startsWith('pdp_layered_size:')) {
                const sizeWidget = (message.widgets || []).find((w) => w.id === 'pdp_layered_size_select');
                if (sizeWidget) {
                    this.$set(sizeWidget, 'selectedAction', actionName);
                }
                this.$set(message, 'disabled', true);
                const sizeStr = actionName.replace('pdp_layered_size:', '');
                const parsedPrompt = this._pdpAdapter.getParsedPrompt();
                this._pdpAdapter.setParsedPrompt({ ...parsedPrompt, pdp_layered_size: sizeStr });
                this._pdpAdapter.addMessage({
                    role: 'assistant',
                    content: `Size set to <b>${sizeStr.replace('x', ' x ')}</b>.`,
                });
                this._pdpAdapter.saveMessages();
                startPdpLayeredQueue(this._pdpAdapter);
            } else if (actionName === 'pdp_image_keep_next') {
                keepAndAdvancePdpQueue(this._pdpAdapter, messageIndex);
            } else if (actionName === 'pdp_image_skip') {
                skipPdpQueue(this._pdpAdapter, messageIndex);
            } else if (actionName === 'pdp_image_finish') {
                finishPdpQueue(this._pdpAdapter, messageIndex);
            } else if (actionName === 'pdp_queue_retry') {
                retryPdpQueueStep(this._pdpAdapter, messageIndex);
            } else if (actionName === 'pdp_queue_skip_failure') {
                skipFailedPdpQueueStep(this._pdpAdapter, messageIndex);
            } else if (actionName === 'pdp_queue_finish_from_failure') {
                finishPdpQueueFromFailure(this._pdpAdapter, messageIndex);
            } else if (actionName === 'pdp_image_change') {
                const label = message._pdpImageContext?.label || 'image';
                this._pdpAdapter.updateMessage(messageIndex, { widgets: [] });
                this._pdpAdapter.setPendingRegeneration({ messageIndex, label });
                this._pdpAdapter.setChatText('Regenerate this image: ');
                this._pdpAdapter.saveMessages();
                this.focusChatInput();
            } else if (actionName && actionName.startsWith('pdp_image_mode:')) {
                const newMode = actionName.replace('pdp_image_mode:', '');
                const currentMode = message._pdpImageContext?.productMode;
                if (currentMode === newMode) return;
                this.handleRegeneratePdpImage(messageIndex, { overrideMode: newMode });
            } else if (actionName === 'create_pdp_ai_layout') {
                this._pdpAdapter.setMessageDisabled(messageIndex, true);
                createPdpAiLayoutInCurrentLayout(this._pdpAdapter, messageIndex);
            } else if (actionName === 'create_pdp_layered_image_layout') {
                this._pdpAdapter.setMessageDisabled(messageIndex, true);
                createPdpLayeredLayoutInCurrentLayout(this._pdpAdapter, messageIndex);
            } else if (actionName && actionName.startsWith('keep_background:')) {
                const refId = actionName.substring('keep_background:'.length);
                this.$store.commit('creativeStudio/SET_PRODUCT_KEEP_BACKGROUND', { refId, value: true });
                toastr.success('Original image will be used');
                return;
            } else if (actionName && actionName.startsWith('toggle_product_selection:')) {
                const refId = actionName.substring('toggle_product_selection:'.length);
                this.$store.commit('creativeStudio/TOGGLE_PRODUCT_DESELECTED', { refId });
                this.$store.dispatch('creativeStudio/saveConversationState');
                return;
            }

            // Always ensure scroll and focus after any widget action
            this.scrollToBottomOfMessages();
            this.focusChatInput();
        },
        async handleSequenceConfirm({ payload, message }) {
            if (!message || !payload) return;
            const pathType = message._pathType;
            if (!pathType) return;

            const keys = (payload.keys || []).map(normalizeTypeKey);
            const parsedPrompt = this._pdpAdapter.getParsedPrompt();
            this._pdpAdapter.setParsedPrompt({ ...parsedPrompt, pdp_image_types: keys });

            axios.put('/users/me/chat-preferences', {
                last_sequence_id: payload.source === 'library' ? payload.sequenceId : null,
                last_sequence_source: payload.source || 'ai',
            }).catch((err) => console.error('Failed to persist last chat selection', err));

            // Persist the user's confirmed selection back into the widget
            // object so a future remount (page reload, re-fetch on return)
            // re-renders the picker with their actual choice instead of the
            // source's defaults. The widget reads its initial state from
            // `initialSelectedKeys` and `initialSourceId`.
            const confirmedSourceId = payload.source === 'library' && payload.sequenceId
                ? `library:${payload.sequenceId}`
                : 'ai';
            const updatedWidgets = (message.widgets || []).map((w) => {
                if (w && w.id === 'pdp_sequence_picker') {
                    return {
                        ...w,
                        initialSelectedKeys: keys.slice(),
                        initialSourceId: confirmedSourceId,
                    };
                }
                return w;
            });
            this.$set(message, 'widgets', updatedWidgets);

            // Show the size picker only on the first confirm. If the user
            // changes the selection later and re-confirms, the new keys are
            // already in parsedPrompt — don't duplicate the size picker.
            if (message._sequenceConfirmedAt) {
                this._pdpAdapter.saveMessages();
                this.scrollToBottomOfMessages();
                this.focusChatInput();
                return;
            }
            this.$set(message, '_sequenceConfirmedAt', Date.now());
            // Lock the sequence picker after the first confirm so the user
            // can't accidentally change the selection mid-flow.
            this.$set(message, 'disabled', true);

            if (pathType === 'ai_generate') {
                showAiGenerateSizePicker(this._pdpAdapter);
            } else if (pathType === 'layered_image') {
                showLayeredImageSizePicker(this._pdpAdapter);
            }

            this.scrollToBottomOfMessages();
            this.focusChatInput();
        },
        async handleRegeneratePdpImage(messageIndex, options) {
            const label = this.messages[messageIndex]?._pdpImageContext?.label || 'image';
            this._pdpAdapter.updateMessage(messageIndex, { widgets: [] });
            this.startLoading();
            this._pdpAdapter.addMessage({
                role: 'assistant',
                content: `Regenerating <b>${label}</b> image...`,
            });
            this._pdpAdapter.saveMessages();

            let success = false;
            try {
                success = await regeneratePdpImage(this._pdpAdapter, messageIndex, options || {});
            } finally {
                this.loading = false;
            }

            this._pdpAdapter.addMessage({
                role: 'assistant',
                content: success
                    ? `<b>${label}</b> image updated.`
                    : `Failed to regenerate <b>${label}</b> image. Please try again.`,
            });
            this._pdpAdapter.saveMessages();
        },
        cancelPdpRegeneration() {
            const pending = this.pendingPdpRegeneration;
            if (pending && typeof pending.messageIndex === 'number') {
                const target = this.messages[pending.messageIndex];
                const ctx = target?._pdpImageContext;
                if (ctx && !target._pdpImageKept) {
                    this._pdpAdapter.updateMessage(pending.messageIndex, {
                        widgets: [buildPdpImageDecisionWidget({
                            isLast: !!ctx.isLast,
                            showFinish: !!ctx.showFinish,
                            totalCount: ctx.totalCount || 1,
                        })],
                    });
                    this._pdpAdapter.saveMessages();
                }
            }
            this.pendingPdpRegeneration = null;
            this.userInput = '';
            if (this.$refs.chatTextArea && typeof this.$refs.chatTextArea.setSuggestion === 'function') {
                this.$refs.chatTextArea.setSuggestion('');
            }
        },
        async reloadLayoutAfterPdpAppend() {
            try {
                await this.triggerSave();
            } catch (e) {
                // fall through to reload anyway
            }
            this.saveMessages();
            localStorage.setItem('aiChatResumeAfterReload', 'true');
            setTimeout(() => location.reload(), 500);
        },
        handleEnterKey(event) {
            if (event.key !== 'Enter') {
                return;
            }
            if (event.shiftKey) {
                return;
            }

            event.preventDefault();
            const text = this.userInput;
            this.userInput = "";
            if (event && event.target) {
                event.target.value = "";
            }
            this.sendMessage(text);
        },
        setSuggestion(text) {
            if (text === 'Analyze layout') {
                this.pushMessage({
                    role: 'assistant',
                    content: 'Would you like to:',
                    widgets: [{
                        type: 'action_buttons',
                        id: 'analyze-layout-scope',
                        stacked: true,
                        buttons: [
                            { label: '1. Analyze the layout of the selected template', variant: 'outline-primary', action: 'analyze_layout_selected' },
                            { label: '2. Analyze the templates in the layout as a group', variant: 'outline-primary', action: 'analyze_layout_all' },
                        ],
                    }],
                });
                return;
            }
            this.$refs.chatTextArea.setSuggestion(text);

            this.scrollToBottomOfMessages();
        },
        async sendMessage(explicitText = null, files = null) {
            const text = typeof explicitText === 'string' ? explicitText : this.userInput;
            if (!text || !text.trim() || this.loading) return;

            this.processCancelled = false;
            const trimmed = text.trim();

            // Intercept PDP regeneration-with-feedback: the user clicked the
            // refine icon on a generated image, we prefilled the input with
            // "Regenerate this image: ". Send the feedback to the regenerate
            // handler instead of OpenAI.
            if (this.pendingPdpRegeneration) {
                const { messageIndex } = this.pendingPdpRegeneration;
                const feedback = trimmed.replace(/^Regenerate this image:\s*/i, '').trim();
                this.pendingPdpRegeneration = null;
                this.userInput = '';
                this.pushMessage({ role: 'user', content: trimmed });
                await this.handleRegeneratePdpImage(messageIndex, { extraUserPrompt: feedback || null });
                return;
            }

            const normalizedFiles = this.normalizeAttachments(files);
            const userMessage = {
                role: "user",
                content: trimmed,
                attachments: normalizedFiles,
            };

            this.pushMessage(userMessage);
            this.userInput = "";
            this.startLoading();

            // Ensure layers are fresh before processing
            try { this.reorderLayers(this.selectedInstance.id); } catch (e) { /* ignore */ }

            try {
                let currentSelectedElement = null;
                
                if (this.selectedInstance && this.selectedInstance.canvas) {
                    currentSelectedElement = this.selectedInstance.canvas.getActiveObject();
                }
                
                if (!currentSelectedElement && this.targetElement && Object.keys(this.targetElement).length > 0) {
                    currentSelectedElement = this.targetElement;
                }
                
                // We need to get only the canvas data that is necessary for the AI's context.
                const layersOrderedPrettyJson = JSON.stringify(
                    this.layersOrdered.map(function (layer) {
                        const objId = layer.relatedCanvasObject?.id || layer.relatedCanvasObject?.element_id || '';
                        const isImageElement = layer.relatedCanvasObject?.type === 'image' ||
                            (objId && objId.includes('image'));

                        const layerData = {
                            element_id: layer.element_id,
                            id: layer.id,
                            name: layer.name,
                            note: layer.note,
                            details: layer.relatedCanvasObject
                                ? {
                                      angle:
                                          layer.relatedCanvasObject.angle ??
                                          null,
                                      charSpacing:
                                          layer.relatedCanvasObject
                                              .charSpacing ?? null,
                                      cornerSize:
                                          layer.relatedCanvasObject
                                              .cornerSize ?? null,
                                      fill:
                                          layer.relatedCanvasObject.fill ??
                                          null,
                                      fontColor:
                                          layer.relatedCanvasObject.fontColor ??
                                          null,
                                      fontFamily:
                                          layer.relatedCanvasObject
                                              .fontFamily ?? null,
                                      fontSize:
                                          layer.relatedCanvasObject.fontSize ??
                                          null,
                                      opacity:
                                          layer.relatedCanvasObject
                                              .opacity ?? null,
                                      text:
                                          layer.relatedCanvasObject.text ??
                                          null,
                                      textAlign:
                                          layer.relatedCanvasObject.textAlign ??
                                          null,
                                  }
                                : null,
                        };

                        if (isImageElement) {
                            layerData.imageType = objId.includes('product_image') ? 'product_image' :
                                objId.includes('background_theme') ? 'background_image' : 'other_image';
                        }

                        return layerData;
                    }),
                    null,
                    2,
                );

                // Get the element_id of the currently selected element
                let selectedLayerElementId = "none";
                if (currentSelectedElement) {
                    selectedLayerElementId =
                        currentSelectedElement.id ||
                        currentSelectedElement.templateField?.element_id ||
                        currentSelectedElement.element_id ||
                        "none";
                }

                // Build template context for multi-template awareness
                const templateContext = (window.previews || []).map((preview, idx) => ({
                    index: idx + 1,
                    instance_id: preview.id,
                    name: preview.template?.name || `Template ${idx + 1}`,
                    dimensions: `${preview.template?.width || '?'}x${preview.template?.height || '?'}`,
                    is_selected: preview.id === (this.selectedInstance && this.selectedInstance.id),
                }));
                const currentTemplateIndex = templateContext.findIndex(t => t.is_selected) + 1;

                const systemPrompt = `You are a banner ad designer AI assistant.

Here is the current list of layers in the active template:
${layersOrderedPrettyJson}

The element_id of the currently selected layer is: ${selectedLayerElementId}

Element summary: ${this.layersOrdered.filter(l => l.relatedCanvasObject?.text).length} text elements, ${this.layersOrdered.filter(l => l.relatedCanvasObject?.type === 'image').length} image elements.${this.layersOrdered.filter(l => l.relatedCanvasObject?.text).length === 0 ? ' WARNING: This template has NO text elements. Any visible text (like product names, sizes, prices) is embedded inside images. To change such text, use edit_element_image on the image containing it.' : ''}

Templates in this layout (user says "first template" = index 1, "second template" = index 2, etc.):
${JSON.stringify(templateContext)}
Currently active: template #${currentTemplateIndex || 1}

ELEMENT RESOLUTION RULES:
- If the user says "the text that says X", search the layers' details.text field for a case-insensitive partial match to find the correct element.
- If the template has exactly one image element and the user says "the image", use that element.
- "the product image" = elements with imageType "product_image"
- "the background image" or "the background" = elements with imageType "background_image"
- When the user says "in the first template" or "in template 2", call switch_template first, then perform the requested operation.

MULTI-ACTION: For compound requests (e.g. "change the text AND the color AND move it"), execute them one function call at a time in sequence. Call the most important action first.

IMAGE OPERATIONS:
- For image editing/modification requests (e.g. "update the glass content", "replace mango with cranberries"), use edit_element_image with a descriptive prompt.
- For full image replacement with AI generation, use replace_element_image or generate_image_inline.
- For image translation (e.g. "translate this image to Spanish"), use translate_image.
- When generating images, you can reference source images from the template by passing their element_ids to generate_image_inline.

TEXT IN IMAGES:
- IMPORTANT: If the user asks to change text (e.g. "change 64 oz to 32 oz") but the layers list shows NO text elements (no layer has details.text), the text is EMBEDDED in an image, not a separate text element.
- In this case, do NOT use edit_element_text, find_element_by_content, or select_element. Instead, use edit_element_image on the image that contains the text, with a prompt describing ALL the changes in one go.
- For compound requests like "change CRANBERRY to BLUEBERRY and change its color to blue", combine everything into ONE edit_element_image call with the full description: "Change the text CRANBERRY to BLUEBERRY and change its color to blue".
- Do NOT break image text changes into multiple actions - handle them as a single edit_element_image call.

PRODUCT REPLACEMENT:
- When the user asks to replace a product image with a REAL product by name (e.g., "replace with a Pepsi can", "use a Coca-Cola image"), use open_product_selector to let the user browse and select the product from the product library. Do NOT use select_element or try to guess element IDs.
- If the user provides a specific product ID/GTIN/ASIN, use change_element_product_id to search and replace directly.
- If the user provides a FILENAME (e.g., "replace with doritos1.png", "use image beach.jpg"), use change_element_product_id with the filename (without extension) as the new_product_id - it will search the file library for matching files.
- Only use replace_element_image or edit_element_image for AI-generated imagery, not for real product photos or existing files.

UPLOADED IMAGE ADD EXECUTION RULES:
- Use add_uploaded_images for requests to add an uploaded image to a template.
- add_uploaded_images supports:
  - attachment_indexex
  - template_index
  - instance_id
- If the user says "add this image to the second template", call add_uploaded_images with:
  - attachment_index: 0
  - template_index: 2
- Do NOT call switch_template first when add_uploaded_images can target the template directly.
- If multiple uploaded images are referenced, call add_uploaded_images once per attachment, unless a bulk add_uploaded_images tool exists.
- If the user says "these images", "both images", "all uploaded images", or otherwise refers to multiple uploaded images, use add_uploaded_images.
- Preserve attachment order:
  - first uploaded image = attachment_index 0
  - second uploaded image = attachment_index 1
  - third uploaded image = attachment_index 2
- Example: "add these images to the third template" => add_uploaded_images with attachment_indexes [0,1,...] and template_index 3.

PROJECT NAME RULES:
- edit_project_name supports template_index to target a specific template directly (resolve ordinals like "first"/"second"/"Template #2" to 1-based integers).
- If the user says "rename the first template to X" or "change the second template's project name to Y", call edit_project_name with template_index set accordingly. Do NOT call switch_template first.
- If no template is referenced, it defaults to the currently selected template.

DUPLICATION RULES:
- Use duplicate_layout when the user asks to duplicate the current layout, this layout, the whole layout, or the entire layout. This copies the layout and all of its templates.
- Use duplicate_template only when the user asks to duplicate a template. It copies the currently selected template unless template_index is provided.
- Never call duplicate_template for a request to duplicate a layout.

DESIGN GALLERY TEMPLATE RULES:
- Use add_template_from_design_gallery when the user asks to add/insert a template from Design Gallery (examples: "add template from design gallery", "insert [name] template from gallery").
- Pass the user-provided template name/id/description in template_query.
- Do not call duplicate_template for design gallery requests.

PDP IMAGES:
- When the user asks to create a PDP image, a PDP image set, a hero image, "how to" image, "reason to believe" image, lifestyle image, comparison image, features image, or ingredients image for a product, call create_pdp_image_set. The generated image will be added as a new template inside the currently-open layout.
- Extract ASIN (B + 9 alphanumeric), GTIN (12-14 digit numeric), product name, or pasted URL into product_references with the right type.
- Always set is_set: true when the user asks for multiple images ("set", "multiple", "several"), false for a single image request. If unsure but the word "set" appears, use true.
- If the user asked for one specific type (e.g. "Create a hero image"), put that single type in image_types. If they asked for a "set" / "multiple images" / listed several types, include them all in image_types. If they just said "Create a PDP image" or "Create a PDP image set" without naming a type, leave image_types empty and the system will suggest a default set.
- Examples:
  - "Create a PDP image for B0BTMTTXBF" -> create_pdp_image_set with is_set=false, image_types=[] and product_references=[{type:"asin", value:"B0BTMTTXBF", display_name:"B0BTMTTXBF"}]
  - "Create a hero image for this shampoo" -> create_pdp_image_set with is_set=false, image_types=["hero"]
  - "Create a PDP image set for Red Bull" -> create_pdp_image_set with is_set=true, image_types=[] and product_references=[{type:"name", value:"Red Bull", display_name:"Red Bull"}]
  - "Create a how-to image for GTIN 00028400096348" -> create_pdp_image_set with is_set=false, image_types=["how_to"] and product_references=[{type:"gtin", value:"00028400096348"}]
- Do NOT use create_pdp_image_set for editing an existing element image \u2014 use replace_element_image or edit_element_image instead.
  `;

                const responseMessage = await this.callOpenAI({
                    messages: [
                        {
                            role: "system",
                            content: systemPrompt,
                        },
                        // Send only the latest 20 messages
                        ...this.messages.slice(-20).map((message) => ({
                            role: message.role,
                            content: message.content,
                        })),
                    ],
                    attachments: userMessage.attachments,
                    model: "o3",
                    functions: aiChatFunctions,
                });
                let assistantMessage;

                if (responseMessage.function_call) {
                    const functionName = responseMessage.function_call.name;
                    const functionArgs = JSON.parse(
                        responseMessage.function_call.arguments,
                    );

                    if (functionName === "select_element") {
                        let layer = this.findLayerByElementIdOrSelected(functionArgs.element_id);

                        if (!layer || !layer.relatedCanvasObject) {
                            // Check if user wanted to replace with a file - fallback to file search
                            const lastUserMsg = this.messages.filter(m => m.role === 'user').slice(-1)[0];
                            const fileMatch = lastUserMsg && lastUserMsg.content.match(/replace.*with\s+(.+?\.\w{3,4})/i);
                            if (fileMatch) {
                                const fileName = fileMatch[1].replace(/\.\w+$/, ''); // strip extension
                                try {
                                    this.pushMessage({ role: 'assistant', content: `Searching for "${fileName}"...` });
                                    const searchRes = await axios.get('/file/file_list', {
                                        params: { pageSize: 5, pageNumber: 1, searchKey: fileName, exactMatch: false },
                                    });
                                    const items = searchRes.data && searchRes.data.items;
                                    if (items && items.length > 0) {
                                        const fileUrl = items[0].full_url || items[0].path || items[0].thumbnailUrl;
                                        if (fileUrl) {
                                            const fullUrl = fileUrl.startsWith('http') ? fileUrl : window.location.origin + fileUrl;
                                            this.applyImageToCurrentElement(fullUrl);
                                            assistantMessage = { role: 'assistant', content: `Found "${items[0].name || fileName}" and replaced the image.` };
                                        } else {
                                            assistantMessage = { role: 'assistant', content: `Found the file but couldn't get its URL.` };
                                        }
                                    } else {
                                        assistantMessage = { role: 'assistant', content: `File "${fileName}" was not found in the library.` };
                                    }
                                } catch (e) {
                                    assistantMessage = { role: 'assistant', content: `Error searching for the file. Please try again.` };
                                }
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an element with ID: "${functionArgs.element_id}". This element may not exist.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }

                        this.setActiveObject(layer.relatedCanvasObject);

                        // Check if user wanted to replace with a file - auto-proceed
                        const lastUserMsgForSelect = this.messages.filter(m => m.role === 'user').slice(-1)[0];
                        const fileMatchAfterSelect = lastUserMsgForSelect && lastUserMsgForSelect.content.match(/replace.*with\s+(.+?\.\w{3,4})/i);
                        if (fileMatchAfterSelect && layer.relatedCanvasObject.type === 'image') {
                            const fileName = fileMatchAfterSelect[1].replace(/\.\w+$/, '');
                            try {
                                this.pushMessage({ role: 'assistant', content: `Searching for "${fileName}"...` });
                                const searchRes = await axios.get('/file/file_list', {
                                    params: { pageSize: 5, pageNumber: 1, searchKey: fileName, exactMatch: false },
                                });
                                const items = searchRes.data && searchRes.data.items;
                                if (items && items.length > 0) {
                                    const fileUrl = items[0].full_url || items[0].path || items[0].thumbnailUrl;
                                    if (fileUrl) {
                                        const fullUrl = fileUrl.startsWith('http') ? fileUrl : window.location.origin + fileUrl;
                                        this.applyImageToCurrentElement(fullUrl);
                                        assistantMessage = { role: 'assistant', content: `Found "${items[0].name || fileName}" and replaced the image.` };
                                    } else {
                                        assistantMessage = { role: 'assistant', content: `Found the file but couldn't get its URL.` };
                                    }
                                } else {
                                    assistantMessage = { role: 'assistant', content: `File "${fileName}" was not found in the library.` };
                                }
                            } catch (e) {
                                assistantMessage = { role: 'assistant', content: `Error searching for the file.` };
                            }
                        } else {
                            assistantMessage = {
                                role: "assistant",
                                content: `I've selected the element with ID: "${functionArgs.element_id}"`,
                            };
                        }
                    } else if (functionName === "open_product_selector") {
                        this.pushMessage({
                            role: "assistant",
                            content: `Would you like me to open the product selector so you can browse and choose a product image?`,
                            widgets: [
                                {
                                    type: 'action_buttons',
                                    id: 'product-selector-confirm',
                                    buttons: [
                                        { label: 'Open Product Selector', variant: 'primary', action: 'open_product_selector' },
                                        { label: 'Cancel', variant: 'outline-secondary', action: 'cancel_product_selector' },
                                    ],
                                },
                            ],
                        });
                        this.stopLoading();
                        return;
                    } else if (functionName === "add_element_product_id") {
                        this.executeProductAddFlow(functionArgs.product_id, false, functionArgs.apply_to_all);
                        assistantMessage = {
                            role: "assistant",
                            content: `I'm searching for "${functionArgs.product_id}". Please wait...`,
                        };
                    } else if (functionName === "change_element_product_id") {
                        const applyTo = functionArgs.apply_to || 'replace_selected_product_image';
                        const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                            this.selectedInstance.canvas.getActiveObject() : null;

                        if (!activeObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: "Please select an image on the canvas first so I can replace it.",
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }

                        // Check if selected element is a Product Image type
                        const isProductImage = activeObject.templateField &&
                            activeObject.templateField.type &&
                            activeObject.templateField.type.includes('Product Image');

                        if (isProductImage) {
                            // Use the product selector modal for actual product images
                            this.executeProductReplacementFlow(functionArgs.new_product_id, applyTo, false);
                            assistantMessage = {
                                role: "assistant",
                                content: `I'm searching for "${functionArgs.new_product_id}". Please wait...`,
                            };
                        } else {
                            // For non-product images (background, theme, etc.), search files and replace directly
                            try {
                                this.pushMessage({ role: 'assistant', content: `Searching for "${functionArgs.new_product_id}"...` });
                                const searchRes = await axios.get('/file/file_list', {
                                    params: { pageSize: 5, pageNumber: 1, searchKey: functionArgs.new_product_id, exactMatch: false },
                                });
                                const items = searchRes.data && searchRes.data.items;
                                if (items && items.length > 0) {
                                    const fileUrl = items[0].full_url || items[0].path || items[0].thumbnailUrl;
                                    if (fileUrl) {
                                        const fullUrl = fileUrl.startsWith('http') ? fileUrl : window.location.origin + fileUrl;
                                        this.applyImageToCurrentElement(fullUrl);
                                        assistantMessage = {
                                            role: 'assistant',
                                            content: `Found "${items[0].name || functionArgs.new_product_id}" and replaced the image.`,
                                        };
                                    } else {
                                        assistantMessage = { role: 'assistant', content: `Found the file but couldn't get its URL. Try using the product selector.` };
                                    }
                                } else {
                                    assistantMessage = { role: 'assistant', content: `File "${functionArgs.new_product_id}" was not found. Try using a different name or the product selector.` };
                                }
                            } catch (searchErr) {
                                console.error('File search error:', searchErr);
                                assistantMessage = { role: 'assistant', content: `Error searching for the file. Please try again.` };
                            }
                        }
                    } else if (functionName === "replace_element_image") {
                        let layer = this.findLayerByElementIdOrSelected(functionArgs.element_id);

                        if (layer) {
                            functionArgs.element_id = functionArgs.element_id || layer.element_id || layer.id || 'selected element';
                        }

                        // Fallback: use active canvas object directly if no layer found
                        let directCanvasObject = null;
                        if (!layer || !layer.relatedCanvasObject) {
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                                this.selectedInstance.canvas.getActiveObject() : null;
                            directCanvasObject = activeObject || this.targetElement || this.target;

                            if (directCanvasObject && directCanvasObject.getSrc) {
                                functionArgs.element_id = directCanvasObject.id || directCanvasObject.element_id || 'selected image';
                            } else {
                                assistantMessage = {
                                    role: "assistant",
                                    content: `I couldn't find an image element. Please select an image on the canvas first.`,
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }
                        }

                        const targetObject = (layer && layer.relatedCanvasObject) || directCanvasObject;
                        this.setActiveObject(targetObject);

                        const imageUrl = targetObject.getSrc ? targetObject.getSrc() : (this.target?.getSrc ? this.target.getSrc() : null);
                        if (!imageUrl) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't get the source image. Please try selecting the image again.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        const formData = new FormData();
                        let imageWidth = 0;
                        let imageHeight = 0;

                        fetch(imageUrl)
                            .then((response) => response.blob())
                            .then(async (blob) => {
                                const { width, height } =
                                    await this.getImageDimensions(blob);
                                formData.append("width", width);
                                formData.append("height", height);
                                formData.append(
                                    "promptForGenerating",
                                    functionArgs.description_of_the_new_image,
                                );
                                if (this.processCancelled) { this.stopLoading(); return; }
                                this.activeAbortController = new AbortController();
                                const response = await axios.post(
                                    "/ai-create-image",
                                    formData,
                                    {
                                        headers: {
                                            "Content-Type":
                                                "multipart/form-data",
                                        },
                                        signal: this.activeAbortController.signal,
                                    },
                                );
                                this.activeAbortController = null;

                                if (this.processCancelled) { this.stopLoading(); return; }
                                const newImageUrl =
                                    window.location.origin + response.data.url;

                                // Auto-apply if user specified in prompt, otherwise show widgets
                                if (functionArgs.auto_apply === 'replace') {
                                    this.pushMessage({ role: 'assistant', content: 'I\'ve generated and replaced the image.', imageUrl: newImageUrl });
                                    this.applyImageToCurrentElement(newImageUrl);
                                } else if (functionArgs.auto_apply === 'new_template') {
                                    this.pushMessage({ role: 'assistant', content: 'I\'ve generated the image and adding to a new template.', imageUrl: newImageUrl });
                                    this.addImageToNewTemplate(newImageUrl, 'original');
                                } else {
                                    this.pushMessage({
                                        role: "assistant",
                                        content: `I've generated a new image for the element "${functionArgs.element_id}". What would you like to do?`,
                                        imageUrl: newImageUrl,
                                        widgets: [
                                            {
                                                type: 'radio',
                                                id: 'image-save-action',
                                                options: [
                                                    { value: 'replace', label: 'Replace current image' },
                                                    { value: 'new_template', label: 'Add to new template' },
                                                ],
                                            },
                                            {
                                                type: 'dropdown',
                                                id: 'template-size',
                                                label: 'Template size',
                                                dependsOn: { widgetId: 'image-save-action', value: 'new_template' },
                                                placeholder: 'Select size...',
                                                options: [
                                                    { value: 'original', label: 'Original size' },
                                                    { value: '1500x1500', label: '1500 x 1500' },
                                                    { value: '2000x2000', label: '2000 x 2000' },
                                                    { value: '2500x2500', label: '2500 x 2500' },
                                                    { value: '3000x3000', label: '3000 x 3000' },
                                                ],
                                            },
                                            {
                                                type: 'action_buttons',
                                                id: 'image-save-confirm',
                                                buttons: [
                                                    { label: 'Apply', variant: 'primary', action: 'apply_image' },
                                                    { label: 'Discard', variant: 'outline-secondary', action: 'discard_image' },
                                                ],
                                            },
                                        ],
                                        _imageActionContext: {
                                            newImageUrl,
                                            elementId: functionArgs.element_id,
                                        },
                                    });
                                }
                                this.stopLoading();
                            })
                            .catch((error) => {
                                console.error(
                                    "Error fetching or processing the image:",
                                    error,
                                );
                                this.pushMessage({ role: 'assistant', content: 'Sorry, there was an error processing the image.' });
                                this.stopLoading();
                            });
                        return; // async fetch chain handles its own completion
                    } else if (functionName === "edit_element_image") {
                        let layer = this.findLayerByElementIdOrSelected(functionArgs.element_id);

                        if (layer) {
                            functionArgs.element_id = functionArgs.element_id || layer.element_id || layer.id || 'selected element';
                        }

                        // Fallback: if no layer found, try active canvas object, then auto-find first image
                        let directCanvasObject = null;
                        if (!layer || !layer.relatedCanvasObject) {
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                                this.selectedInstance.canvas.getActiveObject() : null;
                            directCanvasObject = activeObject || this.targetElement || this.target;

                            // If still nothing, auto-find first image element in layers
                            if (!directCanvasObject || !directCanvasObject.getSrc) {
                                const imageLayer = this.layersOrdered.find(l =>
                                    l.relatedCanvasObject && l.relatedCanvasObject.type === 'image' && l.relatedCanvasObject.getSrc
                                );
                                if (imageLayer) {
                                    directCanvasObject = imageLayer.relatedCanvasObject;
                                    functionArgs.element_id = imageLayer.element_id || imageLayer.id || 'auto-selected image';
                                }
                            }

                            if (directCanvasObject && directCanvasObject.getSrc) {
                                functionArgs.element_id = functionArgs.element_id || directCanvasObject.id || directCanvasObject.element_id || 'selected image';
                            } else {
                                assistantMessage = {
                                    role: "assistant",
                                    content: `I couldn't find an image element. Please select an image on the canvas first.`,
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }
                        }

                        const targetObject = (layer && layer.relatedCanvasObject) || directCanvasObject;
                        this.setActiveObject(targetObject);

                        const imageUrl = targetObject.getSrc ? targetObject.getSrc() : (this.target?.getSrc ? this.target.getSrc() : null);
                        if (!imageUrl) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't get the source image. Please try selecting the image again.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        const formData = new FormData();
                        const newFileName = "image.png";

                        fetch(imageUrl)
                            .then((response) => response.blob())
                            .then(async (blob) => {
                                const file = new File([blob], newFileName, {
                                    type: blob.type,
                                });
                                const { width, height } =
                                    await this.getImageDimensions(blob);
                                formData.append("width", width);
                                formData.append("height", height);
                                formData.append("image", file);
                                formData.append("infillMethod", "gemini_nano_banana_pro");
                                formData.append(
                                    "overwritePrompt",
                                    functionArgs.changes_to_the_image,
                                );

                                // Add reference image from another template if specified
                                if (functionArgs.reference_template_index) {
                                    try {
                                        const refIdx = functionArgs.reference_template_index - 1;
                                        const refPreview = (window.previews || [])[refIdx];
                                        if (refPreview && refPreview.canvas) {
                                            let refObject = null;
                                            if (functionArgs.reference_element_id) {
                                                refObject = refPreview.canvas.getObjects().find(obj =>
                                                    obj.id === functionArgs.reference_element_id ||
                                                    obj.element_id === functionArgs.reference_element_id
                                                );
                                            }
                                            if (!refObject) {
                                                refObject = refPreview.canvas.getObjects().find(obj =>
                                                    obj.type === 'image' && obj.getSrc
                                                );
                                            }
                                            if (refObject && refObject.getSrc) {
                                                const refUrl = refObject.getSrc();
                                                const refResponse = await fetch(refUrl);
                                                const refBlob = await refResponse.blob();
                                                const refFile = new File([refBlob], 'reference.png', { type: refBlob.type });
                                                formData.append("secondImage", refFile);
                                            }
                                        }
                                    } catch (refErr) {
                                        console.error('Error fetching reference image:', refErr);
                                    }
                                }

                                if (this.processCancelled) { this.stopLoading(); return; }
                                this.activeAbortController = new AbortController();
                                const response = await axios.post(
                                    "/ai-edit-image",
                                    formData,
                                    {
                                        headers: {
                                            "Content-Type":
                                                "multipart/form-data",
                                        },
                                        signal: this.activeAbortController.signal,
                                    },
                                );
                                this.activeAbortController = null;

                                if (this.processCancelled) { this.stopLoading(); return; }
                                const newImageUrl =
                                    window.location.origin + response.data.url;

                                // Auto-apply if user specified in prompt, otherwise show widgets
                                if (functionArgs.auto_apply === 'replace') {
                                    this.pushMessage({ role: 'assistant', content: 'I\'ve edited and replaced the image.', imageUrl: newImageUrl });
                                    this.applyImageToCurrentElement(newImageUrl);
                                } else if (functionArgs.auto_apply === 'new_template') {
                                    this.pushMessage({ role: 'assistant', content: 'I\'ve edited the image and adding to a new template.', imageUrl: newImageUrl });
                                    this.addImageToNewTemplate(newImageUrl, 'original');
                                } else {
                                    this.pushMessage({
                                        role: "assistant",
                                        content: `I've edited the image of the element "${functionArgs.element_id}". What would you like to do?`,
                                        imageUrl: newImageUrl,
                                        widgets: [
                                            {
                                                type: 'radio',
                                                id: 'image-save-action',
                                                options: [
                                                    { value: 'replace', label: 'Replace current image' },
                                                    { value: 'new_template', label: 'Add to new template' },
                                                ],
                                            },
                                            {
                                                type: 'dropdown',
                                                id: 'template-size',
                                                label: 'Template size',
                                                dependsOn: { widgetId: 'image-save-action', value: 'new_template' },
                                                placeholder: 'Select size...',
                                                options: [
                                                    { value: 'original', label: 'Original size' },
                                                    { value: '1500x1500', label: '1500 x 1500' },
                                                    { value: '2000x2000', label: '2000 x 2000' },
                                                    { value: '2500x2500', label: '2500 x 2500' },
                                                    { value: '3000x3000', label: '3000 x 3000' },
                                                ],
                                            },
                                            {
                                                type: 'action_buttons',
                                                id: 'image-save-confirm',
                                                buttons: [
                                                    { label: 'Apply', variant: 'primary', action: 'apply_image' },
                                                    { label: 'Discard', variant: 'outline-secondary', action: 'discard_image' },
                                                ],
                                            },
                                        ],
                                        _imageActionContext: {
                                            newImageUrl,
                                            elementId: functionArgs.element_id,
                                        },
                                    });
                                }
                                this.stopLoading();
                            })
                            .catch((error) => {
                                console.error(
                                    "Error fetching or processing the image:",
                                    error,
                                );
                                this.pushMessage({ role: 'assistant', content: 'Sorry, there was an error processing the image.' });
                                this.stopLoading();
                            });
                        return; // async fetch chain handles its own completion
                    } else if (functionName === "edit_element_text") {
                        const updates = functionArgs.updates || [];
                        const applied = [];
                        const failed = [];

                        for (const update of updates) {
                            const layer = this.findLayerByElementIdOrSelected(update.element_id);

                            if (layer && layer.relatedCanvasObject) {
                                this.setActiveObject(layer.relatedCanvasObject);

                                editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: update.new_text, action: "inputText" });

                                if (functionArgs.apply_to_all) {
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: true, action: "text", keys: ["text"] });
                                }

                                const scope = ShowPageScopeCollector.get(['previews']);
                                const instance_id = this.selectedInstance?.id;

                                editorControlEventBroker.fire(EVENTS.PROPERTIES_MODULE_CHANGED, {
                                    action: 'name',
                                    name: update.new_text,
                                    fieldId: layer.id,
                                    link: '',
                                });

                                if (functionArgs.apply_to_all) {
                                    const otherTemplates = (scope.previews || []).filter(p => p.id !== instance_id).map(p => p.id);

                                    if (otherTemplates.length > 0) {
                                        editorControlEventBroker.fire(EVENTS.APPLY_TO_PROPERTIES, {
                                            action: 'apply_name_to',
                                            name: update.new_text,
                                            fieldId: layer.id,
                                            templates: otherTemplates,
                                            oldName: layer.name,
                                        });
                                    }
                                }

                                applied.push(`"${layer.name || update.element_id || 'selected'}" - The new text is: "${update.new_text}"`);
                            } else {
                                failed.push(update.element_id || 'selected');
                            }
                        }

                        if (this.selectedInstance?.canvas) {
                            this.selectedInstance.canvas.fire('layers:update', this.selectedInstance.id);
                        }

                        let summary = `I've edited ${applied.length} field(s): \n${applied.join('\n')}`;

                        if (failed.length) {
                            summary += ` Could not find: ${failed.join(', ')}.`;
                        }

                        assistantMessage = { role: "assistant", content: summary };
                    } else if (functionName === "edit_element_color") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find an element with ID: "${functionArgs.element_id}". Please select an element first.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        // Check if the element is not an image
                        const isImage = layer.relatedCanvasObject.type === 'image' || 
                                       (layer.relatedCanvasObject.id && 
                                        layer.relatedCanvasObject.id.includes('image_')) ||
                                       (layer.relatedCanvasObject.element_id && 
                                        layer.relatedCanvasObject.element_id.includes('image_'));
                        
                        if (isImage) {
                            assistantMessage = {
                                role: "assistant",
                                content: `The selected field is an image and I can't update the text color for it.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: functionArgs.new_color,
                            action: "fontColor",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "fontColor",
                                keys: ["color"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've edited the text of the element with ID: "${functionArgs.element_id}". The new color is: "${functionArgs.new_color}"`,
                        };
                    } else if (functionName === "edit_element_font_size") {
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a text element with ID: "${functionArgs.element_id}". Please select a text element first.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: functionArgs.new_font_size,
                            action: "fontSize",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "fontSize",
                                keys: ["size"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've edited the text of the element with ID: "${functionArgs.element_id}". The new font size is: "${functionArgs.new_font_size}"`,
                        };
                    } else if (functionName === "edit_element_font_family") {
                        // Use another prompt for selecting the prompt. We do not send the font list in the system prompt to improve accuracy.
                        const responseMessage = await this.callOpenAI({
                            messages: [
                                {
                                    role: "system",
                                    content: `You are a banner ad designer AI assistant.`,
                                },
                                // Send only the latest 20 messages
                                ...this.messages.slice(-20).map((message) => ({
                                    role: message.role,
                                    content: message.content,
                                })),
                                {
                                    role: "system",
                                    content: `You must return a font that satisfies the requeriments of the latest message. The font must be one from the following list:\n\nJSON.parse(JSON.stringify(this.getFonts())).`,
                                },
                            ],
                            response_format: {
                                name: "font",
                                schema: {
                                    type: "object",
                                    properties: {
                                        font: {
                                            anyOf: [
                                                {
                                                    type: "string",
                                                    description:
                                                        "The name of the font.",
                                                },
                                                {
                                                    type: "null",
                                                    description:
                                                        "Null if no font is specified.",
                                                },
                                            ],
                                        },
                                    },
                                    required: ["font"],
                                    additionalProperties: false,
                                },
                                strict: true,
                            },
                            model: "gpt-5",
                        });
                        const newFontName = JSON.parse(
                            responseMessage.content,
                        ).font;
                        
                        if (!newFontName) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a font that matches your requirements.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        let layer;
                        if (functionArgs.element_id) {
                            layer = this.layersOrdered.find(layer =>
                                layer.element_id === functionArgs.element_id ||
                                layer.id === functionArgs.element_id ||
                                (layer.relatedCanvasObject &&
                                    (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                        layer.relatedCanvasObject.id === functionArgs.element_id))
                            );
                        } else {
                            // Use currently selected object
                            const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                this.selectedInstance.canvas.getActiveObject() : null;
                            
                            const selectedElement = activeObject || this.targetElement || this.target;
                            
                            if (selectedElement) {
                                layer = this.layersOrdered.find(layer => 
                                    layer.relatedCanvasObject === selectedElement || 
                                    (layer.relatedCanvasObject && 
                                        (layer.relatedCanvasObject.id === selectedElement.id ||
                                         layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                );
                                
                                if (layer) {
                                    functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                }
                            }
                        }
                        
                        if (!layer || !layer.relatedCanvasObject) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find a text element with ID: "${functionArgs.element_id}". Please select a text element first.`,
                            };
                            this.pushMessage(assistantMessage);
                            this.stopLoading();
                            return;
                        }
                        
                        this.setActiveObject(layer.relatedCanvasObject);
                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                            value: newFontName,
                            action: "fontFamily",
                        });
                        if (functionArgs.apply_to_all) {
                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                value: true,
                                action: "fontFamily",
                                keys: ["font"],
                            });
                        }
                        assistantMessage = {
                            role: "assistant",
                            content: `I've changed the font of the element with ID: "${functionArgs.element_id}". The new font is: "${newFontName}"`,
                        };
                    } else if (functionName === "edit_project_name") {
                        const previews = window.previews || [];
                        const targetIndex = functionArgs.template_index;
                        let preview;
                        if (targetIndex && targetIndex > 0 && targetIndex <= previews.length) {
                            preview = previews[targetIndex - 1];
                        } else {
                            preview = previews.find((p) => p.id === this.selectedInstance.id);
                        }
                        if (!preview) {
                            assistantMessage = {
                                role: "assistant",
                                content: `I couldn't find that template. There are ${previews.length} template(s) in this layout.`,
                            };
                        } else {
                            const instanceId = preview.id;
                            const grid = $("#template-group-preview");
                            const customer_id = grid.attr("data-customer-id");
                            const layout_id = grid.attr("data-layout-id");
                            const dataForUpdate = [
                                {
                                    instance_id: instanceId,
                                    key: "project_name",
                                    value: functionArgs.new_project_name,
                                },
                            ];
                            try {
                                await axios.put(
                                    `/banner/${customer_id}/group/${layout_id}/save_changes`,
                                    dataForUpdate,
                                );
                                preview.data.project_name =
                                    functionArgs.new_project_name;
                                const templateTitleElement = $(
                                    `.template-item[data-instance=${instanceId}]`,
                                ).find(".template-name-canvas");
                                templateTitleElement.html(
                                    window.getTemplateNameText(
                                        preview.template,
                                        preview.data,
                                    ),
                                );
                                templateTitleElement.css({
                                    fontSize: "18px",
                                    fontFamily: "Arial",
                                    color: "rgb(0, 0, 0)",
                                    fontWeight: "normal",
                                });
                                assistantMessage = {
                                    role: "assistant",
                                    content: `I've successfully updated the project name. The new name is: ${functionArgs.new_project_name}`,
                                };
                            } catch (e) {
                                console.error(e);
                                assistantMessage = {
                                    role: "assistant",
                                    content: `There was an error and I couldn't update the project name.`,
                                };
                            }
                        }
                    } else if (functionName === "publish_to_platform") {
                        try {
                            this.startLoading();

                            const platformType =
                                functionArgs.platform_type?.toLowerCase();

                            if (!platformType) {
                                assistantMessage = {
                                    role: "assistant",
                                    content: `I couldn't determine which platform you want to publish to. Please specify Bynder, Salsify, or Walmart.`,
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            $("#publishToPlatformModal").modal("show");

                            setTimeout(() => {
                                const platformSelect = $(
                                    "#deployment_platform",
                                );
                                const options = platformSelect.find("option");
                                let platformFound = false;
                                let multiplePlatforms = false;
                                let matchingPlatformIds = [];

                                options.each(function () {
                                    const optionPlatform = $(this)
                                        .data("platform")
                                        ?.toLowerCase();
                                    if (optionPlatform === platformType) {
                                        matchingPlatformIds.push($(this).val());
                                    }
                                });

                                if (matchingPlatformIds.length > 0) {
                                    platformFound = true;

                                    if (matchingPlatformIds.length > 1) {
                                        multiplePlatforms = true;
                                    } else {
                                        platformSelect
                                            .val(matchingPlatformIds[0])
                                            .trigger("change");
                                    }
                                }

                                if (platformFound) {
                                    if (multiplePlatforms) {
                                        assistantMessage = {
                                            role: "assistant",
                                            content: `I found multiple ${platformType} connections. Please select the specific one you want to use from the publish dialog.`,
                                        };
                                    } else {
                                        assistantMessage = {
                                            role: "assistant",
                                            content: `I've opened the publish dialog with ${platformType} selected for you.`,
                                        };
                                    }
                                } else {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `I couldn't find a ${platformType} connection. Please select a platform from the publish dialog manually.`,
                                    };
                                }

                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                            }, 500);

                            return;
                        } catch (e) {
                            console.error(
                                "Error setting up publish dialog:",
                                e,
                            );
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error opening the publish dialog. Please try using the Publish button manually.`,
                            };
                            this.stopLoading();
                        }
                    } else if (functionName === "generate_proof_link") {
                        try {
                            const layoutVersionId = $(
                                "#template-group-preview",
                            ).attr("data-layout-id");

                            this.startLoading();

                            const response = await axios({
                                method: "get",
                                url: `/banner/groups/${layoutVersionId}/get_latest_share_proof_link_for_version`,
                            });

                            if (response.data && response.data.url) {
                                assistantMessage = {
                                    role: "assistant",
                                    content: `Here's the proof link:`,
                                    proofLink: response.data.url,
                                };
                            } else {
                                const response = await axios.post(
                                    `/banner/groups/${layoutVersionId}/share_proof_link`,
                                    {
                                        from: 0,
                                        template_images: window.previews.map(
                                            (preview) => ({
                                                instance_id: preview.id,
                                                name:
                                                    preview.template?.name ||
                                                    "",
                                            }),
                                        ),
                                    },
                                );

                                if (response.data && response.data.url) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Here's the proof link:`,
                                        proofLink: response.data.url,
                                    };
                                } else {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Sorry, I couldn't generate a proof link. Please try again or use the 'Share' button manually.`,
                                    };
                                }
                            }

                            this.stopLoading();
                        } catch (e) {
                            console.error("Error generating proof link:", e);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error generating the proof link. Please try using the 'Share' button manually.`,
                            };
                            this.stopLoading();
                        }
                    } else if (functionName === "generate_image") {
                        try {
                            window.addEventListener('image-generation:popup-ready', () => {
                                $('#generateImageModal #image_generation_function').val(functionArgs.model);
                                $('#generateImageModal #image_generation_prompt').val(functionArgs.prompt);
                                $('#generateImageModal .btn-number-of-images[data-number-of-images=' + functionArgs.number_of_images + ']').click();
                                setTimeout(() => {
                                    $('#generateImageModal .modal-body').animate({
                                        scrollTop: $('#generateImageModal .modal-body')[0].scrollHeight
                                    }, 500);
                                }, 250);
                                setTimeout(() => {
                                    window.addEventListener('image-generation:completed', () => {
                                        setTimeout(() => {
                                            $('#generateImageModal .modal-body').animate({
                                                scrollTop: $('#generateImageModal .modal-body')[0].scrollHeight
                                            }, 500);
                                        }, 250);
                                        setTimeout(() => {
                                            if (functionArgs.number_of_images === 1) {
                                                if (functionArgs.add_to_new_template_of_size) {
                                                    $('#addToNewTemplateContainer #customSizeOption').click();
                                                    setTimeout(() => {
                                                        $('#generateImageModal #addToNewTemplateContainer #customSizeOption').click();
                                                        $('#custom-width').val(functionArgs.add_to_new_template_of_size.width);
                                                        $('#custom-height').val(functionArgs.add_to_new_template_of_size.height);
                                                        $('.swal2-confirm').click();
                                                        this.pushMessage({
                                                            role: "assistant",
                                                            content: `Image generation completed! The generated image will be added to a new ${functionArgs.add_to_new_template_of_size.width}x${functionArgs.add_to_new_template_of_size.height} template.`,
                                                        });
                                                        this.saveMessages();
                                                        this.scrollToBottomOfMessages();
                                                    }, 500);
                                                } else {
                                                    $('#generateImageModal #addBackgroundBtn').click();
                                                    this.pushMessage({
                                                        role: "assistant",
                                                        content: `Image generation completed! The generated image will be added to the selected template.`,
                                                    });
                                                    this.saveMessages();
                                                    this.scrollToBottomOfMessages();
                                                }
                                            } else {
                                                this.pushMessage({
                                                    role: "assistant",
                                                    content: `Image generation completed! Please choose one of the generated images to add and click "Add to template".`,
                                                });
                                                this.saveMessages();
                                                this.scrollToBottomOfMessages();
                                            }
                                        }, 3000);
                                    }, { once: true });
                                }, 750);
                                $('#generateImageModal #generateBackgroundBtn').click();
                            }, { once: true });
                            window.openImageGenrationModal();
                        } catch (e) {
                            console.error("Error opening image generation popup:", e);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error opening the image generation popup. Please try using the 'Generate Image' button manually.`,
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === "check_product_id") {
                        try {
                            this.startLoading();

                            // Manually execute the parsing logic that would normally run server-side
                            const rawInput = functionArgs.product_id;
                            let parsedArgs = {};

                            // Validate input format
                            const validationResult = this.validateProductIdInput(rawInput);
                            if (!validationResult.valid) {
                                assistantMessage = {
                                    role: "assistant",
                                    content: validationResult.message,
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Check if it contains multiple IDs (contains spaces, commas, or newlines)
                            if (/[\s,\n]/.test(rawInput)) {
                                // Parse multiple identifiers
                                const identifiers = rawInput
                                    .split(/[\s,\n]+/) // Split by spaces, commas, or newlines
                                    .map((id) => id.trim())
                                    .filter((id) => id.length > 0); // Remove empty strings

                                if (identifiers.length === 0) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `No valid product identifiers provided.`,
                                    };
                                    this.pushMessage(assistantMessage);
                                    this.stopLoading();
                                    return;
                                }

                                parsedArgs = { multiple: true, identifiers };
                            } else {
                                // Single identifier case
                                parsedArgs = {
                                    multiple: false,
                                    product_id: rawInput.trim(),
                                };
                            }

                            // Now use the parsed arguments
                            if (parsedArgs.multiple) {
                                // Handle multiple product IDs
                                const { identifiers } = parsedArgs;

                                if (!identifiers || !identifiers.length) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `I need valid product identifiers to check. Please provide ASINs, GTINs, or UPCs.`,
                                    };
                                    this.pushMessage(assistantMessage);
                                    this.stopLoading();
                                    return;
                                }

                                const results = [];
                                let foundCount = 0;
                                let notFoundCount = 0;

                                // Process each identifier sequentially
                                for (const identifier of identifiers) {
                                    try {
                                        const response = await axios.get(
                                            "/file/file_list",
                                            {
                                                params: {
                                                    pageSize: 1, // Only need to know if it exists
                                                    pageNumber: 1,
                                                    searchKey: identifier,
                                                    exactMatch: true,
                                                },
                                            },
                                        );

                                        const found =
                                            response.data &&
                                            response.data.items &&
                                            response.data.items.length > 0;

                                        results.push({
                                            identifier,
                                            found,
                                            count: found
                                                ? response.data.items.length
                                                : 0,
                                        });

                                        if (found) {
                                            foundCount++;
                                        } else {
                                            notFoundCount++;
                                        }
                                    } catch (error) {
                                        console.error(
                                            `Error checking identifier ${identifier}:`,
                                            error,
                                        );
                                        results.push({
                                            identifier,
                                            found: false,
                                            error: true,
                                        });
                                        notFoundCount++;
                                    }
                                }

                                // Generate a comprehensive response with HTML instead of markdown
                                let content = `<p><strong>Product Identifier Check Results</strong></p>`;
                                content += `<p>Checked ${identifiers.length} identifiers:</p>`;
                                content += `<ul>`;
                                content += `<li>\u2705 Found: ${foundCount}</li>`;
                                content += `<li>\u274c Not found: ${notFoundCount}</li>`;
                                content += `</ul>`;
                                content += `<p><strong>Details:</strong></p>`;
                                content += `<ul>`;

                                // Add each identifier to the content
                                results.forEach((result) => {
                                    if (result.found) {
                                        content += `<li><strong>${result.identifier}</strong>: \u2705 Found</li>`;
                                    } else if (result.error) {
                                        content += `<li><strong>${result.identifier}</strong>: \u2753 Error checking</li>`;
                                    } else {
                                        content += `<li><strong>${result.identifier}</strong>: \u274c Not found</li>`;
                                    }
                                });

                                content += `</ul>`;

                                // Collect all found product IDs for the Select button
                                const foundProductIds = results
                                    .filter((result) => result.found)
                                    .map((result) => result.identifier);

                                const hasFoundProducts =
                                    foundProductIds.length > 0;
                                const allFoundProductIds =
                                    foundProductIds.join(" ");

                                assistantMessage = {
                                    role: "assistant",
                                    content,
                                    // Add hasSelectButton and productId for all found products
                                    hasSelectButton: hasFoundProducts,
                                    productId: allFoundProductIds,
                                };
                                this.stopLoading();
                            } else {
                                // Handle single product ID (original behavior)
                                const productId = parsedArgs.product_id;

                                if (!productId) {
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `I need a valid product ID to check. Please provide a product ID, GTIN, or ASIN.`,
                                    };
                                    this.pushMessage(assistantMessage);
                                    this.stopLoading();
                                    return;
                                }

                                try {
                                    const response = await axios.get(
                                        "/file/file_list",
                                        {
                                            params: {
                                                pageSize: 12,
                                                pageNumber: 1,
                                                searchKey: productId,
                                                exactMatch: true,
                                            },
                                        },
                                    );

                                    if (
                                        response.data &&
                                        response.data.items &&
                                        response.data.items.length > 0
                                    ) {
                                        const foundProducts =
                                            response.data.items;

                                        assistantMessage = {
                                            role: "assistant",
                                            content: `\u2705 Product ID "${productId}" exists in the database. Found ${foundProducts.length} matching item${foundProducts.length > 1 ? "s" : ""}.`,
                                            foundProductData: response.data,
                                            hasSelectButton: true,
                                            productId: productId,
                                        };
                                        this.stopLoading();
                                    } else {
                                        assistantMessage = {
                                            role: "assistant",
                                            content: `Product ID "${productId}" was not found in the database. Please check the ID and try again.`,
                                        };
                                        this.stopLoading();
                                    }
                                } catch (error) {
                                    console.error(
                                        "Error checking product existence:",
                                        error,
                                    );
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Sorry, there was an error checking if product ID "${productId}" exists. Please try again.`,
                                    };
                                    this.stopLoading();
                                }
                            }
                        } catch (e) {
                            console.error("Error in check_product_id:", e);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error processing your request. Please try again.`,
                            };
                            this.stopLoading();
                        }
                    } else if (functionName === 'rotate_image' || functionName === 'remove_image_background') {
                        try {
                            // Common image handling logic
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.id === functionArgs.element_id ||
                                            layer.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected image';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an image to modify. Please select an image element first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }
                            
                            // Check if this is actually an image
                            const isImage = layer.relatedCanvasObject.type === 'image' || 
                                (layer.relatedCanvasObject.id && 
                                 layer.relatedCanvasObject.id.includes('image'));
                                 
                            if (!isImage) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element is not an image. Please select an image element.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Function-specific operations
                            if (functionName === 'rotate_image') {
                                const angle = functionArgs.angle || 90; // Default to 90 degrees if not specified

                                // Get current angle
                                const currentAngle = layer.relatedCanvasObject.angle || 0;

                                // Set new angle (adds to the current angle)
                                layer.relatedCanvasObject.rotate(currentAngle + angle);

                                // Update coordinates
                                layer.relatedCanvasObject.setCoords();

                                // Fire the object:rotating event to ensure changes are saved
                                layer.relatedCanvasObject.canvas.fire('object:rotating', { target: layer.relatedCanvasObject });

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've rotated the image by ${angle} degrees.`
                                };
                            } else if (functionName === 'remove_image_background') {
                                try {
                                    // Determine if it's a product image or a theme image
                                    const isProductImage = layer.relatedCanvasObject.id &&
                                        (layer.relatedCanvasObject.id.includes('product_image') ||
                                            layer.relatedCanvasObject.id.includes('image_'));

                                    // Show loading notification
                                    toastr.info(
                                        `<div class="d-inline-flex align-content-center">Removing background...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
                                        null,
                                        {
                                            timeOut: 0,
                                            tapToDismiss: false,
                                            closeButton: false
                                        });

                                    // Get the image file path from the source URL
                                    const imageSrc = layer.relatedCanvasObject.getSrc();
                                    const filePath = new URL(imageSrc).searchParams.get('file');

                                    if (!filePath) {
                                        throw new Error('Failed to extract file path from image source');
                                    }

                                    // Call the background removal API with the correct service
                                    const { data } = await FileUploadService.removeImageBackground({
                                        filePath: filePath,
                                        is_crop_to_image: !!functionArgs.crop_to_image
                                    });

                                    if (!data || !data.imgUrl) {
                                        throw new Error('Invalid response from background removal service');
                                    }

                                    if (isProductImage) {
                                        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                            action: 'removeBackgroundProductImage',
                                            value: {
                                                imgUrl: data.imgUrl,
                                                filePath: filePath,
                                                target: layer.relatedCanvasObject,
                                                isCopyAndReplace: true,
                                                instanceId: this.selectedInstance.id,
                                                isCropToImage: !!functionArgs.crop_to_image
                                            }
                                        });
                                    } else {
                                        // It's a theme image
                                        editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                            action: 'removeBackgroundThemeImgBG',
                                            value: {
                                                imgUrl: `/share?file=${data.imgUrl}`,
                                                objectId: layer.relatedCanvasObject.id,
                                                instanceId: this.selectedInstance.id,
                                                target: layer.relatedCanvasObject
                                            }
                                        });
                                    }

                                    toastr.clear();
                                    assistantMessage = {
                                        role: 'assistant',
                                        content: `I've successfully removed the background from the image.`
                                    };
                                } catch (error) {
                                    console.error('Error removing image background:', error);
                                    toastr.clear();
                                    toastr.error('Something went wrong while removing background');
                                    assistantMessage = {
                                        role: 'assistant',
                                        content: `Sorry, there was an error removing the image background. Please try again.`
                                    };
                                }

                                // Always set loading to false and add the message
                                this.stopLoading();
                                this.pushMessage(assistantMessage);
                                return;
                            }

                            // Common updates for move and rotate operations
                            layer.relatedCanvasObject.canvas.renderAll();

                            // Fire object:moving for all operations to ensure changes are saved
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                        } catch (error) {
                            console.error(`Error in ${functionName}:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error modifying the image. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'center_object') {
                        try {
                            // Use provided element_id or fallback to currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected object';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an object to center. Please select an object first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);
                            
                            // Determine what element type this is to use the appropriate event
                            const elementType = this.getElementType(layer.relatedCanvasObject);
                            const eventType = this.getElementEventType(layer.relatedCanvasObject);
                            
                            // Map alignment to the proper action
                            const alignmentActions = {
                                'both': 'center', // Center both horizontally and vertically
                                'horizontal': 'centerToTemplateWidth', // Center only horizontally
                                'vertical': 'centerToTemplateHeight' // Center only vertically
                            };
                            
                            // Get the action based on the alignment parameter or default to center both
                            const action = alignmentActions[functionArgs.alignment || 'both'] || 'center';
                            
                            // Different actions based on element type
                            if (elementType === 'text') {
                                if (action === 'center') {
                                    // For text, we need to center both horizontally and vertically
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        action: 'centerToTemplateWidth'
                                    });
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        action: 'centerToTemplateHeight'
                                    });
                                } else {
                                    // For horizontal or vertical centering
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        action: action
                                    });
                                }
                            } else if (elementType === 'image') {
                                // Check if this is a smart object by looking for a groupName property
                                const isSmartObject = !!layer.relatedCanvasObject.groupName;
                                
                                if (action === 'center') {
                                    // Create center action data for both horizontal and vertical centering
                                    const centerActionData = {
                                        action: 'centerImage',
                                        tooltip: isSmartObject ? 'Center to SmartObject size' : 'Center to template size',
                                        centerPoint: layer.relatedCanvasObject.canvas.getCenter(),
                                        isSmartObject: isSmartObject
                                    };
                                    
                                    // If it's a smart object, get the proper center point
                                    if (isSmartObject) {
                                        // Find the mask object for this smart object group
                                        const maskObject = layer.relatedCanvasObject.canvas
                                            .getObjects()
                                            .find(obj => obj.groupName === layer.relatedCanvasObject.groupName && 
                                                  (obj.id && obj.id.startsWith('mask_')));
                                                  
                                        if (maskObject) {
                                            centerActionData.centerPoint = maskObject.getCenterPoint();
                                        }
                                    }
                                    
                                    // For images, centerImage centers both horizontally and vertically
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        action: 'centerImage',
                                        value: centerActionData
                                    });
                                } else if (action === 'centerToTemplateWidth') {
                                    // For horizontal centering of images
                                    const horizontalCenterData = {
                                        action: 'horizontalCenter',
                                        tooltip: 'Center Horizontally',
                                        centerPoint: layer.relatedCanvasObject.canvas.getCenter(),
                                        isSmartObject: isSmartObject
                                    };
                                    
                                    if (isSmartObject) {
                                        const maskObject = layer.relatedCanvasObject.canvas
                                            .getObjects()
                                            .find(obj => obj.groupName === layer.relatedCanvasObject.groupName && 
                                                  (obj.id && obj.id.startsWith('mask_')));
                                                  
                                        if (maskObject) {
                                            horizontalCenterData.centerPoint = maskObject.getCenterPoint();
                                        }
                                    }
                                    
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        action: 'horizontalCenter',
                                        value: horizontalCenterData
                                    });
                                } else if (action === 'centerToTemplateHeight') {
                                    // For vertical centering, use the verticalCenter action
                                    const verticalCenterData = {
                                        action: 'verticalCenter',
                                        tooltip: 'Center Vertically',
                                        centerPoint: layer.relatedCanvasObject.canvas.getCenter(),
                                        isSmartObject: isSmartObject
                                    };
                                    
                                    if (isSmartObject) {
                                        const maskObject = layer.relatedCanvasObject.canvas
                                            .getObjects()
                                            .find(obj => obj.groupName === layer.relatedCanvasObject.groupName && 
                                                  (obj.id && obj.id.startsWith('mask_')));
                                                  
                                        if (maskObject) {
                                            verticalCenterData.centerPoint = maskObject.getCenterPoint();
                                        }
                                    }
                                    
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        action: 'verticalCenter',
                                        value: verticalCenterData
                                    });
                                }
                            } else {
                                // For shapes or other objects
                                if (action === 'center') {
                                    // Center both horizontally and vertically
                                    // For horizontal centering of shapes
                                    const canvas = layer.relatedCanvasObject.canvas;
                                    const obj = layer.relatedCanvasObject;
                                    
                                    // First center horizontally
                                    const zoom = canvas.getZoom();
                                    const objectWidth = obj.width * obj.scaleX;
                                    const templateWidth = canvas.width / zoom;
                                    const newLeft = (templateWidth - objectWidth) / 2;
                                    
                                    obj.left = newLeft;
                                    obj.setCoords();
                                    
                                    // Then center vertically
                                    const objectHeight = obj.height * obj.scaleY;
                                    const templateHeight = canvas.height / zoom;
                                    const newTop = (templateHeight - objectHeight) / 2;
                                    
                                    obj.top = newTop;
                                    obj.setCoords();
                                    
                                    // Render and trigger moving event to save changes
                                    canvas.renderAll();
                                    canvas.fire('object:moving', { target: obj });
                                    
                                } else if (action === 'centerToTemplateWidth') {
                                    // For horizontal centering of shapes
                                    const canvas = layer.relatedCanvasObject.canvas;
                                    const obj = layer.relatedCanvasObject;
                                    
                                    const zoom = canvas.getZoom();
                                    const objectWidth = obj.width * obj.scaleX;
                                    const templateWidth = canvas.width / zoom;
                                    const newLeft = (templateWidth - objectWidth) / 2;
                                    
                                    obj.left = newLeft;
                                    obj.setCoords();
                                    canvas.renderAll();
                                    canvas.fire('object:moving', { target: obj });
                                    
                                } else if (action === 'centerToTemplateHeight') {
                                    // For vertical centering of shapes
                                    const canvas = layer.relatedCanvasObject.canvas;
                                    const obj = layer.relatedCanvasObject;
                                    
                                    const zoom = canvas.getZoom();
                                    const objectHeight = obj.height * obj.scaleY;
                                    const templateHeight = canvas.height / zoom;
                                    const newTop = (templateHeight - objectHeight) / 2;
                                    
                                    obj.top = newTop;
                                    obj.setCoords();
                                    canvas.renderAll();
                                    canvas.fire('object:moving', { target: obj });
                                }
                            }
                            
                            // Apply to all templates if requested
                            if (functionArgs.apply_to_all) {
                                editorControlEventBroker.fire(eventType, {
                                    value: true,
                                    action: 'position',
                                    keys: ['position']
                                });
                            }
                            
                            // Create appropriate response message
                            let alignmentDescription = '';
                            switch (functionArgs.alignment) {
                                case 'horizontal':
                                    alignmentDescription = 'horizontally';
                                    break;
                                case 'vertical':
                                    alignmentDescription = 'vertically';
                                    break;
                                default:
                                    alignmentDescription = 'in the canvas';
                                    break;
                            }
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've centered the ${elementType} ${alignmentDescription}.`
                            };
                            
                            // Ensure changes are rendered
                            layer.relatedCanvasObject.canvas.renderAll();
                            
                        } catch (error) {
                            console.error(`Error in center_object:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error centering the object. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'change_layer_order') {
                        try {
                            // Find layer based on element_id or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected layer';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find a layer to change order. Please select an object first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);
                            
                            // Get current order from users_fields_order
                            let usersFieldsOrder = [];
                            if (this.selectedInstance.data.users_fields_order) {
                                try {
                                    usersFieldsOrder = JSON.parse(this.selectedInstance.data.users_fields_order);
                                } catch (e) {
                                    usersFieldsOrder = [];
                                }
                            }
                            
                            // If no order exists yet, get current order from canvas
                            if (!usersFieldsOrder.length) {
                                usersFieldsOrder = this.layersOrdered
                                    .filter(layer => layer.relatedCanvasObject)
                                    .map(layer => layer.id);
                            }
                            
                            // Find current layer's index
                            const currentIndex = usersFieldsOrder.indexOf(layer.id);
                            if (currentIndex === -1) {
                                this.pushMessage({ role: 'assistant', content: 'Sorry, I could not determine the layer order. Please try again.' });
                                this.stopLoading();
                                return;
                            }
                            
                            // Create new order array based on requested position
                            let newOrder;
                            switch (functionArgs.position) {
                                case 'back':
                                    // Remove from current position and add to end (top)
                                    newOrder = [
                                        ...usersFieldsOrder.slice(0, currentIndex),
                                        ...usersFieldsOrder.slice(currentIndex + 1),
                                        layer.id
                                    ];
                                    break;
                                    
                                case 'front':
                                    // Remove from current position and add to start (bottom)
                                    newOrder = [
                                        layer.id,
                                        ...usersFieldsOrder.slice(0, currentIndex),
                                        ...usersFieldsOrder.slice(currentIndex + 1)
                                    ];
                                    break;
                                    
                                case 'backward':
                                    if (currentIndex < usersFieldsOrder.length - 1) {
                                        // Swap with previous item
                                        newOrder = [...usersFieldsOrder];
                                        [newOrder[currentIndex], newOrder[currentIndex + 1]] = 
                                        [newOrder[currentIndex + 1], newOrder[currentIndex]];
                                    } else {
                                        newOrder = usersFieldsOrder;
                                    }
                                    break;
                                    
                                case 'forward':
                                    if (currentIndex > 0) {
                                        // Swap with next item
                                        newOrder = [...usersFieldsOrder];
                                        [newOrder[currentIndex], newOrder[currentIndex - 1]] = 
                                        [newOrder[currentIndex - 1], newOrder[currentIndex]];
                                    } else {
                                        newOrder = usersFieldsOrder;
                                    }
                                    break;
                                    
                                default:
                                    throw new Error(`Invalid position: ${functionArgs.position}`);
                            }
                            
                            // Update the layersOrdered array to match the new order
                            this.layersOrdered = _.sortBy(this.layersOrdered, layer => 
                                newOrder.findIndex(id => id === layer.id)
                            );
                            
                            // Bring each layer to front in reverse order (like LayersModule)
                            [...this.layersOrdered]
                                .reverse()
                                .filter(layer => layer.relatedCanvasObject)
                                .forEach(layer => {
                                    (layer.relatedCanvasObject.group || layer.relatedCanvasObject).bringToFront();
                                });
                            
                            // Ensure ruler guide lines stay on top
                            this.moveRulerGuideLinesToFront();
                            
                            // Save the new order
                            this.selectedInstance.canvas.fire('layers:changed', {
                                instance_id: this.selectedInstance.id,
                                key: 'users_fields_order',
                                value: newOrder
                            });
                            
                            // Update the canvas
                            this.selectedInstance.canvas.renderAll();
                            
                            // Generate descriptive message
                            const positionDescriptions = {
                                'front': 'brought to the front',
                                'back': 'sent to the back',
                                'forward': 'brought forward',
                                'backward': 'sent backward'
                            };
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've ${positionDescriptions[functionArgs.position]} the layer with ID: "${functionArgs.element_id}".`
                            };
                        } catch (error) {
                            console.error(`Error in change_layer_order:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error changing the layer order. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'align_text') {
                        try {
                            // Common text element handling logic
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected text';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find a text element to modify. Please select a text element first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }
                            
                            // Check if this is actually a text element
                            const isText = layer.relatedCanvasObject.type === 'text' || 
                                layer.relatedCanvasObject.type === 'i-text' || 
                                layer.relatedCanvasObject.text !== undefined ||
                                (layer.relatedCanvasObject.id && 
                                 layer.relatedCanvasObject.id.includes('text_'));
                                 
                            if (!isText) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element is not a text element. Please select a text element.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Function-specific operations
                            if (functionName === 'align_text') {
                                // Map the user-friendly alignment values to the correct action
                                const alignmentMap = {
                                    'left': 'leftAlignToTemplate',
                                    'center': 'centerToTemplateWidth',
                                    'right': 'rightAlignToTemplate'
                                };

                                const alignFunction = alignmentMap[functionArgs.alignment] || 'leftAlignToTemplate';

                                // Fire the text alignment event with the function name directly
                                // This will be handled by the onTextEditorChange handler in show.js
                                editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                    action: alignFunction
                                });

                                // Apply to all templates if requested
                                if (functionArgs.apply_to_all) {
                                    editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, {
                                        value: true,
                                        action: 'textAlign',
                                        keys: ['alignment']
                                    });
                                }

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've ${functionArgs.alignment}-aligned the text element with ID: "${functionArgs.element_id}" to the template.`
                                };
                            } 

                            // Common updates for all text operations
                            layer.relatedCanvasObject.canvas.renderAll();

                            // Fire object:moving for all operations to ensure changes are saved
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                        } catch (error) {
                            console.error(`Error in ${functionName}:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error modifying the text. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'edit_shape_gradient') {
                        try {
                            let layer = this.findLayerByElementIdOrSelected(functionArgs.element_id);
                            if (!layer) {
                                // Auto-find first shape
                                layer = this.layersOrdered.find(l => {
                                    const t = l.relatedCanvasObject?.type;
                                    return t && ['rect', 'circle', 'CustomRect', 'polygon', 'ellipse'].includes(t);
                                });
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = { role: 'assistant', content: 'I couldn\'t find a shape to apply the gradient to.' };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Normalize color stops: offset 0-100 → 0-1 for fabric
                            const normalizedStops = (functionArgs.color_stops || []).map(s => ({
                                offset: s.offset > 1 ? s.offset / 100 : s.offset,
                                color: s.color,
                            }));

                            const gradientValue = {
                                type: functionArgs.gradient_type || 'linear',
                                direction: functionArgs.direction || 'to right',
                                colorStops: normalizedStops,
                            };

                            // Apply gradient visually to the fabric object
                            const fabricGradient = createGradient(gradientValue);
                            if (fabricGradient) {
                                layer.relatedCanvasObject.set('fill', fabricGradient);
                            }

                            // Fire event to persist the change
                            editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                action: 'gradient',
                                value: gradientValue,
                                keys: functionArgs.apply_to_all ? ['gradient'] : [],
                            });

                            layer.relatedCanvasObject.canvas.renderAll();

                            const colors = (functionArgs.color_stops || []).map(s => s.color).join(' → ');
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've applied a ${gradientValue.type} gradient (${colors}) to the shape.`,
                            };
                        } catch (error) {
                            console.error('Error in edit_shape_gradient:', error);
                            assistantMessage = { role: 'assistant', content: 'Sorry, there was an error applying the gradient.' };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'edit_shape_color' || functionName === 'edit_shape_border') {
                        try {
                            // Common shape handling logic
                            let layer;
                            
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.id === functionArgs.element_id ||
                                            layer.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected shape';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find a shape to modify. Please select a shape element first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);

                            // Improved shape detection logic
                            const isShape = layer.relatedCanvasObject.type && (
                                ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                                (layer.relatedCanvasObject.id && (
                                    layer.relatedCanvasObject.id.includes('circle') ||
                                    layer.relatedCanvasObject.id.includes('rect') ||
                                    layer.relatedCanvasObject.id.includes('shape') ||
                                    layer.relatedCanvasObject.id.includes('polygon') ||
                                    layer.relatedCanvasObject.id.includes('triangle')
                                ))
                            );

                            if (!isShape) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element with ID: "${functionArgs.element_id}" is not a shape.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Function-specific operations
                            if (functionName === 'edit_shape_color') {
                                editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                    action: 'bgColor',
                                    value: functionArgs.new_color,
                                    keys: functionArgs.apply_to_all ? ['fill_color'] : []
                                });

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've changed the fill color of the shape with ID: "${functionArgs.element_id}" to ${functionArgs.new_color}.`
                                };
                            } else if (functionName === 'edit_shape_border') {
                                if (functionArgs.border_color) {
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        action: 'strokeColor',
                                        value: functionArgs.border_color,
                                        keys: functionArgs.apply_to_all ? ['stroke_color'] : []
                                    });
                                }

                                if (functionArgs.border_width !== undefined) {
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        action: 'thickness',
                                        value: functionArgs.border_width,
                                        keys: functionArgs.apply_to_all ? ['thickness'] : []
                                    });
                                }

                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I've updated the border of the shape with ID: "${functionArgs.element_id}"${functionArgs.border_color ? ' with color: ' + functionArgs.border_color : ''}${functionArgs.border_width !== undefined ? ' and width: ' + functionArgs.border_width + 'px' : ''}.`
                                };
                            } 
                            // Common updates for all shape operations
                            layer.relatedCanvasObject.canvas.renderAll();
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                        } catch (error) {
                            console.error(`Error in ${functionName}:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error modifying the shape. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'resize_element') {
                        try {
                            // Find layer based on element_id or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                         layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ? 
                                    this.selectedInstance.canvas.getActiveObject() : null;
                                
                                const selectedElement = activeObject || this.targetElement || this.target;
                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer => 
                                        layer.relatedCanvasObject === selectedElement || 
                                        (layer.relatedCanvasObject && 
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                             layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );
                                    
                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected element';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an element to resize. Please select an element first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            const obj = layer.relatedCanvasObject;
                            
                            // Check if this is a valid resizable object (image or shape)
                            const isImage = obj.type === 'image' || (obj.id && obj.id.includes('image_'));
                            const isShape = obj.type === 'rect' || obj.type === 'circle' || obj.type === 'CustomRect' ||
                                (obj.id && (
                                    obj.id.includes('circle') ||
                                    obj.id.includes('rect') ||
                                    obj.id.includes('shape')
                                ));
                                
                            if (!isImage && !isShape) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `The selected element cannot be resized. Only images and shapes can be resized with this function.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(obj);
                            
                            // Get original dimensions
                            const originalWidth = obj.width * obj.scaleX;
                            const originalHeight = obj.height * obj.scaleY;
                            
                            // Determine scale factor to use
                            let scaleFactor;
                            
                            if (functionArgs.scale !== undefined) {
                                // Use provided scale factor
                                scaleFactor = functionArgs.scale;
                            } else if (functionArgs.scale_direction) {
                                // Use scale direction with default 10% change
                                const defaultScaleChange = 0.1; // 10%
                                if (functionArgs.scale_direction === 'up') {
                                    scaleFactor = 1 + defaultScaleChange; // 110%
                                } else { // 'down'
                                    scaleFactor = 1 - defaultScaleChange; // 90%
                                }
                            } else {
                                // No scale specified, use default increase of 10%
                                scaleFactor = 1.1;
                            }
                            
                            // Calculate new dimensions
                            const newWidth = originalWidth * scaleFactor;
                            const newHeight = originalHeight * scaleFactor;
                            
                            // Handle different object types
                            const isCircle = obj.type === 'circle' || 
                                (obj.id && obj.id.includes('circle')) || 
                                (obj.element_id && obj.element_id.includes('circle'));
                                
                            const isCustomRect = obj.type === 'CustomRect' || 
                                (obj.id && obj.id.includes('rect'));
                                
                            if (isCircle) {
                                // For circle objects, adjust the radius instead of width/height
                                const currentRadius = obj.radius || (obj.width / 2);
                                const newRadius = currentRadius * scaleFactor;
                                
                                obj.set({
                                    radius: newRadius,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                            } else if (isCustomRect || (obj.scaleX === 1 && obj.scaleY === 1)) {
                                // Handle CustomRect or simple resize for non-scaled objects
                                obj.set({
                                    width: newWidth,
                                    height: newHeight,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                            } else {
                                // Set scale values based on the desired dimensions
                                const newScaleX = newWidth / obj.width;
                                const newScaleY = newHeight / obj.height;
                                
                                obj.set({
                                    scaleX: newScaleX,
                                    scaleY: newScaleY
                                });
                            }
                            
                            // Update coordinates
                            obj.setCoords();
                            
                            // Validate the resizing operation
                            if (!isCircle && (obj.width <= 0 || obj.height <= 0)) {
                                console.warn('Resize resulted in invalid dimensions, reverting to original size');
                                obj.set({
                                    width: originalWidth,
                                    height: originalHeight,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                                obj.setCoords();
                            } else if (isCircle && obj.radius <= 0) {
                                console.warn('Resize resulted in invalid radius, reverting to original size');
                                obj.set({
                                    radius: originalWidth / 2,
                                    scaleX: 1,
                                    scaleY: 1
                                });
                                obj.setCoords();
                            }
                            
                            // Apply to all templates if requested
                            if (functionArgs.apply_to_all) {
                                if (isImage) {
                                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                                        value: true,
                                        action: 'scale',
                                        keys: ['scale']
                                    });
                                } else if (isShape) {
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        value: true,
                                        action: 'width',
                                        keys: ['width']
                                    });
                                    editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                        value: true,
                                        action: 'height',
                                        keys: ['height']
                                    });
                                }
                            }
                            
                            // Update the canvas
                            obj.canvas.renderAll();
                            
                            // Fire appropriate events
                            obj.canvas.fire('object:modified', { target: obj });
                            obj.canvas.fire('object:moving', { target: obj });
                            
                            // Create appropriate response message
                            let resizeMethod = '';
                            if (functionArgs.scale !== undefined) {
                                resizeMethod = `scaled to ${Math.round(functionArgs.scale * 100)}%`;
                            } else if (functionArgs.scale_direction) {
                                if (functionArgs.scale_direction === 'up') {
                                    resizeMethod = 'increased by 10%';
                                } else {
                                    resizeMethod = 'decreased by 10%';
                                }
                            } else {
                                resizeMethod = 'increased by 10%';
                            }
                            
                            // Determine element type for response message
                            let elementType;
                            if (isCircle) {
                                elementType = 'circle';
                            } else if (isImage) {
                                elementType = 'image';
                            } else if (obj.type === 'rect' || obj.type === 'CustomRect' || 
                                (obj.id && obj.id.includes('rect'))) {
                                elementType = 'rectangle';
                            } else {
                                elementType = 'shape';
                            }
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've ${resizeMethod} the ${elementType} with ID: "${functionArgs.element_id}".`
                            };
                            
                        } catch (error) {
                            console.error(`Error in resize_element:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error resizing the element. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'move_object') {
                        try {
                            // Find the target element by ID or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                                    this.selectedInstance.canvas.getActiveObject() : null;

                                const selectedElement = activeObject || this.targetElement || this.target;

                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer =>
                                        layer.relatedCanvasObject === selectedElement ||
                                        (layer.relatedCanvasObject &&
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                                layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );

                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected object';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an object to move. Please select an object first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);

                            // Determine the type of object we're working with
                            const isImage = layer.relatedCanvasObject.type === 'image' ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('image'));

                            const isText = layer.relatedCanvasObject.type === 'text' ||
                                layer.relatedCanvasObject.type === 'i-text' ||
                                layer.relatedCanvasObject.text !== undefined ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('text_'));

                            const isShape = layer.relatedCanvasObject.type && (
                                ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                                (layer.relatedCanvasObject.id && (
                                    layer.relatedCanvasObject.id.includes('circle') ||
                                    layer.relatedCanvasObject.id.includes('rect') ||
                                    layer.relatedCanvasObject.id.includes('shape') ||
                                    layer.relatedCanvasObject.id.includes('polygon') ||
                                    layer.relatedCanvasObject.id.includes('triangle')
                                ))
                            );

                            // Common movement calculations
                            const direction = functionArgs.direction;
                            const distance = functionArgs.distance || 10; // Default to 10px if not specified

                            let x = 0;
                            let y = 0;

                            // Set movement coordinates based on direction
                            switch (direction) {
                                case 'up': y = -distance; break;
                                case 'down': y = distance; break;
                                case 'left': x = -distance; break;
                                case 'right': x = distance; break;
                            }

                            // Get current position
                            const currentPosition = layer.relatedCanvasObject.getPointByOrigin('left', 'top');
                            
                            // Special handling for product images (image_X)
                            const isProductImage = isImage && layer.relatedCanvasObject.id && 
                                (layer.relatedCanvasObject.id.includes('image_') || 
                                 layer.relatedCanvasObject.id.includes('product_image'));
                                
                            // Set new position
                            layer.relatedCanvasObject.setPositionByOrigin(
                                { x: currentPosition.x + x, y: currentPosition.y + y },
                                'left',
                                'top'
                            );

                            // Update coordinates
                            layer.relatedCanvasObject.setCoords();

                            // Apply to all templates if requested (only for text and shape)
                            if ((isText || isShape) && functionArgs.apply_to_all) {
                                const eventName = isText ? EVENTS.TEXT_CHANGED : EVENTS.SHAPE_CHANGED;
                                const xKey = isText ? 'x' : 'offset_x';
                                const yKey = isText ? 'y' : 'offset_y';

                                if (x !== 0) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'x',
                                        keys: [xKey]
                                    });
                                }

                                if (y !== 0) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'y',
                                        keys: [yKey]
                                    });
                                }
                            }

                            // Common updates for all object movements
                            layer.relatedCanvasObject.canvas.renderAll();
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                            // Generate appropriate message based on object type
                            const objectType = isImage ? 'image' : (isText ? 'text' : (isShape ? 'shape' : 'object'));
                            assistantMessage = {
                                role: 'assistant',
                                content: `I've moved the ${objectType} ${direction} by ${distance}px. This was a relative movement, not setting absolute coordinates.`
                            };

                        } catch (error) {
                            console.error(`Error in move_object:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error moving the object. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === 'position_object') {
                        try {
                            // Find the target element by ID or use currently selected object
                            let layer;
                            if (functionArgs.element_id) {
                                layer = this.layersOrdered.find(layer =>
                                    layer.element_id === functionArgs.element_id ||
                                    layer.id === functionArgs.element_id ||
                                    (layer.relatedCanvasObject &&
                                        (layer.relatedCanvasObject.element_id === functionArgs.element_id ||
                                            layer.relatedCanvasObject.id === functionArgs.element_id))
                                );
                            } else {
                                // Use currently selected object
                                const activeObject = this.selectedInstance && this.selectedInstance.canvas ?
                                    this.selectedInstance.canvas.getActiveObject() : null;

                                const selectedElement = activeObject || this.targetElement || this.target;

                                if (selectedElement) {
                                    layer = this.layersOrdered.find(layer =>
                                        layer.relatedCanvasObject === selectedElement ||
                                        (layer.relatedCanvasObject &&
                                            (layer.relatedCanvasObject.id === selectedElement.id ||
                                                layer.relatedCanvasObject.element_id === selectedElement.element_id))
                                    );

                                    if (layer) {
                                        functionArgs.element_id = layer.element_id || layer.id || 'selected object';
                                    }
                                }
                            }

                            if (!layer || !layer.relatedCanvasObject) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an object to position. Please select an object first.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }

                            // Set the active object
                            this.setActiveObject(layer.relatedCanvasObject);

                            // Determine the type of object we're working with
                            const isImage = layer.relatedCanvasObject.type === 'image' ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('image'));

                            const isText = layer.relatedCanvasObject.type === 'text' ||
                                layer.relatedCanvasObject.type === 'i-text' ||
                                layer.relatedCanvasObject.text !== undefined ||
                                (layer.relatedCanvasObject.id &&
                                    layer.relatedCanvasObject.id.includes('text_'));

                            const isShape = layer.relatedCanvasObject.type && (
                                ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                                (layer.relatedCanvasObject.id && (
                                    layer.relatedCanvasObject.id.includes('circle') ||
                                    layer.relatedCanvasObject.id.includes('rect') ||
                                    layer.relatedCanvasObject.id.includes('shape') ||
                                    layer.relatedCanvasObject.id.includes('polygon') ||
                                    layer.relatedCanvasObject.id.includes('triangle')
                                ))
                            );

                            // Get X and Y coordinates or use existing ones
                            const newX = typeof functionArgs.x === 'number' ? functionArgs.x : null;
                            const newY = typeof functionArgs.y === 'number' ? functionArgs.y : null;
                            
                            // Check if at least one coordinate is provided
                            if (newX === null && newY === null) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I need either an X coordinate, Y coordinate, or both to position the object. Please specify at least one.`
                                };
                                this.pushMessage(assistantMessage);
                                this.stopLoading();
                                return;
                            }
                            
                            // Get current position
                            const currentPosition = layer.relatedCanvasObject.getPointByOrigin('left', 'top');
                            
                            // Store old position for reporting change
                            const oldX = Math.round(currentPosition.x);
                            const oldY = Math.round(currentPosition.y);
                            
                            // Special handling for product images (image_X)
                            const isProductImage = isImage && layer.relatedCanvasObject.id && 
                                (layer.relatedCanvasObject.id.includes('image_') || 
                                 layer.relatedCanvasObject.id.includes('product_image'));
                                
                            // Set new position
                            layer.relatedCanvasObject.setPositionByOrigin(
                                { 
                                    x: newX !== null ? newX : currentPosition.x, 
                                    y: newY !== null ? newY : currentPosition.y 
                                },
                                'left',
                                'top'
                            );

                            // Update coordinates
                            layer.relatedCanvasObject.setCoords();

                            // Apply to all templates if requested (only for text and shape)
                            if ((isText || isShape) && functionArgs.apply_to_all) {
                                const eventName = isText ? EVENTS.TEXT_CHANGED : EVENTS.SHAPE_CHANGED;
                                const xKey = isText ? 'x' : 'offset_x';
                                const yKey = isText ? 'y' : 'offset_y';

                                if (newX !== null) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'x',
                                        keys: [xKey]
                                    });
                                }

                                if (newY !== null) {
                                    editorControlEventBroker.fire(eventName, {
                                        value: true,
                                        action: 'y',
                                        keys: [yKey]
                                    });
                                }
                            }

                            // Common updates for all object positioning
                            layer.relatedCanvasObject.canvas.renderAll();
                            layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });

                            // Generate appropriate message based on object type and what was changed
                            const objectType = isImage ? 'image' : (isText ? 'text' : (isShape ? 'shape' : 'object'));
                            
                            let positionMessage = '';
                            const elementName = layer.element_id || layer.id || 'selected element';
                            
                            if (newX !== null && newY !== null) {
                                positionMessage = `I've set the exact coordinates of the ${objectType} to X: ${newX}px, Y: ${newY}px (changed from X: ${oldX}px, Y: ${oldY}px).`;
                            } else if (newX !== null) {
                                positionMessage = `I've set the exact X coordinate of the ${objectType} to ${newX}px (changed from ${oldX}px). The Y coordinate remains at ${oldY}px.`;
                            } else if (newY !== null) {
                                positionMessage = `I've set the exact Y coordinate of the ${objectType} to ${newY}px (changed from ${oldY}px). The X coordinate remains at ${oldX}px.`;
                            }
                            
                            assistantMessage = {
                                role: 'assistant',
                                content: positionMessage
                            };

                        } catch (error) {
                            console.error(`Error in position_object:`, error);
                            assistantMessage = {
                                role: 'assistant',
                                content: `Sorry, there was an error positioning the object. Please try again.`
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === "create_template") {
                        try {
                            this.startLoading();
                            const sizes = functionArgs.sizes || [];
                            if (!sizes.length) {
                                this.pushMessage({ role: 'assistant', content: 'No sizes were specified for the new template.' });
                                this.stopLoading();
                            } else {

                            const grid = $("#template-group-preview");
                            let customerId = grid.attr("data-customer-id");
                            const layoutId = grid.attr("data-layout-id");
                            if (parseInt(customerId) === 0) {
                                customerId = this.selectedInstance?.template?.customer_id
                                    || (window.location.pathname.match(/\/banner\/(\d+)\//) || [])[1]
                                    || customerId;
                            }

                            // Generate a blank white image and upload it as placeholder
                            const blankCanvas = document.createElement('canvas');
                            blankCanvas.width = sizes[0].width;
                            blankCanvas.height = sizes[0].height;
                            const ctx = blankCanvas.getContext('2d');
                            ctx.fillStyle = '#ffffff';
                            ctx.fillRect(0, 0, blankCanvas.width, blankCanvas.height);
                            const blankBlob = await new Promise(resolve => blankCanvas.toBlob(resolve, 'image/png'));
                            const uploadFd = new FormData();
                            uploadFd.append('img_url', new File([blankBlob], 'blank.png', { type: 'image/png' }));
                            uploadFd.append('return_blob', 1);
                            const instanceId = this.selectedInstance.id;
                            const uploadRes = await axios.post(
                                `/banner/${customerId}/group/${layoutId}/template/${instanceId}/upload_generated_background`,
                                uploadFd,
                                { headers: { 'Content-Type': 'multipart/form-data' } }
                            );
                            const blankImagePath = uploadRes.data;

                            for (const size of sizes) {
                                this.pushMessage({ role: 'assistant', content: `Creating a new ${size.width}x${size.height} template...` });
                                this.saveMessages();
                                await axios.post(`/banner/${customerId}/group/create-template-with-size`, {
                                    customer_id: customerId,
                                    layout_id: layoutId,
                                    width: size.width,
                                    height: size.height,
                                    image_path: blankImagePath,
                                    product_image_urls: [],
                                    scale_mode: 'fit',
                                });
                            }

                            this.pushMessage({ role: 'assistant', content: `Template(s) created! Reloading page...` });
                            this.saveMessages();
                            this.stopLoading();
                            localStorage.setItem('aiChatResumeAfterReload', 'true');
                            setTimeout(() => location.reload(), 500);
                            }
                        } catch (error) {
                            console.error('Error in create_template:', error);
                            assistantMessage = { role: 'assistant', content: 'Sorry, there was an error creating the template.' };
                            this.stopLoading();
                        }
                    } else if (functionName === "duplicate_layout") {
                        try {
                            this.startLoading();

                            const grid = $("#template-group-preview");
                            const customerId = grid.attr("data-customer-id");
                            const layoutId = grid.attr("data-layout-id");

                            if (!customerId || !layoutId) {
                                throw new Error('Could not identify the current layout');
                            }

                            const response = await axios.get(
                                `/banner/${customerId}/group/${layoutId}/copy`,
                                { params: { api_request: 1 } },
                            );
                            const newLayoutId = response.data?.new_layout_id;

                            if (!newLayoutId) {
                                throw new Error('Layout copy did not return a new layout ID');
                            }

                            this.pushMessage({
                                role: 'assistant',
                                content: 'Layout duplicated successfully! Opening copied layout...',
                            });
                            this.saveMessages();
                            this.stopLoading();
                            localStorage.setItem('aiChatResumeAfterReload', 'true');
                            window.location.href = `/banner/${customerId}/group/${newLayoutId}/show`;
                        } catch (error) {
                            console.error('Error in duplicate_layout:', error);
                            assistantMessage = {
                                role: 'assistant',
                                content: 'Sorry, there was an error duplicating the layout.',
                            };
                            this.stopLoading();
                        }
                    } else if (functionName === "duplicate_template") {
                        try {
                            this.startLoading();

                            // Switch to target template if specified
                            if (functionArgs.template_index) {
                                const targetPreview = (window.previews || [])[functionArgs.template_index - 1];
                                if (targetPreview) {
                                    setSelectedInstance(targetPreview.id);
                                    this.reorderLayers(targetPreview.id);
                                }
                            }

                            const instanceId = this.selectedInstance.id;
                            const grid = $("#template-group-preview");
                            let customerId = grid.attr("data-customer-id");
                            const layoutId = grid.attr("data-layout-id");
                            const templateId = grid.attr("data-template-id");

                            if (parseInt(customerId) === 0) {
                                customerId = this.selectedInstance?.template?.customer_id
                                    || (window.location.pathname.match(/\/banner\/(\d+)\//) || [])[1]
                                    || customerId;
                            }

                            const url = `/banner/${customerId}/group/${layoutId}/template/${instanceId}/${templateId}/duplicate?old-customer=0`;
                            const response = await axios.post(location.origin + url);

                            this.pushMessage({
                                role: 'assistant',
                                content: 'Template duplicated successfully! Reloading page...',
                            });
                            this.saveMessages();
                            this.stopLoading();
                            localStorage.setItem('aiChatResumeAfterReload', 'true');

                            if (response.data && response.data.redirect_url) {
                                window.location.href = response.data.redirect_url;
                            } else {
                                location.reload();
                            }
                        } catch (error) {
                            console.error('Error in duplicate_template:', error);
                            assistantMessage = {
                                role: 'assistant',
                                content: 'Sorry, there was an error duplicating the template.',
                            };
                            this.stopLoading();
                        }
                    } else if (functionName === "add_template_from_design_gallery") {
                        await this.addTemplateFromDesignGallery(functionArgs.template_query);
                        return;
                    } else if (functionName === "resize_banner") {
                        const instanceId = this.selectedInstance.id;
                        const grid = $("#template-group-preview");
                        const customer_id = grid.attr("data-customer-id");
                        const layout_id = grid.attr("data-layout-id");
                        try {
                            this.startLoading();
                            const { width, height } = functionArgs;
                            const canvasElement = this.selectedInstance.canvas.getElement();
                            canvasElement.toBlob(async (blob) => {
                                try {
                                    this.reorderLayers(instanceId);
                                    const formData = new FormData();
                                    formData.append("width", width);
                                    formData.append("height", height);
                                    formData.append("thumbnail", blob, "thumbnail.png");
                                    formData.append("layersOrdered", JSON.stringify(this.layersOrdered));

                                    this.startLoading();
                                    const response = await axios.post(
                                        `/banner/${customer_id}/group/${layout_id}/template/${instanceId}/ai_resize`,
                                        formData,
                                        {
                                            headers: {
                                                "Content-Type": "multipart/form-data",
                                            }
                                        }
                                    );
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `The selected banner will be resized to ${width}px width and ${height}px height.`,
                                        newWidth: width,
                                        newHeight: height,
                                        bannerResizeSettings: response.data.updated_layer_positions
                                    };
                                    this.pushMessage(assistantMessage);
                                    this.stopLoading();
                                } catch (error) {
                                    console.error(`Error in resize_banner:`, error);
                                    assistantMessage = {
                                        role: "assistant",
                                        content: `Sorry, there was an error resizing the banner. Please try again.`,
                                    };
                                    this.pushMessage(assistantMessage);
                                    this.stopLoading();
                                }
                            }, "image/png");
                        } catch (error) {
                            console.error(`Error in resize_banner:`, error);
                            assistantMessage = {
                                role: "assistant",
                                content: `Sorry, there was an error resizing the banner. Please try again.`,
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === "switch_template") {
                        try {
                            const targetIndex = functionArgs.template_index;
                            const targetInstanceId = functionArgs.instance_id;
                            const previews = window.previews || [];

                            let targetPreview;
                            if (targetIndex && targetIndex > 0 && targetIndex <= previews.length) {
                                targetPreview = previews[targetIndex - 1];
                            } else if (targetInstanceId) {
                                targetPreview = previews.find(p => p.id === targetInstanceId);
                            }

                            if (!targetPreview) {
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find that template. There are ${previews.length} template(s) in this layout.`,
                                };
                            } else {
                                setSelectedInstance(targetPreview.id);
                                this.reorderLayers(targetPreview.id);
                                const templateNum = targetIndex || (previews.indexOf(targetPreview) + 1);
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `Switched to template #${templateNum}: "${targetPreview.template?.name || 'Untitled'}".`,
                                };
                            }
                        } catch (error) {
                            console.error('Error in switch_template:', error);
                            assistantMessage = {
                                role: 'assistant',
                                content: 'Sorry, there was an error switching templates.',
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === "find_element_by_content") {
                        try {
                            const searchText = (functionArgs.text_content || '').toLowerCase().trim();
                            const searchWords = searchText.split(/\s+/);

                            // Try multiple matching strategies
                            let matchingLayer = null;

                            // 1. Exact substring match in text
                            matchingLayer = this.layersOrdered.find(layer => {
                                const text = layer.relatedCanvasObject?.text;
                                return text && text.toLowerCase().includes(searchText);
                            });

                            // 2. Match all words individually (handles "64 oz" split as "64" + "oz")
                            if (!matchingLayer && searchWords.length > 1) {
                                matchingLayer = this.layersOrdered.find(layer => {
                                    const text = (layer.relatedCanvasObject?.text || '').toLowerCase();
                                    return searchWords.every(word => text.includes(word));
                                });
                            }

                            // 3. Match the most significant word (longest or numeric)
                            if (!matchingLayer) {
                                const significantWord = searchWords.reduce((a, b) => a.length >= b.length ? a : b, '');
                                matchingLayer = this.layersOrdered.find(layer => {
                                    const text = (layer.relatedCanvasObject?.text || '').toLowerCase();
                                    return text.includes(significantWord);
                                });
                            }

                            // 4. Match in element name or note
                            if (!matchingLayer) {
                                matchingLayer = this.layersOrdered.find(layer => {
                                    const name = (layer.name || '').toLowerCase();
                                    const note = (layer.note || '').toLowerCase();
                                    return name.includes(searchText) || note.includes(searchText);
                                });
                            }

                            if (matchingLayer) {
                                this.setActiveObject(matchingLayer.relatedCanvasObject);
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `Found and selected the element with text "${matchingLayer.relatedCanvasObject?.text || matchingLayer.name}" (ID: "${matchingLayer.element_id || matchingLayer.id}").`,
                                };
                            } else {
                                // List available text elements to help the AI
                                const textElements = this.layersOrdered
                                    .filter(l => l.relatedCanvasObject?.text)
                                    .map(l => `"${l.relatedCanvasObject.text}" (${l.element_id || l.id})`)
                                    .slice(0, 5);
                                assistantMessage = {
                                    role: 'assistant',
                                    content: `I couldn't find an element containing "${functionArgs.text_content}". Available text elements: ${textElements.join(', ') || 'none found'}.`,
                                };
                            }
                        } catch (error) {
                            console.error('Error in find_element_by_content:', error);
                            assistantMessage = {
                                role: 'assistant',
                                content: 'Sorry, there was an error searching for the element.',
                            };
                        } finally {
                            this.stopLoading();
                        }
                    } else if (functionName === "generate_image_inline") {
                        try {
                            this.startLoading();
                            this.pushMessage({
                                role: 'assistant',
                                content: 'Generating image, please wait...',
                            });
                            this.saveMessages();

                            const grid = $("#template-group-preview");
                            const layout_id = grid.attr("data-layout-id");
                            const formData = new FormData();

                            formData.append('layout_id', layout_id);
                            formData.append('layout_template_id', this.selectedInstance.id);
                            formData.append('function', functionArgs.model || 'gemini_nano_banana_2_image_generate');
                            formData.append('prompt', functionArgs.prompt);
                            formData.append('number_of_images', functionArgs.number_of_images || 1);

                            // Collect source images from template elements
                            if (functionArgs.source_element_ids && functionArgs.source_element_ids.length) {
                                for (const elemId of functionArgs.source_element_ids) {
                                    const layer = this.findLayerByElementIdOrSelected(elemId);
                                    if (layer && layer.relatedCanvasObject && layer.relatedCanvasObject.getSrc) {
                                        formData.append('product_image_urls[]', layer.relatedCanvasObject.getSrc());
                                    }
                                }
                            } else {
                                // Auto-include template images as source
                                this.layersOrdered.forEach(layer => {
                                    if (layer.relatedCanvasObject && layer.relatedCanvasObject.type === 'image' && layer.relatedCanvasObject.getSrc) {
                                        const src = layer.relatedCanvasObject.getSrc();
                                        if (src) {
                                            formData.append('product_image_urls[]', src);
                                        }
                                    }
                                });
                            }

                            formData.append('source', 'creative_studio_editor');

                            const genRes = await axios.post('/banner/groups/generate_image', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' },
                            });

                            if (!genRes.data || !genRes.data.uuid) {
                                throw new Error('Image generation failed to start');
                            }

                            // Poll for completion
                            const uuid = genRes.data.uuid;
                            let result;
                            let attempts = 0;
                            const maxAttempts = 60;

                            while (attempts < maxAttempts) {
                                if (this.processCancelled) return;
                                await new Promise(r => setTimeout(r, 2000));
                                if (this.processCancelled) return;
                                result = await axios.get(`/banner/groups/check_image_generation_job/${uuid}`);
                                if (result.data && result.data.done) break;
                                attempts++;
                            }

                            if (!result || !result.data || !result.data.done || !result.data.image_urls || !result.data.image_urls.length) {
                                throw new Error('Image generation timed out or returned no results');
                            }

                            const generatedImageUrl = window.location.origin + result.data.image_urls[0];

                            this.pushMessage({
                                role: 'assistant',
                                content: 'Image generated successfully! What would you like to do with it?',
                                imageUrl: generatedImageUrl,
                                widgets: [
                                    {
                                        type: 'radio',
                                        id: 'gen-image-action',
                                        options: [
                                            { value: 'replace', label: 'Replace selected image' },
                                            { value: 'add_background', label: 'Add as background' },
                                            { value: 'new_template', label: 'Add to new template' },
                                        ],
                                    },
                                    {
                                        type: 'dropdown',
                                        id: 'template-size',
                                        label: 'Template size',
                                        dependsOn: { widgetId: 'gen-image-action', value: 'new_template' },
                                        placeholder: 'Select size...',
                                        options: [
                                            { value: 'original', label: 'Original size' },
                                            { value: '1500x1500', label: '1500 x 1500' },
                                            { value: '2000x2000', label: '2000 x 2000' },
                                            { value: '2500x2500', label: '2500 x 2500' },
                                            { value: '3000x3000', label: '3000 x 3000' },
                                        ],
                                    },
                                    {
                                        type: 'action_buttons',
                                        id: 'gen-image-confirm',
                                        buttons: [
                                            { label: 'Apply', variant: 'primary', action: 'apply_generated_image' },
                                            { label: 'Discard', variant: 'outline-secondary', action: 'discard_image' },
                                        ],
                                    },
                                ],
                                _imageActionContext: {
                                    imageUrls: result.data.image_urls.map(u => window.location.origin + u),
                                    newImageUrl: generatedImageUrl,
                                },
                            });
                            this.stopLoading();
                        } catch (e) {
                            console.error('Error in generate_image_inline:', e);
                            this.pushMessage({
                                role: 'assistant',
                                content: 'Sorry, image generation failed. Please try again or use the Generate Image button.',
                            });
                            this.stopLoading();
                        }
                    } else if (functionName === "translate_image") {
                        // Check if language needs clarification (has variants)
                        const languageVariants = {
                            'french': ['French-CA', 'French-FR'],
                            'spanish': ['Spanish (LatAm)', 'Spanish (Spain/EU)'],
                            'chinese': ['Simplified Chinese', 'Traditional Chinese'],
                        };
                        const langLower = (functionArgs.target_language || '').toLowerCase();
                        const matchedKey = Object.keys(languageVariants).find(key => langLower === key || (langLower.includes(key) && !languageVariants[key].some(v => v.toLowerCase() === langLower)));

                        if (matchedKey) {
                            const variants = languageVariants[matchedKey];
                            this.pushMessage({
                                role: 'assistant',
                                content: `There are multiple variants for "${functionArgs.target_language}". Which one would you like?`,
                                widgets: [
                                    {
                                        type: 'radio',
                                        id: 'language-variant',
                                        options: variants.map(v => ({ value: v, label: v })),
                                    },
                                    {
                                        type: 'action_buttons',
                                        id: 'language-confirm',
                                        buttons: [
                                            { label: 'Translate', variant: 'primary', action: 'confirm_language_variant' },
                                        ],
                                    },
                                ],
                                _imageActionContext: {
                                    originalArgs: functionArgs,
                                },
                            });
                            this.stopLoading();
                            return;
                        }

                        try {
                            this.startLoading();

                            // 1. Resolve image element
                            const layer = this.findLayerByElementIdOrSelected(functionArgs.element_id);
                            if (!layer || !layer.relatedCanvasObject) {
                                this.pushMessage({
                                    role: 'assistant',
                                    content: 'I couldn\'t find an image element. Please select an image first.',
                                });
                                this.stopLoading();
                                return;
                            }

                            this.setActiveObject(layer.relatedCanvasObject);
                            const elementId = layer.element_id || layer.id || 'selected image';

                            let imageUrl = layer.relatedCanvasObject.getSrc ? layer.relatedCanvasObject.getSrc() : null;
                            if (!imageUrl) {
                                this.pushMessage({
                                    role: 'assistant',
                                    content: 'I couldn\'t get the source image. Please try selecting the image again.',
                                });
                                this.stopLoading();
                                return;
                            }

                            this.pushMessage({
                                role: 'assistant',
                                content: `Translating image to ${functionArgs.target_language}... This may take a moment.`,
                            });
                            this.saveMessages();

                            // 2. Fetch image and get dimensions
                            const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl;
                            const imageResponse = await fetch(fullImageUrl);
                            const imageBlob = await imageResponse.blob();
                            const { width, height } = await this.getImageDimensions(imageBlob);

                            // 3. Detect text blocks via GPT-4o vision
                            const base64Image = await new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result);
                                reader.onerror = reject;
                                reader.readAsDataURL(imageBlob);
                            });

                            if (this.processCancelled) return;

                            const detectResponse = await this.callOpenAI({
                                model: "gpt-4o",
                                messages: [{
                                    role: "user",
                                    content: [
                                        {
                                            type: "text",
                                            text: "Extract all visible text blocks from this image. Return each text block as a separate string. Include headlines, body text, product names, sizes, prices, claims, and any other visible text. Return them in the order they appear."
                                        },
                                        {
                                            type: "image_url",
                                            image_url: { url: base64Image }
                                        }
                                    ]
                                }],
                                response_format: {
                                    name: "extracted_blocks",
                                    schema: {
                                        type: "object",
                                        properties: {
                                            blocks: {
                                                type: "array",
                                                items: { type: "string" }
                                            }
                                        },
                                        required: ["blocks"],
                                        additionalProperties: false
                                    },
                                    strict: true
                                }
                            });

                            if (this.processCancelled) return;

                            let detectedBlocks = [];
                            try {
                                detectedBlocks = JSON.parse(detectResponse.content).blocks.filter(b => b && b.length > 1);
                            } catch (e) {
                                detectedBlocks = [];
                            }

                            const blocksToTranslate = (functionArgs.text_blocks_to_translate && functionArgs.text_blocks_to_translate.length)
                                ? functionArgs.text_blocks_to_translate
                                : detectedBlocks;

                            // 4. Build translation prompt
                            const language = functionArgs.target_language;
                            let translationPrompt;
                            if (!functionArgs.text_blocks_to_translate || !functionArgs.text_blocks_to_translate.length) {
                                translationPrompt = `Edit this image and replace all the text with its translation to ${language}. Translate all the text including logos, brand names, product names, etc. Do not translate symbols like '+', '-', '*', '/', '№', '°', etc. Do not change the color of the font and do not make any other edits or changes.`;
                            } else {
                                const quotedBlocks = blocksToTranslate.map(b => `"${b}"`).join(', ');
                                translationPrompt = `Edit this image and replace only the following text blocks with their translation to ${language}: ${quotedBlocks}. Do NOT translate any other text not listed here. Do not change the color of the font and do not make any other edits or changes.`;
                            }

                            // 5. Call /ai-edit-image with translation prompt
                            const formData = new FormData();
                            formData.append('image', new File([imageBlob], 'input.png', { type: imageBlob.type }));
                            formData.append('infillMethod', 'gemini_nano_banana_pro');
                            formData.append('overwritePrompt', translationPrompt);
                            formData.append('width', width);
                            formData.append('height', height);

                            // Use AbortController so stop button cancels this request too
                            this.activeAbortController = new AbortController();
                            const translateRes = await axios.post('/ai-edit-image', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' },
                                signal: this.activeAbortController.signal,
                            });
                            this.activeAbortController = null;

                            if (this.processCancelled) return;

                            const translatedImageUrl = window.location.origin + translateRes.data.url;

                            // 6. Display result with widgets
                            this.pushMessage({
                                role: 'assistant',
                                content: `Translation to ${language} complete:`,
                                imageUrl: translatedImageUrl,
                            });

                            // Auto-apply if specified, otherwise show buttons
                            if (functionArgs.auto_apply === 'replace') {
                                this.applyImageToCurrentElement(translatedImageUrl);
                                this.pushMessage({ role: 'assistant', content: 'The translated image has been applied.' });
                            } else if (functionArgs.auto_apply === 'new_template') {
                                this.addImageToNewTemplate(translatedImageUrl, 'original');
                            } else {
                                // Show action buttons
                                this.pushMessage({
                                    role: 'assistant',
                                    content: 'What would you like to do with the translated image?',
                                    widgets: [
                                        {
                                            type: 'radio',
                                            id: 'translate-action',
                                            options: [
                                                { value: 'new_template', label: 'Add to new template' },
                                                { value: 'replace', label: 'Apply to template' },
                                            ],
                                        },
                                        {
                                            type: 'action_buttons',
                                            id: 'translate-confirm',
                                            buttons: [
                                                { label: 'Apply', variant: 'primary', action: 'apply_translate_choice' },
                                            ],
                                        },
                                    ],
                                    _imageActionContext: {
                                        newImageUrl: translatedImageUrl,
                                        elementId: elementId,
                                        language: language,
                                    },
                                });
                            }
                            this.stopLoading();
                        } catch (e) {
                            if (this.processCancelled || e?.code === 'ERR_CANCELED') return;
                            console.error('Error in translate_image:', e);
                            this.pushMessage({
                                role: 'assistant',
                                content: 'Sorry, image translation failed. Please try again or use the Translate Image button.',
                            });
                            this.stopLoading();
                        }
                    } else if (functionName === "add_uploaded_images") {
                        this.addUploadedImageFlow(functionArgs.template_index, functionArgs.instance_id, functionArgs.attachment_indexes);
                    } else if (functionName === "analyze_image_order_template" || functionName === "analyze_image_order_layout") {
                        try {
                            this.startLoading();
                            this.pushMessage({ role: 'assistant', content: 'Analyzing image order for storytelling...' });
                            this.saveMessages();

                            const isTemplate = functionName === "analyze_image_order_template";

                            const previewsToScan = isTemplate
                                ? (window.previews || []).filter(p => p.id === this.selectedInstance?.id)
                                : (window.previews || []);

                            const imageItems = [];
                            for (const [idx, preview] of previewsToScan.entries()) {
                                const imgObjs = preview.canvas.getObjects().filter(obj => obj.type === 'image' && obj.getSrc);

                                for (const imgObj of imgObjs) {
                                    const img = imgObj._element;

                                    if (!img || !img.naturalWidth || !img.naturalHeight) {
                                        continue;
                                    }

                                    const base64 = this.generateThumbnail(img);

                                    imageItems.push({
                                        label: isTemplate
                                            ? `image ${imageItems.length + 1}`
                                            : `image ${imageItems.length + 1}: template ${idx + 1} (${preview.template?.name || 'Template ' + (idx + 1)})`,
                                        base64,
                                    });
                                }
                            }

                            if (!imageItems.length) {
                                this.pushMessage({ role: 'assistant', content: 'No images found.' });
                                this.stopLoading();
                                return;
                            }

                            if (this.processCancelled) {
                                return;
                            }

                            const promptText = isTemplate
                                ? `Here are ${imageItems.length} images from the selected template in their current order. Are they in the best order for immersive storytelling on a Product Detail Page (PDP)? Give a brief assessment and specific recommendations if the order could be improved.`
                                : `Here are ${imageItems.length} images from this layout across all templates in their current order (${imageItems.map(i => i.label).join(', ')}). Are they in the best order for immersive storytelling on a Product Detail Page (PDP)? Consider what order best guides a shopper through the product story. Give a brief assessment and specific recommendations if the order could be improved.`;

                            const content = [{
                                type: 'text',
                                text: promptText
                            }];

                            for (const item of imageItems) {
                                content.push({
                                    type: 'image_url',
                                    image_url: { url: item.base64 },
                                });
                            }

                            const visionResponse = await this.callOpenAI({
                                model: 'gpt-4o',
                                messages: [{ role: 'user', content }],
                            });

                            this.pushMessage({ role: 'assistant', content: visionResponse.content || 'No response.' });
                            this.stopLoading();
                        } catch (e) {
                            if (this.processCancelled || e?.code === 'ERR_CANCELED') {
                                return;
                            }
                            console.error('Error in analyze_image_order:', e);
                            this.pushMessage({ role: 'assistant', content: 'Sorry, image order analysis failed. Please try again.' });
                            this.stopLoading();
                        }
                    } else if (functionName === "extract_text_from_image") {
                        this.pushMessage({
                            role: 'assistant',
                            content: 'Would you like to extract text from:',
                            widgets: [{
                                type: 'action_buttons',
                                id: 'extract-text-scope',
                                stacked: true,
                                buttons: [
                                    { label: 'Selected image', variant: 'outline-primary', action: 'extract_text_selected_image' },
                                    { label: 'Selected template', variant: 'outline-primary', action: 'extract_text_selected_template' },
                                ],
                            }],
                        });

                        this.stopLoading();
                    } else if (functionName === "create_pdp_image_set") {
                        // Stop the generic loader so the PDP chooser is visible immediately.
                        this.stopLoading();
                        await handleCreatePdpImageSet(
                            {
                                function_call: responseMessage.function_call,
                                savedAttachments: userMessage.attachments || [],
                            },
                            this._pdpAdapter
                        );
                        return;
                    } else if (functionName === "extract_text_from_user_image") {
                      this.pushMessage({
                        role: 'assistant',
                        content: functionArgs.text,
                      });
                      this.stopLoading();
                    }
                } else {
                    assistantMessage = {
                        role: "assistant",
                        content: responseMessage.content,
                    };
                }

                if (assistantMessage) {
                    this.pushMessage(assistantMessage);
                    this.stopLoading();
                }

                // Multi-action chaining: if the user's message implied multiple actions,
                // re-query the AI to execute remaining actions (max 4 follow-ups).
                if (responseMessage.function_call && !this._isChaining) {
                    const lastUserMsg = this.messages.filter(m => m.role === 'user').slice(-1)[0];
                    const hasMultipleActions = lastUserMsg && /\band\b|\balso\b|,\s*(change|update|replace|move|set|translate|edit|make)/i.test(lastUserMsg.content);

                    if (hasMultipleActions) {
                        this._isChaining = true;
                        this.startLoading();
                        try {
                            // Rebuild system prompt with CURRENT state after the first action
                            try { this.reorderLayers(this.selectedInstance.id); } catch (e) { /* ignore */ }
                            const chainLayers = JSON.stringify(
                                this.layersOrdered.map(layer => {
                                    const objId = layer.relatedCanvasObject?.id || '';
                                    return {
                                        element_id: layer.element_id,
                                        id: layer.id,
                                        name: layer.name,
                                        details: layer.relatedCanvasObject ? {
                                            text: layer.relatedCanvasObject.text ?? null,
                                            fill: layer.relatedCanvasObject.fill ?? null,
                                            fontSize: layer.relatedCanvasObject.fontSize ?? null,
                                        } : null,
                                        imageType: (objId.includes('image')) ? (
                                            objId.includes('product_image') ? 'product_image' :
                                            objId.includes('background_theme') ? 'background_image' : 'other_image'
                                        ) : undefined,
                                    };
                                }), null, 2
                            );
                            const chainTemplateCtx = (window.previews || []).map((p, i) => ({
                                index: i + 1,
                                instance_id: p.id,
                                name: p.template?.name || `Template ${i + 1}`,
                                is_selected: p.id === this.selectedInstance.id,
                            }));
                            const chainActiveIdx = chainTemplateCtx.findIndex(t => t.is_selected) + 1;
                            let chainSelectedId = 'none';
                            const chainActive = this.selectedInstance.canvas ? this.selectedInstance.canvas.getActiveObject() : null;
                            if (chainActive) chainSelectedId = chainActive.id || chainActive.element_id || 'none';

                            const chainSystemPrompt = `You are a banner ad designer AI assistant.
Here is the current list of layers in the active template:
${chainLayers}
The element_id of the currently selected layer is: ${chainSelectedId}
Templates: ${JSON.stringify(chainTemplateCtx)}
Currently active: template #${chainActiveIdx}
The user's original request had multiple parts. The first part has been completed. Now execute the NEXT part of the original request. Do NOT call switch_template again - you are already on the correct template.`;

                            // Build messages array for the chain call (without mutating this.messages)
                            const completedAction = responseMessage.function_call.name;
                            const chainMessages = [
                                { role: "system", content: chainSystemPrompt },
                                ...this.messages.slice(-20).map(m => ({ role: m.role, content: m.content })),
                                { role: 'user', content: `I just completed the "${completedAction}" action. Now please execute the NEXT action from my original request: "${lastUserMsg.content}". Call the appropriate function now - do NOT respond with text, you MUST call a function.` },
                            ];

                            const chainResponse = await this.callOpenAI({
                                messages: chainMessages,
                                model: "o3",
                                functions: aiChatFunctions,
                            });

                            if (chainResponse.function_call) {
                                const chainFuncName = chainResponse.function_call.name;
                                const chainFuncArgs = JSON.parse(chainResponse.function_call.arguments);

                                // Skip if AI wants to re-execute the same function that just completed
                                if (chainFuncName === completedAction) {
                                    // AI is repeating itself - no more actions needed
                                } else if (chainFuncName === 'edit_project_name') {
                                    const chainPreviews = window.previews || [];
                                    const chainTargetIndex = chainFuncArgs.template_index;
                                    let preview;
                                    if (chainTargetIndex && chainTargetIndex > 0 && chainTargetIndex <= chainPreviews.length) {
                                        preview = chainPreviews[chainTargetIndex - 1];
                                    } else {
                                        preview = chainPreviews.find(p => p.id === this.selectedInstance.id);
                                    }
                                    if (!preview) {
                                        this.pushMessage({ role: 'assistant', content: `I couldn't find that template. There are ${chainPreviews.length} template(s) in this layout.` });
                                        return;
                                    }
                                    const instanceId = preview.id;
                                    const grid = $("#template-group-preview");
                                    let customer_id = grid.attr("data-customer-id");
                                    const layout_id = grid.attr("data-layout-id");
                                    if (parseInt(customer_id) === 0) {
                                        // Find real customer_id from multiple sources
                                        customer_id = this.selectedInstance?.template?.customer_id
                                            || preview?.template?.customer_id
                                            || preview?.data?.customer_id
                                            || $('input[name="customer_id"]').val()
                                            || $('[customer-id]').not('[customer-id="0"]').first().attr('customer-id')
                                            || customer_id;
                                        // If still 0, try to extract from any link on the page
                                        if (parseInt(customer_id) === 0) {
                                            const link = $('a[href*="/banner/"]').not('[href*="/banner/0/"]').first().attr('href');
                                            if (link) {
                                                const m = link.match(/\/banner\/(\d+)\//);
                                                if (m) customer_id = m[1];
                                            }
                                        }
                                        // customer_id resolved
                                    }
                                    const dataForUpdate = [{ instance_id: instanceId, key: "project_name", value: chainFuncArgs.new_project_name }];
                                    try {
                                        await axios.put(`/banner/${customer_id}/group/${layout_id}/save_changes`, dataForUpdate);
                                        if (preview) {
                                            preview.data.project_name = chainFuncArgs.new_project_name;
                                            const el = $(`.template-item[data-instance=${instanceId}]`).find(".template-name-canvas");
                                            if (el.length && window.getTemplateNameText) {
                                                el.html(window.getTemplateNameText(preview.template, preview.data));
                                            }
                                        }
                                        this.pushMessage({ role: 'assistant', content: `I've also updated the project name to: "${chainFuncArgs.new_project_name}"` });
                                    } catch (e) {
                                        console.error('Chain edit_project_name error:', e.response?.status, e.response?.data || e.message);
                                        this.pushMessage({ role: 'assistant', content: `I couldn't update the project name. Error: ${e.response?.status || e.message}` });
                                    }
                                } else if (chainFuncName === 'edit_element_text') {
                                    const updates = chainFuncArgs.updates || [];
                                    const applied = [];

                                    const chainScope = ShowPageScopeCollector.get(['previews']);
                                    const chainInstanceId = this.selectedInstance?.id;

                                    for (const update of updates) {
                                        const layer = this.findLayerByElementIdOrSelected(update.element_id);

                                        if (layer && layer.relatedCanvasObject) {
                                            this.setActiveObject(layer.relatedCanvasObject);

                                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: update.new_text, action: "inputText" });

                                            if (chainFuncArgs.apply_to_all) {
                                                editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: true, action: "text", keys: ["text"] });
                                            }

                                            editorControlEventBroker.fire(EVENTS.PROPERTIES_MODULE_CHANGED, {
                                                action: 'name',
                                                name: update.new_text,
                                                fieldId: layer.id,
                                                link: '',
                                            });

                                            if (chainFuncArgs.apply_to_all) {
                                                const otherTemplates = (chainScope.previews || []).filter(p => p.id !== chainInstanceId).map(p => p.id);

                                                if (otherTemplates.length > 0) {
                                                    editorControlEventBroker.fire(EVENTS.APPLY_TO_PROPERTIES, {
                                                        action: 'apply_name_to',
                                                        name: update.new_text,
                                                        fieldId: layer.id,
                                                        templates: otherTemplates,
                                                        oldName: layer.name,
                                                    });
                                                }
                                            }

                                            applied.push(`"${layer.name || update.element_id || 'selected'}" - The new text is: "${update.new_text}"`);
                                        }
                                    }

                                    if (this.selectedInstance?.canvas) {
                                        this.selectedInstance.canvas.fire('layers:update', this.selectedInstance.id);
                                    }

                                    this.pushMessage({ role: 'assistant', content: `I've edited ${applied.length} field(s): \n${applied.join('\n')}` });
                                } else if (chainFuncName === 'edit_element_color') {
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    if (layer && layer.relatedCanvasObject) {
                                        this.setActiveObject(layer.relatedCanvasObject);
                                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: chainFuncArgs.new_color, action: "fontColor" });
                                        if (chainFuncArgs.apply_to_all) {
                                            editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: true, action: "fontColor", keys: ["color"] });
                                        }
                                        this.pushMessage({ role: 'assistant', content: `I've also changed the color to: "${chainFuncArgs.new_color}"` });
                                    }
                                } else if (chainFuncName === 'edit_element_font_size') {
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    if (layer && layer.relatedCanvasObject) {
                                        this.setActiveObject(layer.relatedCanvasObject);
                                        editorControlEventBroker.fire(EVENTS.TEXT_CHANGED, { value: chainFuncArgs.new_font_size, action: "fontSize" });
                                        this.pushMessage({ role: 'assistant', content: `I've also changed the font size to: "${chainFuncArgs.new_font_size}"` });
                                    }
                                } else if (chainFuncName === 'edit_shape_color') {
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    if (layer && layer.relatedCanvasObject) {
                                        this.setActiveObject(layer.relatedCanvasObject);
                                        editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, { action: 'bgColor', value: chainFuncArgs.new_color, keys: chainFuncArgs.apply_to_all ? ['fill_color'] : [] });
                                        this.pushMessage({ role: 'assistant', content: `I've also changed the shape color to: "${chainFuncArgs.new_color}"` });
                                    }
                                } else if (chainFuncName === 'edit_shape_gradient') {
                                    let layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    if (!layer) {
                                        layer = this.layersOrdered.find(l => {
                                            const t = l.relatedCanvasObject?.type;
                                            return t && ['rect', 'circle', 'CustomRect', 'polygon', 'ellipse'].includes(t);
                                        });
                                    }
                                    if (layer && layer.relatedCanvasObject) {
                                        this.setActiveObject(layer.relatedCanvasObject);
                                        const normStops = (chainFuncArgs.color_stops || []).map(s => ({
                                            offset: s.offset > 1 ? s.offset / 100 : s.offset,
                                            color: s.color,
                                        }));
                                        const gradVal = {
                                            type: chainFuncArgs.gradient_type || 'linear',
                                            direction: chainFuncArgs.direction || 'to right',
                                            colorStops: normStops,
                                        };
                                        const fg = createGradient(gradVal);
                                        if (fg) layer.relatedCanvasObject.set('fill', fg);
                                        editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
                                            action: 'gradient',
                                            value: gradVal,
                                            keys: chainFuncArgs.apply_to_all ? ['gradient'] : [],
                                        });
                                        layer.relatedCanvasObject.canvas.renderAll();
                                        const colors = (chainFuncArgs.color_stops || []).map(s => s.color).join(' → ');
                                        this.pushMessage({ role: 'assistant', content: `I've also applied a gradient (${colors}) to the shape.` });
                                    }
                                } else if (chainFuncName === 'move_object') {
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    if (layer && layer.relatedCanvasObject) {
                                        this.setActiveObject(layer.relatedCanvasObject);
                                        const distance = chainFuncArgs.distance || 10;
                                        const pos = layer.relatedCanvasObject.getPointByOrigin('left', 'top');
                                        let dx = 0, dy = 0;
                                        if (chainFuncArgs.direction === 'up') dy = -distance;
                                        else if (chainFuncArgs.direction === 'down') dy = distance;
                                        else if (chainFuncArgs.direction === 'left') dx = -distance;
                                        else if (chainFuncArgs.direction === 'right') dx = distance;
                                        layer.relatedCanvasObject.setPositionByOrigin({ x: pos.x + dx, y: pos.y + dy }, 'left', 'top');
                                        layer.relatedCanvasObject.setCoords();
                                        layer.relatedCanvasObject.canvas.renderAll();
                                        layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });
                                        this.pushMessage({ role: 'assistant', content: `I've also moved the element ${chainFuncArgs.direction} by ${distance}px.` });
                                    }
                                } else if (chainFuncName === 'switch_template') {
                                    // Already on the right template, skip
                                    this.pushMessage({ role: 'assistant', content: 'Already on the correct template.' });
                                } else if (chainFuncName === 'select_element' || chainFuncName === 'find_element_by_content') {
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id || chainFuncArgs.text_content);
                                    if (layer && layer.relatedCanvasObject) {
                                        this.setActiveObject(layer.relatedCanvasObject);
                                        this.pushMessage({ role: 'assistant', content: `Selected element "${layer.element_id || layer.id}".` });
                                    }
                                } else if (chainFuncName === 'edit_element_image' || chainFuncName === 'replace_element_image') {
                                    // Handle image editing/replacement directly in chain
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    let targetObject = layer && layer.relatedCanvasObject;

                                    // Fallback to active canvas object
                                    if (!targetObject) {
                                        const activeObj = this.selectedInstance && this.selectedInstance.canvas
                                            ? this.selectedInstance.canvas.getActiveObject() : null;
                                        targetObject = activeObj || this.targetElement || this.target;
                                    }

                                    if (!targetObject || !targetObject.getSrc) {
                                        this.pushMessage({ role: 'assistant', content: 'I couldn\'t find an image to edit. Please select an image first.' });
                                    } else {
                                        this.setActiveObject(targetObject);
                                        const imgUrl = targetObject.getSrc();
                                        const prompt = chainFuncArgs.changes_to_the_image || chainFuncArgs.description_of_the_new_image || '';

                                        try {
                                            const imgResponse = await fetch(imgUrl);
                                            const imgBlob = await imgResponse.blob();
                                            const { width, height } = await this.getImageDimensions(imgBlob);

                                            const fd = new FormData();
                                            if (chainFuncName === 'edit_element_image') {
                                                fd.append('image', new File([imgBlob], 'image.png', { type: imgBlob.type }));
                                                fd.append('infillMethod', 'gemini_nano_banana_pro');
                                                fd.append('overwritePrompt', prompt);

                                                // Add reference image if specified
                                                if (chainFuncArgs.reference_template_index) {
                                                    try {
                                                        const refIdx = chainFuncArgs.reference_template_index - 1;
                                                        const refPreview = (window.previews || [])[refIdx];
                                                        if (refPreview && refPreview.canvas) {
                                                            let refObj = chainFuncArgs.reference_element_id
                                                                ? refPreview.canvas.getObjects().find(o => o.id === chainFuncArgs.reference_element_id)
                                                                : null;
                                                            if (!refObj) refObj = refPreview.canvas.getObjects().find(o => o.type === 'image' && o.getSrc);
                                                            if (refObj && refObj.getSrc) {
                                                                const refRes = await fetch(refObj.getSrc());
                                                                const refBlob = await refRes.blob();
                                                                fd.append('secondImage', new File([refBlob], 'reference.png', { type: refBlob.type }));
                                                            }
                                                        }
                                                    } catch (e) { /* ignore ref errors */ }
                                                }
                                            } else {
                                                fd.append('promptForGenerating', prompt);
                                            }
                                            fd.append('width', width);
                                            fd.append('height', height);

                                            if (this.processCancelled) return;
                                            const endpoint = chainFuncName === 'edit_element_image' ? '/ai-edit-image' : '/ai-create-image';
                                            this.activeAbortController = new AbortController();
                                            const aiRes = await axios.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' }, signal: this.activeAbortController.signal });
                                            this.activeAbortController = null;
                                            const newImageUrl = window.location.origin + aiRes.data.url;

                                            // Auto-apply if specified, otherwise show widgets
                                            if (chainFuncArgs.auto_apply === 'replace') {
                                                this.pushMessage({ role: 'assistant', content: 'I\'ve edited and replaced the image.', imageUrl: newImageUrl });
                                                this.applyImageToCurrentElement(newImageUrl);
                                            } else if (chainFuncArgs.auto_apply === 'new_template') {
                                                this.pushMessage({ role: 'assistant', content: 'I\'ve edited the image and adding to a new template.', imageUrl: newImageUrl });
                                                this.addImageToNewTemplate(newImageUrl, 'original');
                                            } else {
                                                this.pushMessage({
                                                    role: 'assistant',
                                                    content: 'I\'ve edited the image. What would you like to do?',
                                                    imageUrl: newImageUrl,
                                                    widgets: [
                                                        { type: 'radio', id: 'image-save-action', options: [
                                                            { value: 'replace', label: 'Replace current image' },
                                                            { value: 'new_template', label: 'Add to new template' },
                                                        ]},
                                                        { type: 'dropdown', id: 'template-size', label: 'Template size',
                                                          dependsOn: { widgetId: 'image-save-action', value: 'new_template' },
                                                          placeholder: 'Select size...', options: [
                                                            { value: 'original', label: 'Original size' },
                                                            { value: '1500x1500', label: '1500 x 1500' },
                                                            { value: '2000x2000', label: '2000 x 2000' },
                                                        ]},
                                                        { type: 'action_buttons', id: 'image-save-confirm', buttons: [
                                                            { label: 'Apply', variant: 'primary', action: 'apply_image' },
                                                            { label: 'Discard', variant: 'outline-secondary', action: 'discard_image' },
                                                        ]},
                                                    ],
                                                    _imageActionContext: { newImageUrl },
                                                });
                                            }
                                        } catch (imgErr) {
                                            console.error('Chain image edit error:', imgErr);
                                            this.pushMessage({ role: 'assistant', content: 'Sorry, the image editing failed. Please try the action separately.' });
                                        }
                                    }
                                } else if (chainFuncName === 'translate_image') {
                                    // Handle image translation in chain
                                    const layer = this.findLayerByElementIdOrSelected(chainFuncArgs.element_id);
                                    let tgtObj = layer && layer.relatedCanvasObject;
                                    if (!tgtObj) {
                                        const activeObj = this.selectedInstance?.canvas?.getActiveObject();
                                        tgtObj = activeObj || this.targetElement || this.target;
                                    }
                                    if (!tgtObj) {
                                        const imgLayer = this.layersOrdered.find(l => l.relatedCanvasObject?.type === 'image' && l.relatedCanvasObject?.getSrc);
                                        if (imgLayer) tgtObj = imgLayer.relatedCanvasObject;
                                    }

                                    if (!tgtObj || !tgtObj.getSrc) {
                                        this.pushMessage({ role: 'assistant', content: 'I couldn\'t find an image to translate.' });
                                    } else {
                                        try {
                                            this.setActiveObject(tgtObj);
                                            const tImgUrl = tgtObj.getSrc();
                                            const tFullUrl = tImgUrl.startsWith('http') ? tImgUrl : window.location.origin + tImgUrl;

                                            this.pushMessage({ role: 'assistant', content: `Translating image to ${chainFuncArgs.target_language}... This may take a moment.` });
                                            this.saveMessages();

                                            const tImgRes = await fetch(tFullUrl);
                                            const tImgBlob = await tImgRes.blob();
                                            const tDims = await this.getImageDimensions(tImgBlob);

                                            // Build translation prompt
                                            const tLang = chainFuncArgs.target_language;
                                            const tPrompt = `Edit this image and replace all the text with its translation to ${tLang}. Do not change the color of the font and do not make any other edits or changes.`;

                                            const tFd = new FormData();
                                            tFd.append('image', new File([tImgBlob], 'input.png', { type: tImgBlob.type }));
                                            tFd.append('infillMethod', 'gemini_nano_banana_pro');
                                            tFd.append('overwritePrompt', tPrompt);
                                            tFd.append('width', tDims.width);
                                            tFd.append('height', tDims.height);

                                            if (this.processCancelled) return;
                                            this.activeAbortController = new AbortController();
                                            const tRes = await axios.post('/ai-edit-image', tFd, { headers: { 'Content-Type': 'multipart/form-data' }, signal: this.activeAbortController.signal });
                                            this.activeAbortController = null;
                                            const tNewUrl = window.location.origin + tRes.data.url;

                                            this.pushMessage({ role: 'assistant', content: `Translation to ${tLang} complete:`, imageUrl: tNewUrl });

                                            if (chainFuncArgs.auto_apply === 'replace') {
                                                this.applyImageToCurrentElement(tNewUrl);
                                                this.pushMessage({ role: 'assistant', content: 'The translated image has been applied.' });
                                            } else if (chainFuncArgs.auto_apply === 'new_template') {
                                                this.addImageToNewTemplate(tNewUrl, 'original');
                                            } else {
                                                this.pushMessage({
                                                    role: 'assistant',
                                                    content: 'What would you like to do?',
                                                    widgets: [
                                                        { type: 'radio', id: 'translate-action', options: [
                                                            { value: 'new_template', label: 'Add to new template' },
                                                            { value: 'replace', label: 'Apply to template' },
                                                        ]},
                                                        { type: 'action_buttons', id: 'translate-confirm', buttons: [
                                                            { label: 'Apply', variant: 'primary', action: 'apply_translate_choice' },
                                                        ]},
                                                    ],
                                                    _imageActionContext: { newImageUrl: tNewUrl },
                                                });
                                            }
                                        } catch (tErr) {
                                            console.error('Chain translate error:', tErr);
                                            this.pushMessage({ role: 'assistant', content: 'Sorry, translation failed. Please try it separately.' });
                                        }
                                    }
                                } else {
                                    // For any other unhandled functions
                                    this.pushMessage({ role: 'assistant', content: chainResponse.content || 'Please send another message to continue with the remaining actions.' });
                                }
                            } else if (chainResponse.content) {
                                this.pushMessage({ role: 'assistant', content: chainResponse.content });
                            }
                        } catch (chainError) {
                            console.error('Multi-action chain error:', chainError);
                            this.pushMessage({ role: 'assistant', content: 'Sorry, I could not complete the remaining actions. Please try them separately.' });
                        }
                        this.stopLoading();
                        delete this._isChaining;
                    }
                }

            } catch (error) {
                // Don't show error message if the request was intentionally aborted (stop button)
                if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.message === 'canceled') {
                    return;
                }
                console.error("Error:", error);
                this.pushMessage({
                    role: "assistant",
                    content:
                        "Sorry, there was an error processing your message.",
                });
                this.stopLoading();
            } finally {
                this.$nextTick(() => {
                    const messagesDiv = this.$refs.messages;
                    messagesDiv.scrollTop = messagesDiv.scrollHeight;
                });
            }
        },
        // Helper method to determine the event type based on the element type
        getElementEventType(object) {
            // Determine the appropriate event type based on object properties
            if (object.type === 'text' || object.type === 'i-text' || object.text !== undefined) {
                return EVENTS.TEXT_CHANGED;
            } else if (object.type === 'image' || object.id && object.id.includes('image')) {
                return EVENTS.IMAGE_CHANGED;
            } else if (['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(object.type) ||
                (object.id && (
                    object.id.includes('circle') ||
                    object.id.includes('rect') ||
                    object.id.includes('shape') ||
                    object.id.includes('polygon') ||
                    object.id.includes('triangle')
                ))) {
                return EVENTS.SHAPE_CHANGED;
            }
            
            // Default to shape changed if type can't be determined
            return EVENTS.SHAPE_CHANGED;
        },
        // Helper method to determine the element type
        getElementType(object) {
            // Check what type of object this is based on properties
            if (!object) return 'object';
            
            if (object.type === 'textbox' || object.type === 'i-text' || object.id && object.id.startsWith('text_')) {
                return 'text';
            } else if (object.type === 'image' || object.id && object.id.startsWith('image_')) {
                return 'image';
            } else if (object.type === 'rect' || object.type === 'circle' || object.id && (object.id.startsWith('rect_') || object.id.startsWith('circle_') || object.id.startsWith('additional_field_rect_'))) {
                return 'shape';
            }
            
            return 'object';
        },
        buildGalleryNoMatchMessage(query, suggestions = []) {
            const safeSuggestions = Array.isArray(suggestions)
                ? suggestions.map((value) => String(value).trim()).filter(Boolean).slice(0, 3)
                : [];

            if (safeSuggestions.length === 0) {
                return `I couldn't find a Design Gallery template matching "${query}". Please try another name, id, or keyword.`;
            }

            if (safeSuggestions.length === 1) {
                return `I couldn't find an exact match for "${query}". Did you mean "${safeSuggestions[0]}"?`;
            }

            return `I couldn't find an exact match for "${query}". Closest templates I found are ${safeSuggestions.map((name) => `"${name}"`).join(', ')}.`;
        },
        async resolveGalleryTemplateMatchWithAI(query, templates) {
            const candidates = (Array.isArray(templates) ? templates : []).slice(0, 120).map((template) => ({
                id: template?.id,
                name: String(template?.name || '').trim(),
            })).filter((candidate) => candidate.id && candidate.name);

            if (!candidates.length) {
                return null;
            }

            try {
                const response = await this.callOpenAI({
                    model: 'o3',
                    messages: [
                        {
                            role: 'system',
                            content: `You help match a user's Design Gallery request to one of the candidate templates.
The user is looking for a template named: "${query}".
Rules:
- Choose the best template whose name or meaning matches the user's request.
- If the user typed a partial name, use the closest likely template name.
- If none are convincing, return match_id null.
- Return ONLY valid JSON in this shape:
{
  "match_id": 123 | null,
  "confidence": 0.0,
  "message": "short message for the user",
  "suggestions": ["template name 1", "template name 2"]
}`,
                        },
                        {
                            role: 'user',
                            content: JSON.stringify({ query, candidates }, null, 2),
                        },
                    ],
                });

                const raw = String(response?.content || '').trim();
                const jsonText = raw.match(/\{[\s\S]*}/)?.[0] || raw;
                const parsed = JSON.parse(jsonText);
                const matchId = parsed?.match_id != null ? Number(parsed.match_id) : null;
                const confidence = Number(parsed?.confidence || 0);
                const suggestions = Array.isArray(parsed?.suggestions)
                    ? parsed.suggestions.map((value) => String(value).trim()).filter(Boolean)
                    : [];

                return {
                    matchId: Number.isFinite(matchId) ? matchId : null,
                    confidence: Number.isFinite(confidence) ? confidence : 0,
                    message: String(parsed?.message || '').trim(),
                    suggestions,
                };
            } catch (error) {
                console.error('Error resolving gallery match with AI:', error);
                return null;
            }
        },
        dispatchGalleryTemplateSelection(chosenTemplate, options = {}) {
            const shouldOpenAiChat = Boolean(options?.openAiChat);
            const normalized = {
                id: Number(chosenTemplate?.id),
                width: Number(chosenTemplate?.width),
                height: Number(chosenTemplate?.height),
            };

            if (!normalized.id || !normalized.width || !normalized.height) {
                toastr.error('Selected template is missing required dimensions.');
                return false;
            }

            if (chosenTemplate?.source_file) {
                window.dispatchEvent(
                    new CustomEvent('gallery-import-psd-templates', {
                        detail: {
                            files: [{
                                url: chosenTemplate.source_file,
                                name: chosenTemplate.name || `Template ${normalized.id}`,
                                id: normalized.id,
                            }],
                            templatesIds: [normalized.id],
                            autoAddToTemplate: true,
                            openAiChatAfterReload: shouldOpenAiChat,
                        },
                    })
                );
            } else {
                window.dispatchEvent(
                    new CustomEvent('gallery-add-templates', {
                        detail: {
                            templates: [normalized],
                            openAiChatAfterReload: shouldOpenAiChat,
                        },
                    })
                );
            }

            if (shouldOpenAiChat) {
                window.dispatchEvent(new Event('ai-chat:open-tab'));
            }

            return true;
        },
        async addTemplateFromDesignGallery(templateQuery) {
            const query = String(templateQuery || '').trim();
            if (!query) {
                this.pushMessage({
                    role: 'assistant',
                    content: 'Please tell me which design-gallery template you want to add.',
                });
                this.stopLoading();
                return;
            }

            try {
                this.startLoading();
                this.pushMessage({
                    role: 'assistant',
                    content: `Got it - I will search Design Gallery for "${query}" and add the best match.`,
                });

                const { data } = await axios.get('/networks/templates');
                const templates = Array.isArray(data) ? data : [];
                const aiMatch = await this.resolveGalleryTemplateMatchWithAI(query, templates);

                if (!aiMatch) {
                    this.pushMessage({
                        role: 'assistant',
                        content: this.buildGalleryNoMatchMessage(query, []),
                    });
                    this.stopLoading();
                    return;
                }

                const selectedTemplate = aiMatch.matchId != null
                    ? templates.find((template) => Number(template.id) === Number(aiMatch.matchId))
                    : null;

                if (!selectedTemplate) {
                    const suggestionText = this.buildGalleryNoMatchMessage(query, aiMatch.suggestions || []);
                    this.pushMessage({
                        role: 'assistant',
                        content: `${aiMatch.message || suggestionText}${aiMatch.suggestions?.length ? ` Closest matches: ${aiMatch.suggestions.map((name) => `"${name}"`).join(', ')}.` : ''}`,
                    });
                    this.stopLoading();
                    return;
                }

                const dispatched = this.dispatchGalleryTemplateSelection(selectedTemplate, { openAiChat: true });
                if (!dispatched) {
                    this.pushMessage({
                        role: 'assistant',
                        content: `I found "${selectedTemplate.name || `Template ${selectedTemplate.id}`}", but it is missing required size data.`,
                    });
                    this.stopLoading();
                    return;
                }

                this.pushMessage({
                    role: 'assistant',
                    content: `Found "${selectedTemplate.name || `Template ${selectedTemplate.id}`}". Adding it now...`,
                });
                this.stopLoading();
            } catch (error) {
                console.error('Error in add_template_from_design_gallery:', error);
                this.pushMessage({
                    role: 'assistant',
                    content: 'Sorry, I could not load Design Gallery templates right now. Please try again.',
                });
                this.stopLoading();
            }
        },
        currentFabricObjects(canvas, fieldType) {
            return canvas
                .getObjects()
                .filter((object) => object.templateField)
                .filter(({ templateField }) => templateField.type.includes(fieldType));
        },
        async initProductSelectorForAdd(instanceId) {
            const preview = window.previews.find(p => p.id === instanceId);
            if (!preview) return;

            const FIELD_TYPE_PRODUCT_IMAGE = 'Product Image';
            const currentProductImages = this.currentFabricObjects(preview.canvas, FIELD_TYPE_PRODUCT_IMAGE);
            
            // 1. Initialize store with current images (this sets mode to VIEW [0])
            await this.$store.dispatch('fileBrowserProductSelector/initView', {
                currentProductImages: currentProductImages,
                selectedObjects: []
            });

            // 2. Transition to BROWSER mode [1] manually to avoid clearing selection
            // (Standard enableBrowserMode action calls CLEAR_SELECTION, which we want to avoid)
            this.$store.commit('fileBrowserProductSelector/SET_MODE', 1); // 1 is BROWSER
            this.$store.commit('fileBrowserProductSelector/SET_SHOW_ONLY_SELECTED', false);
            this.$store.commit('fileBrowserProductSelector/SET_IS_ITEMS_PICK_ENABLED', true);
            this.$store.commit('fileBrowserProductSelector/SET_ONLY_ONE_ITEM_SELECTION', false);
            this.$store.commit('fileBrowserProductSelector/SET_GROUPED', true);
            this.$store.commit('fileBrowserProductSelector/SET_CURRENT_BROWSER_TYPE', 'regular');
            this.$store.commit('fileBrowserProductSelector/SET_CURRENT_PAGE', 1);

            // 3. Mirror addProductDimAndGTIN logic
            const FIELD_TYPE_PRODUCT_DIMENSIONS = 'Product Dimensions';
            const FIELD_TYPE_UPC_GTIN = 'UPC/GTIN';
            const fields = preview.template.fields;
            const hasDimensions = fields.some(f => f.type === FIELD_TYPE_PRODUCT_DIMENSIONS);
            const hasGtin = fields.some(f => f.type === FIELD_TYPE_UPC_GTIN);

            if (!hasDimensions || !hasGtin) {
                if (typeof window.addFieldProductDimensionToBaseTemplate === 'function') {
                    window.addFieldProductDimensionToBaseTemplate(preview);
                }
                if (typeof window.addFieldUpcGtinToBaseTemplate === 'function') {
                    window.addFieldUpcGtinToBaseTemplate(preview);
                }
            }
        },
        async initProductSelectorForReplace(instanceId) {
            const preview = window.previews.find(p => p.id === instanceId);
            if (!preview) return;

            const FIELD_TYPE_PRODUCT_IMAGE = 'Product Image';
            const currentProductImages = this.currentFabricObjects(preview.canvas, FIELD_TYPE_PRODUCT_IMAGE);
            
            // Get the currently selected object on canvas
            const selectedObjects = preview.canvas.getActiveObjects();

            // 1. Initialize store with current images and selection (this sets mode to VIEW [0])
            await this.$store.dispatch('fileBrowserProductSelector/initView', {
                currentProductImages: currentProductImages,
                selectedObjects: selectedObjects
            });

            // 2. Transition to REPLACE mode [2] manually
            this.$store.commit('fileBrowserProductSelector/SET_MODE', 2); // 2 is REPLACE
            this.$store.commit('fileBrowserProductSelector/SET_SHOW_ONLY_SELECTED', false);
            this.$store.commit('fileBrowserProductSelector/SET_IS_ITEMS_PICK_ENABLED', false);
            this.$store.commit('fileBrowserProductSelector/SET_ONLY_ONE_ITEM_SELECTION', true);
            this.$store.commit('fileBrowserProductSelector/SET_GROUPED', false);
            this.$store.commit('fileBrowserProductSelector/SET_CURRENT_BROWSER_TYPE', 'regular');
            this.$store.commit('fileBrowserProductSelector/SET_CURRENT_PAGE', 1);

            await this.$store.dispatch('fileBrowserProductSelector/fetchData');
        },
        selectProduct(productId, message) {
            if (message) this.$set(message, 'disabled', true);
            $("#addProductImages").modal("show");

            setTimeout(() => {
                const searchInput = $(
                    "#addProductImages .c-top-bar__search--input",
                );
                if (searchInput.length) {
                    searchInput.val(productId);
                    searchInput.trigger("input");

                    const events = ["keydown", "keypress", "keyup"];
                    events.forEach((eventType) => {
                        const event = new Event(eventType, { bubbles: true });
                        Object.defineProperties(event, {
                            keyCode: { value: 13 },
                            key: { value: "Enter" },
                            which: { value: 13 },
                            code: { value: "Enter" },
                        });
                        searchInput[0].dispatchEvent(event);
                    });
                }
            }, 300);
        },
        copyProofLink(id, message) {
            if (message) this.$set(message, 'disabled', true);
            const input = document.getElementById(id);
            input.select();
            document.execCommand("copy");
            toastr.success("Proof link copied to clipboard!");
        },
        openProductSelector(message) {
            if (message) this.$set(message, 'disabled', true);
            const isAddFlow = message && message.actionType === 'add_product';

            $("#addProductImages").modal("show");
            
            /*
            // Clear the search input when opening manually and trigger refresh
            setTimeout(async () => {
                const $inputs = $("#addProductImages .c-top-bar__search--input");
                
                await this.$store.dispatch("fileBrowserProductSelector/setSearchQuery", "");
                
                if (isAddFlow) {
                    await this.initProductSelectorForAdd(this.selectedInstance.id);
                } else {
                    await this.initProductSelectorForReplace(this.selectedInstance.id);
                }
                
                $inputs.val("").trigger("input").trigger("change").trigger("keyup");
            }, 500);
            */
        },
        async selectProductOption(option, message) {
            this.startLoading();
            if (message) this.$set(message, 'disabled', true);
            if (message.actionType === 'add_product') {
                this.executeProductAddFlow(option.name, true, message.applyToAll);
                this.pushMessage({
                    role: "assistant",
                    content: `I'm adding product image "${option.name}"...`,
                });
            } else {
                this.executeProductReplacementFlow(option.name, message.applyTo, true);
                this.pushMessage({
                    role: "assistant",
                    content: `I'm replacing product image with "${option.name}"...`,
                });
            }
        },
        async executeProductAddFlow(searchTerm, forceAutoSelect = false, applyToAll = false) {
            await this.initProductSelectorForAdd(this.selectedInstance.id);
            $("#addProductImages").modal("show");
            
            setTimeout(async () => {
                const $searchInput = $(".c-top-bar__search--input");
                if ($searchInput.length) {
                    $searchInput.val(searchTerm);
                }
                
                try {
                    await this.$store.dispatch('fileBrowserProductSelector/setSearchQuery', searchTerm);
                    await this.$store.dispatch('fileBrowserProductSelector/fetchCurrentBrowserData');
                } catch (e) {
                    console.error("AI Chat: Failed to trigger search via store", e);
                }
                
                const storeItems = this.$store.getters['fileBrowserProductSelector/items'];
                if (!storeItems || storeItems.length === 0) {
                    this.pushMessage({
                        role: "assistant",
                        content: `Product ID "${searchTerm}" was not found in our database`,
                        actionType: 'add_product',
                        hasOpenSelectorButton: true
                    });
                    $("#addProductImages").modal("hide");
                    this.stopLoading();
                    return;
                }
                
                const pollResults = setInterval(() => {
                    const $firstItem = $(".c-grid-view-item img");
                    if ($firstItem.length > 0) {
                        clearInterval(pollResults);
                        
                        if (!forceAutoSelect && storeItems && storeItems.length > 1) {
                            this.pushMessage({
                                role: "assistant",
                                content: `Which of these images should I use to add a product?`,
                                productOptions: storeItems,
                                actionType: 'add_product',
                                applyToAll: applyToAll
                            });
                            $("#addProductImages").modal("hide");
                            this.stopLoading();
                        } else {
                            $firstItem[0].click();
                            
                            setTimeout(() => {
                                if (applyToAll) {
                                    $("#save-product-images-to-all").click();
                                } else {
                                    $("#save-product-images").click();
                                }
                                this.stopLoading();
                            }, 800);
                        }
                    }
                }, 500);
                
                setTimeout(() => {
                    clearInterval(pollResults);
                    if (!this.loading) return; 
                    $("#addProductImages").modal("hide");
                    this.stopLoading();
                }, 10000);
            }, 1000);
        },
        async executeProductReplacementFlow(searchTerm, applyTo, forceAutoSelect = false) {
            await this.initProductSelectorForReplace(this.selectedInstance.id);
            $("#addProductImages").modal("show");
            
            setTimeout(async () => {
                const $searchInput = $("#addProductImages .c-top-bar__search--input");
                if ($searchInput.length) {
                    $searchInput.val(searchTerm);
                }
                
                try {
                    await this.$store.dispatch('fileBrowserProductSelector/setSearchQuery', searchTerm.toString());
                    await this.$store.dispatch('fileBrowserProductSelector/fetchCurrentBrowserData');
                } catch (e) {
                    console.error("AI Chat: Failed to trigger search via store", e);
                }
                
                const storeItems = this.$store.getters['fileBrowserProductSelector/items'];
                if (!storeItems || storeItems.length === 0) {
                    this.pushMessage({
                        role: "assistant",
                        content: `Product ID "${searchTerm}" was not found in our database`,
                        applyTo: applyTo,
                        hasOpenSelectorButton: true
                    });
                    $("#addProductImages").modal("hide");
                    this.stopLoading();
                    return;
                }
                
                const pollResults = setInterval(() => {
                    const $firstItem = $("#addProductImages .c-grid-view-item img");
                    if ($firstItem.length > 0) {
                        clearInterval(pollResults);
                        
                        if (!forceAutoSelect && storeItems && storeItems.length > 1) {
                            this.pushMessage({
                                role: "assistant",
                                content: `Which of these images should I use as the replacement?`,
                                productOptions: storeItems,
                                applyTo: applyTo
                            });
                            $("#addProductImages").modal("hide");
                            this.stopLoading();
                        } else {
                            $firstItem[0].click();

                            setTimeout(() => {
                                if (applyTo === 'replace_selected_product_image') {
                                    $("#save-product-images").click();
                                } else {
                                    const $dropdown = $(".replace-options-dropdown");
                                    $dropdown.find(".dropdown-toggle").click();
                                    setTimeout(() => {
                                        if (applyTo === 'replace_all_product_images') {
                                            $dropdown.find("label:contains('Replace All Product Images')").click();
                                        } else if (applyTo === 'replace_visible_product_images') {
                                            $dropdown.find("label:contains('Replace Visible Product Images')").click();
                                        } else if (applyTo === 'apply_to_all_templates') {
                                            $dropdown.find("label:contains('Apply to All Templates')").click();
                                        }
                                        setTimeout(() => {
                                            $dropdown.find("button:not(.dropdown-toggle)").click();
                                        }, 300);
                                    }, 500);
                                }
                                this.pushMessage({ role: 'assistant', content: 'The image has been replaced.' });
                                this.stopLoading();
                            }, 800);
                        }
                    }
                }, 500);
                
                setTimeout(() => {
                    clearInterval(pollResults);
                    if (!this.loading) return; 
                    $("#addProductImages").modal("hide");
                    this.stopLoading();
                }, 10000);
            }, 1000);
        },
        async duplicateTemplateAndPrepareResizeData(newWidth, newHeight, bannerResizeSettings, message) {
            if (message) this.$set(message, 'disabled', true);
            this.messages[this.messages.length - 1].bannerResizeSettings = null;
            this.messages[this.messages.length - 1].resizeConfirmButton = null;

            const instanceId = this.selectedInstance.id;
            const grid = $("#template-group-preview");
            let customerId = grid.attr("data-customer-id");
            const isAllCustomer = parseInt(customerId) === 0;
            if (isAllCustomer) {
                customerId = this.selectedInstance?.template?.customer_id || customerId
            }
            const layoutId = grid.attr("data-layout-id");
            const templateId = grid.attr("data-template-id");

            const url = `/banner/${customerId}/group/${layoutId}/template/${instanceId}/${templateId}/duplicate?old-customer=0`;
            const response = await axios.post(location.origin + url, {
                forceReturnInstanceId: true
            });

            localStorage.setItem("newSettingsFromAiBannerResizer", JSON.stringify({
                instanceId: response.data.instance_id,
                templateId,
                layoutId,
                customerId,
                newWidth,
                newHeight,
                bannerResizeSettings
            }));

            this.pushMessage({
                role: "assistant",
                content: `The template was copied successfully. Reloading page...`
            });
            this.stopLoading();

            if (response.data.redirect_url) {
                window.location.href = response.data.redirect_url;
                return;
            }

            location.reload();
        },
        async applyAiResizeSettings(_instanceId, _templateId, _layoutId, _customerId, newWidth, newHeight, bannerResizeSettings) {
            this.startLoading();

            const instanceId = this.selectedInstance.id;
            const grid = $("#template-group-preview");
            let customerId = grid.attr("data-customer-id");
            const isAllCustomer = parseInt(customerId) === 0;
            if (isAllCustomer) {
                customerId = this.selectedInstance?.template?.customer_id || customerId
            }
            const layoutId = grid.attr("data-layout-id");
            const templateId = grid.attr("data-template-id");

            if (!(instanceId == _instanceId && templateId == _templateId && layoutId == _layoutId && customerId == _customerId)) {
                this.pushMessage({
                    role: "assistant",
                    content: `Sorry, I couldn't apply the new positions to the resized template.`
                });
                return;
            }

            this.reorderLayers(instanceId);

            const objects = getObjectsForOrder(this.selectedInstance.canvas.getObjects());

            let preview = window.previews.find((preview) => {
                return preview.id === this.selectedInstance.id;
            });

            copyAndResizeEventBroker.fire(COPY_AND_RESIZE_EVENTS.SAVED, {
                statePreview: preview,
                isUseDestinationTemplateFields: false,
                selectedSizes: [
                    {
                        width: newWidth,
                        height: newHeight,
                        type: "proportional"
                    }
                ],
                alignContent: {
                    originX: "start",
                    originY: "start",
                },
                backgroundProperties: {
                    selectedField: null,
                    size: "cover",
                    position: "left_and_top",
                },
                isMultiSizes: false,
                objects: objects,
            });

            await new Promise(resolve => setTimeout(resolve, 100));

            for (let i = 0; i < this.layersOrdered.length; i++) {
                const layer = this.layersOrdered[i];
                if (!bannerResizeSettings) {
                    continue;
                }

                // eslint-disable-next-line no-control-regex
                const clean = s => s.replace(/[\x00-\x1F]/g, '');
                const layerUpdates = bannerResizeSettings.find(
                    (updatedLayer) => 
                        updatedLayer.id === layer.id ||
                        (updatedLayer.layer_name && clean(updatedLayer.layer_name) === clean(layer.name))
                );
                if (!layerUpdates) {
                    continue;
                }

                if (!layer.relatedCanvasObject) {
                    continue;
                }

                // Set the active object
                this.setActiveObject(layer.relatedCanvasObject);

                // Determine the type of object we're working with
                const isImage = layer.relatedCanvasObject.type === 'image' ||
                    (layer.relatedCanvasObject.id &&
                        layer.relatedCanvasObject.id.includes('image'));

                const isText = layer.relatedCanvasObject.type === 'text' ||
                    layer.relatedCanvasObject.type === 'i-text' ||
                    layer.relatedCanvasObject.text !== undefined ||
                    (layer.relatedCanvasObject.id &&
                        layer.relatedCanvasObject.id.includes('text_'));

                const isShape = layer.relatedCanvasObject.type && (
                    ['rect', 'circle', 'polygon', 'path', 'ellipse', 'triangle', 'CustomRect'].includes(layer.relatedCanvasObject.type) ||
                    (layer.relatedCanvasObject.id && (
                        layer.relatedCanvasObject.id.includes('circle') ||
                        layer.relatedCanvasObject.id.includes('rect') ||
                        layer.relatedCanvasObject.id.includes('shape') ||
                        layer.relatedCanvasObject.id.includes('polygon') ||
                        layer.relatedCanvasObject.id.includes('triangle')
                    ))
                );

                // Get new X and new Y coordinates or use existing ones
                const newX = layerUpdates.new_x;
                const newY = layerUpdates.new_y;

                // Get current position
                const currentPosition = layer.relatedCanvasObject.getPointByOrigin('left', 'top');

                // Set new position
                layer.relatedCanvasObject.setPositionByOrigin(
                    { 
                        x: newX !== null ? newX : currentPosition.x, 
                        y: newY !== null ? newY : currentPosition.y 
                    },
                    'left',
                    'top'
                );

                // Get new width and height
                const newW = layerUpdates.new_width || null;
                const newH = layerUpdates.new_height || null;

                // Emit size changed event
                if (newW !== null && newH !== null) {
                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                        action: 'w',
                        value: newW
                    });
                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                        action: 'h',
                        value: newH
                    });
                }

                // Update coordinates
                layer.relatedCanvasObject.setCoords();

                // Common updates for all object positioning
                layer.relatedCanvasObject.canvas.renderAll();
                layer.relatedCanvasObject.canvas.fire('object:moving', { target: layer.relatedCanvasObject });
            }

            this.triggerSave();

            this.pushMessage({
                role: "assistant",
                content: `Done! The new AI generated positions were applied successfully.`
            });

            this.stopLoading();
        },
        generateThumbnail(imgElement, maxSize = 300, quality = 0.8) {
            const scale = Math.min(maxSize / imgElement.naturalWidth, maxSize / imgElement.naturalHeight, 1);

            const thumbCanvas = document.createElement('canvas');

            thumbCanvas.width = Math.round(imgElement.naturalWidth * scale);
            thumbCanvas.height = Math.round(imgElement.naturalHeight * scale);

            const ctx = thumbCanvas.getContext('2d');

            ctx.drawImage(imgElement, 0, 0, thumbCanvas.width, thumbCanvas.height);

            return thumbCanvas.toDataURL('image/jpeg', quality);
        },
        normalizeAttachments(attachments) {
            if (!Array.isArray(attachments)) {
                attachments = [attachments];
            }

            return attachments.filter(a => a != null).map((attachment) => {
                if (attachment?.file instanceof File) {
                    return {
                        type: 'file',
                        payload: attachment.file,
                        preview: URL.createObjectURL(attachment.file),
                        fileType: attachment.file.type,
                        name: attachment.file.name,
                        size: attachment.file.size,
                    };
                } else {
                    return {
                        type: 'file',
                        payload: null,
                    };
                }
            });
        },
        async clearAttachments() {
            try {
                const attachmentsToDelete = this.messages
                .flatMap(msg => msg.attachments || [])
                .filter(a => a.type === 'url' && !a.payload.startsWith('data:'))
                .map(a => a.payload);

                if (attachmentsToDelete.length) {
                    await axios.put('/attachments/clear', {
                        attachmentsUrls: attachmentsToDelete
                    });
                }

                return true;
            } catch (e) {
                toastr.error('Failed to clear attachments. Please try again later.', {});
                console.error('Failed to clear attachments:', e);
                return false;
            }
        },
        async addUploadedImageFlow(targetIndex, targetInstanceId, attachment_indexes) {
            try {
                const attachmentIndexesRaw = Array.isArray(attachment_indexes)
                    ? attachment_indexes
                    : [];

                const attachmentIndexes = [...new Set(
                    attachmentIndexesRaw
                        .map(i => Number(i))
                        .filter(i => Number.isInteger(i) && i >= 0)
                )];

                if (!attachmentIndexes.length) {
                    this.pushMessage({
                        role: "assistant",
                        content: "I couldn't find any uploaded image indexes to add.",
                    });
                    this.stopLoading();
                    return;
                }

                const previews = window.previews || [];
                let targetPreview = null;

                if (targetIndex && targetIndex > 0 && targetIndex <= previews.length) {
                    targetPreview = previews[targetIndex - 1];
                } else if (targetInstanceId) {
                    targetPreview = previews.find(p => p.id === targetInstanceId);
                }

                if (targetIndex || targetInstanceId) {
                    if (!targetPreview) {
                        this.pushMessage({
                            role: "assistant",
                            content: `I couldn't find that template. There are ${previews.length} template(s) in this layout.`,
                        });
                        this.stopLoading();
                        return;
                    }

                    setSelectedInstance(targetPreview.id);
                    this.reorderLayers(targetPreview.id);
                    await this.$nextTick();
                }

                const activePreviewId =
                    targetPreview?.id || this.selectedInstance || window.selectedInstance;

                if (!activePreviewId) {
                    this.pushMessage({
                        role: "assistant",
                        content: "I couldn't determine the active template.",
                    });
                    this.stopLoading();
                    return;
                }

                const lastUserMsg = this.messages.filter(m => m.role === "user").slice(-1)[0];
                const attachments = lastUserMsg?.attachments || [];

                const invalidIndexes = attachmentIndexes.filter(
                    i => i < 0 || i >= attachments.length
                );

                if (invalidIndexes.length) {
                    this.pushMessage({
                        role: "assistant",
                        content: `I couldn't find uploaded image(s) with index: ${invalidIndexes.join(", ")}.`,
                    });
                    this.stopLoading();
                    return;
                }

                const { id: templateId, customer_id, preview_id } = window.template_data || {};

                if (templateId == null || customer_id == null || preview_id == null) {
                    this.pushMessage({
                        role: "assistant",
                        content: "I couldn't find the template data.",
                    });
                    this.stopLoading();
                    return;
                }

                const addedIndexes = [];
                const failedIndexes = [];

                for (const attachmentIndex of attachmentIndexes) {
                    try {
                        const attachment = attachments[attachmentIndex];
                        let file = null;
                        let fileName = `ai_chat_${Date.now()}_${attachmentIndex}.png`;

                        if (attachment?.type === "url" && attachment?.payload) {
                            const response = await fetch(attachment.payload);
                            const blob = await response.blob();

                            file = new File([blob], fileName, {
                                type: blob.type || "image/png",
                            });
                        } else if (attachment?.type === "file") {
                            file = attachment.payload;
                            fileName = attachment.payload?.name || fileName;
                        }

                        if (!file || !(file.type || "").startsWith("image/")) {
                            failedIndexes.push(`${attachmentIndex} (not an image)`);
                            continue;
                        }

                        const formData = new FormData();
                        formData.append("filename_1", fileName);
                        formData.append("customer_id", customer_id);
                        formData.append("theme_id", $("#theme").val());
                        formData.append("template_id", templateId);
                        formData.append("file_1", file);

                        let uploadResponse;
                        try {
                            uploadResponse = await API_MEDIA.uploadBackgroundImage(formData);
                        } catch (e) {
                            failedIndexes.push(`${attachmentIndex} (upload failed)`);
                            continue;
                        }

                        const uploadedPath = uploadResponse?.data?.[0]?.path;
                        if (!uploadedPath) {
                            failedIndexes.push(`${attachmentIndex} (invalid upload response)`);
                            continue;
                        }

                        try {
                            await addAddField(
                                FIELD_TYPE_BACKGROND_THEME_IMAGE,
                                preview_id,
                                { additional_background_images: `/share?file=${uploadedPath}` }
                            );
                        } catch (e) {
                            failedIndexes.push(`${attachmentIndex} (failed to add to template)`);
                            continue;
                        }

                        addedIndexes.push(attachmentIndex);
                    } catch (e) {
                        console.error(`Error processing attachment ${attachmentIndex}:`, e);
                        failedIndexes.push(`${attachmentIndex} (unexpected error)`);
                    }
                }

                const templateNum =
                    targetIndex || (previews.findIndex(p => p.id === activePreviewId) + 1) || 1;
                let assistantMessage;
                if (!addedIndexes.length) {
                    assistantMessage = {
                        role: "assistant",
                        content: "I couldn't add any of the uploaded images to the template.",
                    };
                } else if (!failedIndexes.length) {
                    assistantMessage = {
                        role: "assistant",
                        content:
                            addedIndexes.length === 1
                                ? `Image added to template #${templateNum}.`
                                : `${addedIndexes.length} images added to template #${templateNum}.`,
                    };
                } else {
                    assistantMessage = {
                        role: "assistant",
                        content: `${addedIndexes.length} image(s) added to template #${templateNum}, but some failed: ${failedIndexes.join(", ")}.`,
                    };
                }

                this.pushMessage(assistantMessage);
                this.stopLoading();
                return;
            } catch (error) {
                console.error("Error in add_uploaded_images:", error);
                this.pushMessage({
                    role: "assistant",
                    content: "Sorry, there was an error handling the uploaded image(s).",
                });
                this.stopLoading();
            }
        }
    },
};
</script>

<style scoped lang="scss">
#ai-chat-messages {
  border: none!important;
    
  .card-body {
    padding: 0 10px 0 0;
  }

  :deep(.message__content) {
    width: 100%!important;
  }
}

.chat-module {
    height: calc(100vh - 98px);
}
.chat-module .card {
    position: relative;
    overflow-y: auto;
}

.separator {
  height: 1px;
  background-color: #e9ecef;
  margin: 8px 0;
}

.ai-chat-tab-drop-overlay {
  position: absolute;
  inset: 0;
  background: rgba(32, 156, 134, 0.08);
  border: 2px dashed #209c86;
  border-radius: 12px;
  z-index: 50;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &__inner {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(32, 156, 134, 0.35);
    color: #1f2937;
    padding: 8px 12px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 12px;
  }
}

.thinking-dots {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}
.thinking-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #6c757d;
    animation: thinking-bounce 1s infinite ease-in-out;
}
.thinking-dots span:nth-child(2) {
    animation-delay: 0.15s;
}
.thinking-dots span:nth-child(3) {
    animation-delay: 0.3s;
}
@keyframes thinking-bounce {
    0%, 80%, 100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.ai-chat-suggestions {
  gap: 4px;

  &__bubble {
    padding: 2px 8px;
    border: 1px solid #d0d0d0;
    border-radius: 10px;
    background: #fff;
    font-size: 10px !important;
    color: #495057;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, border-color 0.15s, transform 0.15s;

    &:hover {
      background: #6EBFB0;
      border-color: #aaa;
      transform: scale(1.15);
    }
  }
}

.btn-stop-ai {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid #cbd5e1;
    background: #fff;
    color: #64748b;
    cursor: pointer;
    padding: 0;
    transition: all 0.15s;

    &:hover {
        background: #fee2e2;
        border-color: #ef4444;
        color: #ef4444;
    }
}

.welcome-message {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    max-width: 100%;
    width: fit-content;
    margin-bottom: 16px;

    .message__author {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #EEF8F5;
        border-radius: 100%;
        margin-top: 2px;

        svg {
            width: 14px;
            height: 14px;
            color: #209c86;
        }
    }

    &__content {
        background-color: #F2F5F9;
        color: #212529;
        border-radius: 10px;
        padding: 8px 12px;
    }
}

.ai-chat-regen-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    margin-bottom: 6px;
    background-color: #F2F5F9;
    border-radius: 8px;
    font-size: 13px;
    color: #212529;

    b {
        font-weight: 600;
    }

    &__cancel {
        background: transparent;
        border: none;
        color: #6c757d;
        font-size: 13px;
        cursor: pointer;

        &:hover {
            color: #212529;
        }
    }
}
</style>
